import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Box,
    Button,
    Container,
    Divider,
    Typography,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Chip,
    Snackbar,
    Dialog,             // Import Dialog components
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton
} from "@mui/material";
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from "../../helpers/AuthProvider";
import queryGraphql from "../../helpers/ApiInterface"; // Make sure path is correct
import MainDrawer from "../MainDrawer"; // Make sure path is correct
import MiniBot from "../MiniBot";

// --- Plugin Configuration ---
const pluginDisplayConfig: {
    [key: string]: {
        displayName: string;
        icon?: React.ReactElement;
        category?: string;
        description?: string;
        route?: string;
        checkUsage?: boolean;
        redirectable?: boolean;
    }
 } = {
    checkov: { displayName: "Checkov", icon: <img src="/assets/checkov.svg" alt="Checkov Logo" width={40} height={40} />, category: "SECURITY", description: "Infrastructure as Code (IaC) static analysis tool for security and compliance.", route: "/integrations/checkov-details", checkUsage: true },
    costAgent: { displayName: "GCP Cost Agent", icon: <img src="/assets/gcp.svg" alt="GCP Logo" width={40} height={40} />, category: "COST MANAGEMENT", description: "Tracks and reports Google Cloud Platform costs.", route: "/integrations/gcp-cost-agent", },
    kyverno: { displayName: "Kyverno", icon: <img src="/assets/kyverno.svg" alt="Kyverno Logo" width={40} height={40} />, category: "SECURITY", description: "Kubernetes native policy management for security and compliance.", route: "/integrations/kyverno-details", checkUsage: true }, // Enabled usage check
    prometheus: { displayName: "Prometheus", icon: <img src="/assets/Prometheus.svg" alt="Prometheus Logo" width={40} height={40} />, category: "OBSERVABILITY", description: "Open-source systems monitoring and alerting toolkit.", route: "/integrations/prometheus" },
    grafana: { displayName: "Grafana", icon: <img src="/assets/Grafana.svg" alt="Grafana Logo" width={40} height={40} />, category: "OBSERVABILITY", description: "An open-source platform for visualizing, querying, and alerting on metrics and logs.", route: "/integrations/grafana-integration" },
    sonarQube: { displayName: "SonarQube", icon: <img src="/assets/sonarqube.svg" alt="SonarQube Logo" width={40} height={40} />, category: "SECURITY", description: "An open platform for continuous inspection of code quality and security.", route: "/integrations/sonarqube" },
    gitlab: { displayName: "Gitlab", icon: <img src="/assets/logos/gitlab.svg" alt="Gitlab Logo" width={40} height={40} />, category: "GIT PROVIDER", description: "Code hosting, CI/CD, and project management.", },
    github: { displayName: "GitHub", icon: <img src="/assets/logos/github.svg" alt="GitHub Logo" width={40} height={40} />, category: "GIT PROVIDER", description: "Code hosting, version control, and collaboration.", },
    jenkins: { displayName: "Jenkins", icon: <img src="/assets/logos/jenkins.png" alt="Jenkins Logo" width={40} height={40} />, category: "CICD", description: "Open source automation server.", },
    mirroringApp: { displayName: "Mirroring App", icon: <img src="/assets/flip-horizontal-line.svg" alt="Mirroring App Logo" width={40} height={40} />, category: "GIT PROVIDER", description: "Helps you mirror existing repos to faraday gitops workflow.",route: "/integrations/mirroring-app" },
    // xray:{displayName: "XRAY", icon: <img src="/assets/aws-xray.svg" alt="Xray Logo" width={40} height={40} />, category: "API MANAGEMENT", description: "Analyze and debug production, distributed applications, such as those built using a microservices architecture.",redirectable: false  },
    // awsCloudLogging:{displayName: "AWS Cloud Logging", icon: <img src="/assets/aws-cloud-monitoring.svg" alt="AWS Cloud Logging Logo" width={40} height={40} />, category: "API MANAGEMENT", description: "Centralize, monitor, and analyze log data from AWS services.",redirectable: false },
    customAPICatalogue:{displayName: "Custom API Catalogue", icon: <img src="/assets/aws-cloud-monitoring.svg" alt="Custom API Catalogue Logo" width={40} height={40} />, category: "API MANAGEMENT", description: "Centralize, monitor, and analyze log data from AWS services.", route: "/integrations/custom-api-catalogue"},
    default: { displayName: "Plugin", icon: <svg width="40" height="40" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>, category: "GENERAL", description: "View details for this plugin.", }

};

// --- Helper Functions ---
const getInstalledPlugins = (data: any): { key: string; details: any }[] => {
    if (!data || typeof data !== 'object') return [];
    const plugins: { key: string; details: any }[] = [];
    const excludedKeys = ['id', '__typename'];
    for (const key in data) {
        if (!excludedKeys.includes(key) && typeof data[key] === 'object' && data[key] !== null && data[key].enabled === true) {
           plugins.push({ key: key, details: data[key] });
        }
    }
    return plugins;
};
const formatKeyForDisplay = (key: string): string => {
    if (!key) return '';
    const result = key.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
};
const formatDateForDisplay = (timestamp: string | number | null | undefined): string => {
    if (timestamp === null || timestamp === undefined) return "N/A";
    let date: Date | null = null;
    try {
        if (typeof timestamp === 'number') { date = new Date(timestamp); }
        else if (typeof timestamp === 'string') {
            const nTs = Number(timestamp);
             if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) { date = new Date(nTs); }
             else { date = new Date(timestamp); }
        }
        if (date && !isNaN(date.getTime())) {
             return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } else { console.error(`Invalid date: ${timestamp}`); return "Invalid Date"; }
    } catch (e) { console.error(`Error parsing date (${timestamp}):`, e); return "Error"; }
};


// --- GraphQL Queries ---
const checkovUsageQuery = `
  query GetCheckovResults {
    getCheckovResults { id cluster { clusterName } application { applicationName } service { serviceName } }
  }
`;
const kyvernoUsageQuery = `
  query GetKyvernoResults {
    getKyvernoResults {
      id
      cluster {
        clusterName
      }
    }
  }
`;

const pluginUsageQueries: { [key: string]: string } = {
    checkov: checkovUsageQuery,
    kyverno: kyvernoUsageQuery,
};

// --- Plugin Usage Status Interface ---
interface PluginUsageStatus {
    [key: string]: {
        loading: boolean;
        hasResults: boolean;
        error: string | null;
        checked: boolean;
        linkedEntities?: { clusters: string[]; applications: string[]; services: string[]; };
    };
}

// Interface/Type for Dialog Info State
interface UsageDialogInfo {
    pluginName: string;
    error?: string | null;
    linkedEntities?: PluginUsageStatus[string]['linkedEntities'];
}


// ========================================================================
// --- Main Integrations Component ---
// ========================================================================
const Integrations: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // --- State ---
    const [integrations, setIntegrations] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [pluginUsageStatus, setPluginUsageStatus] = useState<PluginUsageStatus>({});
    const [isUsageDialogOpen, setIsUsageDialogOpen] = useState<boolean>(false);
    const [dialogPluginInfo, setDialogPluginInfo] = useState<UsageDialogInfo | null>(null);
    const { user } = useAuth();
    const isIDPAdmin =
		user.roles?.some((e: string) => ["idp_admin", "viewer"].includes(e)) || false;
    const canDeletePlugins =
        user.roles?.some((role: string) => role === "idp_admin") || false;
    // --- Fetch Initial Integration Status ---
    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                setLoading(true); setError(null); setPluginUsageStatus({});
                const data = await queryGraphql(`
                    query GetIntegrationStatus {
                      getIntegrations {
                        checkov { enabled updatedAt }
                        costAgent { enabled updatedAt }
                        kyverno { enabled updatedAt }
                        prometheus { enabled updatedAt }
                        grafana { enabled updatedAt }
                        mirroringApp { enabled updatedAt }
                        sonarQube { enabled updatedAt }
                        
                        customAPICatalogue{ enabled updatedAt}
                        id
                      }
                    }
                `);
                if (isMounted) {
                    if (data.getIntegrations) { setIntegrations(data.getIntegrations); console.log("Fetched integrations:", data.getIntegrations); }
                    else { console.log("No integration data returned."); setIntegrations(null); }
                }
            } catch (err) { console.error("Err fetching integrations:", err); if (isMounted) setError(err); if (isMounted) setIntegrations(null); }
            finally { if (isMounted) setLoading(false); }
        })();
        return () => { isMounted = false };
     }, []);

    // --- useEffect to Show Snackbar ---
    useEffect(() => {
        if (location.state?.installedPlugin) {
            setSnackbarMessage(`${location.state.installedPlugin} enabled successfully!`);
            setSnackbarOpen(true);
            navigate('.', { replace: true, state: {} });
        }
    }, [location.state, navigate]);

    // --- Fetch Plugin Usage Function ---
    const fetchPluginUsage = useCallback(async (pluginKey: string) => {
        const query = pluginUsageQueries[pluginKey];
        // Ensure fetch doesn't run if already loading for this key
        if (!query || pluginUsageStatus[pluginKey]?.loading) {
             if (!query) console.log(`No usage query defined for plugin: ${pluginKey}`);
             return;
        }
        setPluginUsageStatus(prev => ({ ...prev, [pluginKey]: { ...(prev[pluginKey] || {}), loading: true, hasResults: false, error: null, checked: false } })); // Set loading true
        try {
            console.log(`Workspaceing usage data for ${pluginKey}...`);
            const data = await queryGraphql(query);
            let resultsArray: any[] | undefined;

            // Determine results array based on plugin and query structure
            if (pluginKey === 'checkov' && data.getCheckovResults) {
                resultsArray = data.getCheckovResults;
            } else if (pluginKey === 'kyverno' && data.getKyvernoResults) {
                resultsArray = data.getKyvernoResults;
            }
            // Add other 'else if' blocks for future plugins here

            if (resultsArray === undefined) {
                 if(data) { console.warn(`Expected results field not found for plugin ${pluginKey} in response:`, data); }
                 throw new Error(`Could not find results array in response for ${pluginKey}`);
            }

            const hasResults = Array.isArray(resultsArray) && resultsArray.length > 0;
            let linkedEntities: PluginUsageStatus[string]['linkedEntities'] | undefined = undefined;

            if (hasResults) {
                const clusterNames = new Set<string>();
                const appNames = new Set<string>();
                const serviceNames = new Set<string>();

                resultsArray.forEach(item => {
                    if (pluginKey === 'checkov') {
                        if (item.cluster?.clusterName) clusterNames.add(item.cluster.clusterName);
                        if (item.application?.applicationName) appNames.add(item.application.applicationName);
                        if (item.service?.serviceName) serviceNames.add(item.service.serviceName);
                    } else if (pluginKey === 'kyverno') {
                        if (item.cluster?.clusterName) clusterNames.add(item.cluster.clusterName);
                    }
                });

                if (clusterNames.size > 0 || appNames.size > 0 || serviceNames.size > 0) {
                    linkedEntities = { clusters: Array.from(clusterNames), applications: Array.from(appNames), services: Array.from(serviceNames) };
                }
                console.log(`${pluginKey} usage check: Results found. Linked entities:`, linkedEntities);
            } else {
                 console.log(`${pluginKey} usage check: No results found.`);
            }
            setPluginUsageStatus(prev => ({ ...prev, [pluginKey]: { ...(prev[pluginKey] || {}), loading: false, hasResults: hasResults, error: null, checked: true, linkedEntities: linkedEntities } })); // Set loading false, update results
        } catch (err: any) { console.error(`Error fetching usage for ${pluginKey}:`, err); setPluginUsageStatus(prev => ({ ...prev, [pluginKey]: { ...(prev[pluginKey] || {}), loading: false, hasResults: false, error: err.message || 'Failed to fetch usage', checked: true } })); } // Set loading false on error
     // MODIFICATION START: Added pluginUsageStatus to dependency array as we check it inside useCallback now
     }, [pluginUsageStatus]);
     // MODIFICATION END

    // --- MODIFICATION START ---
    // useEffect to trigger usage checks using Promise.all
    useEffect(() => {
        if (integrations) {
            const installedPluginsList = getInstalledPlugins(integrations);

            // Filter for plugins that need usage check and haven't been checked yet
            const pluginsToFetchUsageFor = installedPluginsList
                .filter(plugin => {
                    const config = pluginDisplayConfig[plugin.key] || pluginDisplayConfig.default;
                    // Check config flag AND if status doesn't exist or hasn't been checked
                    return config.checkUsage && !pluginUsageStatus[plugin.key]?.checked;
                })
                .map(plugin => plugin.key); // Get only the keys

            if (pluginsToFetchUsageFor.length > 0) {
                console.log('Initiating parallel usage fetch for:', pluginsToFetchUsageFor);
                // Use Promise.all to run fetches concurrently
                Promise.all(pluginsToFetchUsageFor.map(key => fetchPluginUsage(key)))
                    .then(() => {
                        console.log('Parallel usage fetch batch completed.');
                        // Individual results/errors are handled within fetchPluginUsage and set state there
                    })
                    .catch(err => {
                        // This catches errors if Promise.all itself fails, unlikely if fetchPluginUsage handles its own errors well
                        console.error("Error during parallel usage fetch execution:", err);
                    });
            }
        }
    // Removed fetchPluginUsage from dependency array here, as it now depends on pluginUsageStatus,
    // which is already in the array. This prevents potential re-trigger loops if fetchPluginUsage reference changes unnecessarily.
    }, [integrations, pluginUsageStatus]);
    // --- MODIFICATION END ---


    // --- Derived Data ---
    const installedPlugins = getInstalledPlugins(integrations);
    const installedCount = installedPlugins.length;

    // --- Event Handlers ---
    const handlePluginClick = (pluginKey: string) => {
        const config = pluginDisplayConfig[pluginKey] || pluginDisplayConfig.default;
        const route = config?.route || `/integrations/${pluginKey}`;
        console.log(`Navigating to: ${route}`);
        navigate(route);
    };
    const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };
    const handleUsageDialogClose = () => {
        setIsUsageDialogOpen(false);
        setDialogPluginInfo(null);
    };


    // --- Render Logic ---
    return (
        <>
            <Box sx={{ display: "Flex" }}>
                <MainDrawer index={"integrations"} />
                <MiniBot/>
                <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
                    {/* --- Header / Sub-Header --- */}
                    <Box sx={{ p: 2, bgcolor: "#fff" }}>
                         <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
                             {/* Adjusted condition to show button even if count is 0 */}
                             <Box sx={{display:'flex', justifyContent:'center'}}>
                             {!loading && !error && installedCount > 0 && (
                                 <Button
                                    sx={{
                                        backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold",
                                        fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1,
                                        '&:hover': { backgroundColor: "dimgray" },
                                        '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
                                    }}
                                    disabled={!isIDPAdmin} variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")}>
                                    Add New Plugin
                                </Button>
                             )}
                             <IconButton
									  aria-label="Help" 
									  onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
									  sx={{
										padding: 0, 
										border: 'none',  
										ml:2,
										'&:hover': {
										  
										  backgroundColor: 'action.hover', 
										},
										
									  }}
									>
									  <Box
										component="img"
										src="/assets/Help.svg" 
										alt="" 
										sx={{
										  width: 40,  
										  height: 40,
										  display: 'block', 
										}}
									  />
									</IconButton>
                             </Box>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                    </Box>
                     <Box m={1} sx={{ pl: "25px", pt: 2, pb: 1 }}>
                        <Typography fontSize="16px" fontFamily="Gilroy-SemiBold"> INSTALLED PLUGINS ({loading ? '...' : installedCount}) </Typography>
                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="text.secondary"> List of all the installed plugins </Typography>
                    </Box>

                    {/* --- Content Area --- */}
                    <Box>
                        {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
                        {error && !loading && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations list. Please try again later.</Alert> </Box> )}

                        {/* --- CONDITION 1: Plugins Installed --- */}
                        {!loading && !error && installedCount > 0 && (
                            <Box sx={{ p: 3, pt: 1 }}>
                                <List sx={{ bgcolor: 'background.paper', borderRadius: '8px', p: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                    {installedPlugins.map((plugin, index) => {
                                        const config = pluginDisplayConfig[plugin.key] || pluginDisplayConfig.default;
                                        const displayName = config.displayName || formatKeyForDisplay(plugin.key);
                                        const displayIcon = config.icon;
                                        const category = config.category || '';
                                        const description = config.description || `View details for ${displayName}.`;
                                        const installedDate = formatDateForDisplay(plugin.details?.updatedAt);

                                        const usageStatus = pluginUsageStatus[plugin.key];
                                        // Read loading status directly from state for this specific plugin
                                        const isLoadingUsage = !!usageStatus?.loading;

                                        return (
                                            <React.Fragment key={plugin.key}>
                                                <ListItem
                                                    disablePadding
                                                    secondaryAction={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', pr: { xs: 2, md: '6vh' } , height: '100%' }}>
                                                            <img src="/assets/calendar-line.svg" alt="" width={14} height={14} style={{ marginRight: '4px', verticalAlign: 'middle' }}/>
                                                            <Typography variant="caption" color="text.secondary" fontFamily="Gilroy-Regular" whiteSpace="nowrap">
                                                               Installed On: {installedDate}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', border: '1px solid transparent',
                                                        transition: 'border-color 0s ease-in-out, background-color 0.2s ease-in-out',
                                                        '&:hover': { borderColor: 'black', backgroundColor: 'action.hover', borderRadius: '1vh', '& .list-item-delete-button': { visibility: 'visible', opacity: 1, }, },
                                                        position: 'relative',
                                                    }}
                                                >
                                                    <ListItemButton disabled={config.redirectable === false} onClick={() => handlePluginClick(plugin.key)} sx={{ pt: 1.5, pb: 1.5, pl: 2, pr: 1, flexGrow: 1, display: 'flex', alignItems: 'center','&.Mui-disabled': { opacity: 1,} }}>
                                                        <ListItemIcon sx={{ mr: 2, minWidth: 'auto', display: 'flex', alignItems: 'center' }}>
                                                            {displayIcon}
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                                    <Typography component="span" variant="body1" sx={{ fontFamily: 'Gilroy-SemiBold' }}>
                                                                        {displayName}
                                                                    </Typography>
                                                                    {category && (
                                                                        <Chip label={category} size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' }}}/>
                                                                    )}
                                                                </Box>
                                                            }
                                                            secondary={ <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> {description} </Typography> }
                                                            sx={{ mr: 1 }}
                                                        />
                                                    </ListItemButton>

                                                    {/* Delete Button Logic */}
                                                    {canDeletePlugins && (
                                                        <Button
                                                        className="list-item-delete-button"
                                                        aria-label={isLoadingUsage && config.checkUsage ? `Checking ${displayName} usage...` : `Delete ${displayName}`}
                                                        title={isLoadingUsage && config.checkUsage ? `Checking ${displayName} usage...` : `Delete ${displayName}`}
                                                        disabled={isLoadingUsage && !!config.checkUsage} // Disable only if loading AND check is required
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            const currentUsageStatus = pluginUsageStatus[plugin.key]; // Read the latest status on click

                                                            // Check if usage check is required and blocks deletion
                                                            if (config.checkUsage && (currentUsageStatus?.hasResults || !!currentUsageStatus?.error)) {
                                                                setDialogPluginInfo({ pluginName: displayName, error: currentUsageStatus.error, linkedEntities: currentUsageStatus.linkedEntities });
                                                                setIsUsageDialogOpen(true);
                                                                return; // Stop: Don't delete
                                                            }

                                                            // If loading, do nothing (button is disabled anyway)
                                                            // This check might be redundant due to 'disabled' prop but provides extra safety
                                                            if (isLoadingUsage && config.checkUsage) {
                                                                 console.log("Still checking usage (onClick)...");
                                                                 return; // Stop: Wait for check to finish
                                                            }

                                                            // --- Proceed with actual deletion (if not blocked and not loading) ---
                                                            try {
                                                                console.log(`Attempting to disable plugin: ${plugin.key}`);
                                                                const pluginUpdateData = { [plugin.key]: { enabled: false } };
                                                                const data = await queryGraphql( `mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { id } }`, { "input": pluginUpdateData } );
                                                                if (data.errors && data.errors.length > 0) throw new Error(data.errors[0].message || 'GraphQL mutation failed.');
                                                                console.log(`Successfully disabled plugin: ${plugin.key}, reloading page.`);
                                                                window.location.reload(); // Consider state update instead
                                                            } catch (err) {
                                                                console.error(`Failed to disable plugin ${plugin.key}:`, err);
                                                                setSnackbarMessage(`Error disabling ${displayName}: ${err instanceof Error ? err.message : 'Unknown error'}`);
                                                                setSnackbarOpen(true);
                                                            }
                                                        }}
                                                        sx={{ /* Button styles */
                                                            minWidth: 0, padding: '8px', borderRadius: '50%', color: 'text.secondary', mx: 1, alignSelf: 'center',
                                                            visibility: 'hidden', opacity: 0, transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out, background-color 0.2s ease-in-out',
                                                            position: 'relative', zIndex: 1,
                                                            '&.Mui-disabled': { color: 'action.disabled', backgroundColor: 'transparent', cursor: 'wait', },
                                                            '&:hover:not(.Mui-disabled)': { backgroundColor: 'action.selected', color: 'error.main', },
                                                        }}
                                                    >
                                                        {/* Show spinner only when loading and check is required */}
                                                        {isLoadingUsage && config.checkUsage ? ( <CircularProgress size={20} sx={{ color: 'text.secondary' }}/> ) : ( <img src="/assets/delete-bin-2-line.svg" alt={`Delete ${displayName}`} width={20} height={20} style={{ display: 'block' }} /> )}
                                                    </Button>
                                                    
                                                    )}

                                                </ListItem>
                                                {index < installedPlugins.length - 1 && <Divider component="li" variant="inset" sx={{ ml: { xs: 2, md: 'calc(40px + 16px + 16px)' } }} />}
                                            </React.Fragment>
                                        );
                                    })}
                                </List>
                            </Box>
                        )}

                        {/* --- CONDITION 2: No Plugins Installed --- */}
                        {!loading && !error && installedCount === 0 && (
                             <Box sx={{ p: { xs: 3, md: 10 } }}>
                                 <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
                                   <img src="/assets/Intergration Page.svg" alt="No plugins installed" style={{ width: "400px", maxWidth:'80%', marginBottom: '24px' }}/>
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" mb={1}>No plugins are added yet</Typography>
                                    <Typography marginBottom="24px" fontSize="16px" fontFamily="Gilroy-Regular" color="text.secondary">Click below button to explore and add new plugins</Typography>
                                    <Button
                                        sx={{ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1, '&:hover': { backgroundColor: "dimgray" }, }}
                                        disabled={!isIDPAdmin} variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")} >
                                            Add New Plugin
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>

            {/* --- Snackbar --- */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity="success" variant="filled" sx={{ width: '100%', boxShadow: 3 }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {/* Usage Information Dialog */}
            <Dialog
                open={isUsageDialogOpen}
                onClose={handleUsageDialogClose}
                aria-labelledby="usage-dialog-title"
                aria-describedby="usage-dialog-description"
            >
                <DialogTitle id="usage-dialog-title">
                    Cannot Delete {dialogPluginInfo?.pluginName ?? 'Plugin'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="usage-dialog-description" component="div">
                        {dialogPluginInfo?.error ? (
                            <Typography color="error"> Failed to check usage: {dialogPluginInfo.error} </Typography>
                        ) : dialogPluginInfo?.linkedEntities ? (
                            <>
                                <Typography gutterBottom> This plugin cannot be deleted because it is associated with the following: </Typography>
                                {/* Section only shows if clusters exist */}
                                {dialogPluginInfo.linkedEntities.clusters?.length > 0 && (
                                    <Box mt={1}>
                                        <Typography variant="body2" fontWeight="bold">Clusters:</Typography>
                                        <List dense disablePadding sx={{ pl: 2 }}>
                                            {dialogPluginInfo.linkedEntities.clusters.map(c => <ListItem key={c} disableGutters sx={{ py: 0.2 }}><ListItemText primary={c} sx={{ m: 0 }} /></ListItem>)}
                                        </List>
                                    </Box>
                                )}
                                {/* Section only shows if apps exist */}
                                {dialogPluginInfo.linkedEntities.applications?.length > 0 && (
                                     <Box mt={1}>
                                        <Typography variant="body2" fontWeight="bold">Applications:</Typography>
                                        <List dense disablePadding sx={{ pl: 2 }}>
                                            {dialogPluginInfo.linkedEntities.applications.map(a => <ListItem key={a} disableGutters sx={{ py: 0.2 }}><ListItemText primary={a} sx={{ m: 0 }} /></ListItem>)}
                                        </List>
                                    </Box>
                                )}
                                {/* Section only shows if services exist */}
                                {dialogPluginInfo.linkedEntities.services?.length > 0 && (
                                     <Box mt={1}>
                                        <Typography variant="body2" fontWeight="bold">Services:</Typography>
                                         <List dense disablePadding sx={{ pl: 2 }}>
                                            {dialogPluginInfo.linkedEntities.services.map(s => <ListItem key={s} disableGutters sx={{ py: 0.2 }}><ListItemText primary={s} sx={{ m: 0 }} /></ListItem>)}
                                         </List>
                                    </Box>
                                )}
                                {/* Fallback only shown if results existed but all lists are empty */}
                                {!(dialogPluginInfo.linkedEntities.clusters?.length > 0) &&
                                 !(dialogPluginInfo.linkedEntities.applications?.length > 0) &&
                                 !(dialogPluginInfo.linkedEntities.services?.length > 0) &&
                                 <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 1 }}>
                                     General usage detected (no specific entities listed).
                                 </Typography>
                                }
                            </>
                         ) : (
                             // Fallback if dialog is open but info is somehow missing
                             <Typography>Usage information is unavailable.</Typography>
                         )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleUsageDialogClose} autoFocus> OK </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
export default Integrations;
// import React, { useState, useEffect, useCallback } from "react";
// import { useNavigate, useLocation } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress,
//     Alert,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     ListItemIcon,
//     Chip,
//     Snackbar,
//     Dialog,             // Import Dialog components
//     DialogActions,
//     DialogContent,
//     DialogContentText,
//     DialogTitle
// } from "@mui/material";
// // import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// // import DeleteIcon from '@mui/icons-material/Delete';

// import queryGraphql from "../../helpers/ApiInterface"; // Make sure path is correct
// import MainDrawer from "../MainDrawer"; // Make sure path is correct

// // --- Plugin Configuration ---
// const pluginDisplayConfig: {
//     [key: string]: {
//         displayName: string;
//         icon?: React.ReactElement;
//         category?: string;
//         description?: string;
//         route?: string;
//         checkUsage?: boolean;
//     }
//  } = {
//     checkov: { displayName: "Checkov", icon: <img src="/assets/checkov.svg" alt="Checkov Logo" width={40} height={40} />, category: "POLICIES", description: "Automated policy checks for infrastructure as code.", route: "/integrations/checkov-details", checkUsage: true },
//     costAgent: { displayName: "GCP Cost Agent", icon: <img src="/assets/gcp.svg" alt="GCP Logo" width={40} height={40} />, category: "COST MANAGEMENT", description: "Tracks and reports Google Cloud Platform costs.", route: "/integrations/gcp-cost-agent", },
//     kyverno: { displayName: "Kyverno", icon: <img src="/assets/kyverno.svg" alt="Kyverno Logo" width={40} height={40} />, category: "POLICIES", description: "Kubernetes native policy management.", route: "/integrations/kyverno-details", checkUsage: true }, // Enabled usage check
//     prometheus: { displayName: "Prometheus", icon: <img src="/assets/logos/prometheus.png" alt="Prometheus Logo" width={40} height={40} />, category: "MONITORING", description: "Open-source systems monitoring and alerting toolkit.", },
//     gitlab: { displayName: "Gitlab", icon: <img src="/assets/logos/gitlab.svg" alt="Gitlab Logo" width={40} height={40} />, category: "GIT PROVIDER", description: "Code hosting, CI/CD, and project management.", },
//     github: { displayName: "GitHub", icon: <img src="/assets/logos/github.svg" alt="GitHub Logo" width={40} height={40} />, category: "GIT PROVIDER", description: "Code hosting, version control, and collaboration.", },
//     jenkins: { displayName: "Jenkins", icon: <img src="/assets/logos/jenkins.png" alt="Jenkins Logo" width={40} height={40} />, category: "CICD", description: "Open source automation server.", },
//     default: { displayName: "Plugin", icon: <svg width="40" height="40" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>, category: "GENERAL", description: "View details for this plugin.", }
// };

// // --- Helper Functions ---
// const getInstalledPlugins = (data: any): { key: string; details: any }[] => {
//     if (!data || typeof data !== 'object') return [];
//     const plugins: { key: string; details: any }[] = [];
//     const excludedKeys = ['id', '__typename'];
//     for (const key in data) {
//         if (!excludedKeys.includes(key) && typeof data[key] === 'object' && data[key] !== null && data[key].enabled === true) {
//            plugins.push({ key: key, details: data[key] });
//         }
//     }
//     return plugins;
// };
// const formatKeyForDisplay = (key: string): string => {
//     if (!key) return '';
//     const result = key.replace(/([A-Z])/g, ' $1');
//     return result.charAt(0).toUpperCase() + result.slice(1);
// };
// const formatDateForDisplay = (timestamp: string | number | null | undefined): string => {
//     if (timestamp === null || timestamp === undefined) return "N/A";
//     let date: Date | null = null;
//     try {
//         if (typeof timestamp === 'number') { date = new Date(timestamp); }
//         else if (typeof timestamp === 'string') {
//             const nTs = Number(timestamp);
//              if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) { date = new Date(nTs); }
//              else { date = new Date(timestamp); }
//         }
//         if (date && !isNaN(date.getTime())) {
//              return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
//         } else { console.error(`Invalid date: ${timestamp}`); return "Invalid Date"; }
//     } catch (e) { console.error(`Error parsing date (${timestamp}):`, e); return "Error"; }
// };


// // --- GraphQL Queries ---
// const checkovUsageQuery = `
//   query GetCheckovResults {
//     getCheckovResults { id cluster { clusterName } application { applicationName } service { serviceName } }
//   }
// `;
// const kyvernoUsageQuery = `
//   query GetKyvernoResults {
//     getKyvernoResults {
//       id
//       cluster {
//         clusterName
//       }
//     }
//   }
// `;

// const pluginUsageQueries: { [key: string]: string } = {
//     checkov: checkovUsageQuery,
//     kyverno: kyvernoUsageQuery,
// };

// // --- Plugin Usage Status Interface ---
// interface PluginUsageStatus {
//     [key: string]: {
//         loading: boolean;
//         hasResults: boolean;
//         error: string | null;
//         checked: boolean;
//         linkedEntities?: { clusters: string[]; applications: string[]; services: string[]; };
//     };
// }

// // Interface/Type for Dialog Info State
// interface UsageDialogInfo {
//     pluginName: string;
//     error?: string | null;
//     linkedEntities?: PluginUsageStatus[string]['linkedEntities'];
// }


// // ========================================================================
// // --- Main Integrations Component ---
// // ========================================================================
// const Integrations: React.FC = () => {
//     const navigate = useNavigate();
//     const location = useLocation();

//     // --- State ---
//     const [integrations, setIntegrations] = useState<any>(null);
//     const [loading, setLoading] = useState<boolean>(true);
//     const [error, setError] = useState<any>(null);
//     const [snackbarOpen, setSnackbarOpen] = useState(false);
//     const [snackbarMessage, setSnackbarMessage] = useState("");
//     const [pluginUsageStatus, setPluginUsageStatus] = useState<PluginUsageStatus>({});
//     const [isUsageDialogOpen, setIsUsageDialogOpen] = useState<boolean>(false);
//     const [dialogPluginInfo, setDialogPluginInfo] = useState<UsageDialogInfo | null>(null);


//     // --- Fetch Initial Integration Status ---
//     useEffect(() => {
//         let isMounted = true;
//         (async () => {
//             try {
//                 setLoading(true); setError(null); setPluginUsageStatus({});
//                 const data = await queryGraphql(`
//                     query GetIntegrationStatus {
//                       getIntegrations {
//                         checkov { enabled updatedAt }
//                         costAgent { enabled updatedAt }
//                         kyverno { enabled updatedAt }
//                         prometheus { enabled updatedAt }
//                         id
//                       }
//                     }
//                 `);
//                 if (isMounted) {
//                     if (data.getIntegrations) { setIntegrations(data.getIntegrations); console.log("Fetched integrations:", data.getIntegrations); }
//                     else { console.log("No integration data returned."); setIntegrations(null); }
//                 }
//             } catch (err) { console.error("Err fetching integrations:", err); if (isMounted) setError(err); if (isMounted) setIntegrations(null); }
//             finally { if (isMounted) setLoading(false); }
//         })();
//         return () => { isMounted = false };
//      }, []);

//     // --- useEffect to Show Snackbar ---
//     useEffect(() => {
//         if (location.state?.installedPlugin) {
//             setSnackbarMessage(`${location.state.installedPlugin} installed successfully!`);
//             setSnackbarOpen(true);
//             navigate('.', { replace: true, state: {} });
//         }
//     }, [location.state, navigate]);

//     // --- Fetch Plugin Usage Function ---
//     const fetchPluginUsage = useCallback(async (pluginKey: string) => {
//         const query = pluginUsageQueries[pluginKey];
//         if (!query) { console.log(`No usage query defined for plugin: ${pluginKey}`); return; }
//         setPluginUsageStatus(prev => ({ ...prev, [pluginKey]: { loading: true, hasResults: false, error: null, checked: false } }));
//         try {
//             console.log(`Workspaceing usage data for ${pluginKey}...`);
//             const data = await queryGraphql(query);
//             let resultsArray: any[] | undefined;

//             // Determine results array based on plugin and query structure
//             if (pluginKey === 'checkov' && data.getCheckovResults) {
//                 resultsArray = data.getCheckovResults;
//             } else if (pluginKey === 'kyverno' && data.getKyvernoResults) { // Updated to check getKyvernoResults
//                 resultsArray = data.getKyvernoResults;
//             }
//             // Add other 'else if' blocks for future plugins here

//             if (resultsArray === undefined) {
//                  // If data exists but the expected field is missing, log it for debugging
//                  if(data) {
//                     console.warn(`Expected results field not found for plugin ${pluginKey} in response:`, data);
//                  }
//                  throw new Error(`Could not find results array in response for ${pluginKey}`);
//             }

//             const hasResults = Array.isArray(resultsArray) && resultsArray.length > 0;
//             let linkedEntities: PluginUsageStatus[string]['linkedEntities'] | undefined = undefined; // Default to undefined

//             if (hasResults) {
//                 const clusterNames = new Set<string>();
//                 const appNames = new Set<string>();
//                 const serviceNames = new Set<string>();

//                 resultsArray.forEach(item => {
//                     // Extract data based on the plugin and its query structure
//                     if (pluginKey === 'checkov') {
//                         if (item.cluster?.clusterName) clusterNames.add(item.cluster.clusterName);
//                         if (item.application?.applicationName) appNames.add(item.application.applicationName);
//                         if (item.service?.serviceName) serviceNames.add(item.service.serviceName);
//                     } else if (pluginKey === 'kyverno') {
//                         if (item.cluster?.clusterName) clusterNames.add(item.cluster.clusterName);
//                         // Kyverno query doesn't have app/service, so sets will remain empty
//                     }
//                     // Add other 'else if' conditions for future plugins
//                 });

//                 // Only create the linkedEntities object if at least one name was found
//                 if (clusterNames.size > 0 || appNames.size > 0 || serviceNames.size > 0) {
//                     linkedEntities = {
//                         clusters: Array.from(clusterNames),
//                         applications: Array.from(appNames),
//                         services: Array.from(serviceNames)
//                     };
//                 }
//                 console.log(`${pluginKey} usage check: Results found. Linked entities:`, linkedEntities);
//             } else {
//                  console.log(`${pluginKey} usage check: No results found.`);
//             }
//             setPluginUsageStatus(prev => ({ ...prev, [pluginKey]: { loading: false, hasResults: hasResults, error: null, checked: true, linkedEntities: linkedEntities } }));
//         } catch (err: any) { console.error(`Error fetching usage for ${pluginKey}:`, err); setPluginUsageStatus(prev => ({ ...prev, [pluginKey]: { loading: false, hasResults: false, error: err.message || 'Failed to fetch usage', checked: true } })); }
//      }, []); // Dependency array kept minimal

//     // --- useEffect to trigger usage checks ---
//     useEffect(() => {
//         if (integrations) {
//             const installed = getInstalledPlugins(integrations);
//             installed.forEach(plugin => {
//                 const config = pluginDisplayConfig[plugin.key] || pluginDisplayConfig.default;
//                 if (config.checkUsage && (!pluginUsageStatus[plugin.key] || !pluginUsageStatus[plugin.key].checked)) {
//                     fetchPluginUsage(plugin.key);
//                 }
//             });
//         }
//     }, [integrations, fetchPluginUsage, pluginUsageStatus]); // Added pluginUsageStatus dependency


//     // --- Derived Data ---
//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     // --- Event Handlers ---
//     const handlePluginClick = (pluginKey: string) => {
//         const config = pluginDisplayConfig[pluginKey] || pluginDisplayConfig.default;
//         const route = config?.route || `/integrations/${pluginKey}`;
//         console.log(`Navigating to: ${route}`);
//         navigate(route);
//     };
//     const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//         if (reason === 'clickaway') return;
//         setSnackbarOpen(false);
//     };
//     const handleUsageDialogClose = () => {
//         setIsUsageDialogOpen(false);
//         setDialogPluginInfo(null);
//     };


//     // --- Render Logic ---
//     return (
//         <>
//             <Box sx={{ display: "Flex" }}>
//                 <MainDrawer index={"integrations"} />
//                 <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                     {/* --- Header / Sub-Header --- */}
//                     <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                          <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
//                              {/* Adjusted condition to show button even if count is 0 */}
//                              {!loading && !error && (
//                                  <Button
//                                     sx={{
//                                         backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold",
//                                         fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1,
//                                         '&:hover': { backgroundColor: "dimgray" },
//                                         '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
//                                     }}
//                                     variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")}>
//                                     Add New Plugin
//                                 </Button>
//                              )}
//                         </Box>
//                         <Divider sx={{ marginY: 1 }} />
//                     </Box>
//                      <Box m={1} sx={{ pl: "25px", pt: 2, pb: 1 }}>
//                         <Typography fontSize="16px" fontFamily="Gilroy-SemiBold"> INSTALLED PLUGINS ({loading ? '...' : installedCount}) </Typography>
//                         <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="text.secondary"> List of all the installed plugins </Typography>
//                     </Box>

//                     {/* --- Content Area --- */}
//                     <Box>
//                         {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
//                         {error && !loading && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations list. Please try again later.</Alert> </Box> )}

//                         {/* --- CONDITION 1: Plugins Installed --- */}
//                         {!loading && !error && installedCount > 0 && (
//                             <Box sx={{ p: 3, pt: 1 }}>
//                                 <List sx={{ bgcolor: 'background.paper', borderRadius: '8px', p: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
//                                     {installedPlugins.map((plugin, index) => {
//                                         const config = pluginDisplayConfig[plugin.key] || pluginDisplayConfig.default;
//                                         const displayName = config.displayName || formatKeyForDisplay(plugin.key);
//                                         const displayIcon = config.icon;
//                                         const category = config.category || '';
//                                         const description = config.description || `View details for ${displayName}.`;
//                                         const installedDate = formatDateForDisplay(plugin.details?.updatedAt);

//                                         const usageStatus = pluginUsageStatus[plugin.key];
//                                         const isLoadingUsage = !!usageStatus?.loading;

//                                         return (
//                                             <React.Fragment key={plugin.key}>
//                                                 <ListItem
//                                                     disablePadding
//                                                     secondaryAction={
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', pr: { xs: 2, md: '6vh' } , height: '100%' }}>
//                                                             <img src="/assets/calendar-line.svg" alt="" width={14} height={14} style={{ marginRight: '4px', verticalAlign: 'middle' }}/>
//                                                             <Typography variant="caption" color="text.secondary" fontFamily="Gilroy-Regular" whiteSpace="nowrap">
//                                                                Installed On: {installedDate}
//                                                             </Typography>
//                                                         </Box>
//                                                     }
//                                                     sx={{
//                                                         display: 'flex', alignItems: 'center', border: '1px solid transparent',
//                                                         transition: 'border-color 0s ease-in-out, background-color 0.2s ease-in-out',
//                                                         '&:hover': { borderColor: 'black', backgroundColor: 'action.hover', borderRadius: '1vh', '& .list-item-delete-button': { visibility: 'visible', opacity: 1, }, },
//                                                         position: 'relative',
//                                                     }}
//                                                 >
//                                                     <ListItemButton onClick={() => handlePluginClick(plugin.key)} sx={{ pt: 1.5, pb: 1.5, pl: 2, pr: 1, flexGrow: 1, display: 'flex', alignItems: 'center', }}>
//                                                         <ListItemIcon sx={{ mr: 2, minWidth: 'auto', display: 'flex', alignItems: 'center' }}>
//                                                             {displayIcon}
//                                                         </ListItemIcon>
//                                                         <ListItemText
//                                                             primary={
//                                                                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
//                                                                     <Typography component="span" variant="body1" sx={{ fontFamily: 'Gilroy-SemiBold' }}>
//                                                                         {displayName}
//                                                                     </Typography>
//                                                                     {category && (
//                                                                         <Chip label={category} size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' }}}/>
//                                                                     )}
//                                                                 </Box>
//                                                             }
//                                                             secondary={ <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> {description} </Typography> }
//                                                             sx={{ mr: 1 }}
//                                                         />
//                                                     </ListItemButton>

//                                                     {/* Delete Button Logic */}
//                                                     <Button
//                                                         className="list-item-delete-button"
//                                                         aria-label={isLoadingUsage && config.checkUsage ? `Checking ${displayName} usage...` : `Delete ${displayName}`}
//                                                         title={isLoadingUsage && config.checkUsage ? `Checking ${displayName} usage...` : `Delete ${displayName}`}
//                                                         disabled={isLoadingUsage && !!config.checkUsage} // Disable only if loading AND check is required
//                                                         onClick={async (e) => {
//                                                             e.stopPropagation();
//                                                             const currentUsageStatus = pluginUsageStatus[plugin.key];

//                                                             if (config.checkUsage && (currentUsageStatus?.hasResults || !!currentUsageStatus?.error)) {
//                                                                 setDialogPluginInfo({ pluginName: displayName, error: currentUsageStatus.error, linkedEntities: currentUsageStatus.linkedEntities });
//                                                                 setIsUsageDialogOpen(true);
//                                                                 return;
//                                                             }
//                                                             if (isLoadingUsage && config.checkUsage) { console.log("Still checking usage..."); return; }

//                                                             try {
//                                                                 console.log(`Attempting to disable plugin: ${plugin.key}`);
//                                                                 const pluginUpdateData = { [plugin.key]: { enabled: false } };
//                                                                 const data = await queryGraphql( `mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { id } }`, { "input": pluginUpdateData } );
//                                                                 if (data.errors && data.errors.length > 0) throw new Error(data.errors[0].message || 'GraphQL mutation failed.');
//                                                                 console.log(`Successfully disabled plugin: ${plugin.key}, reloading page.`);
//                                                                 window.location.reload();
//                                                             } catch (err) {
//                                                                 console.error(`Failed to disable plugin ${plugin.key}:`, err);
//                                                                 setSnackbarMessage(`Error disabling ${displayName}: ${err instanceof Error ? err.message : 'Unknown error'}`);
//                                                                 setSnackbarOpen(true);
//                                                             }
//                                                         }}
//                                                         sx={{ /* Button styles */
//                                                             minWidth: 0, padding: '8px', borderRadius: '50%', color: 'text.secondary', mx: 1, alignSelf: 'center',
//                                                             visibility: 'hidden', opacity: 0, transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out, background-color 0.2s ease-in-out',
//                                                             position: 'relative', zIndex: 1,
//                                                             '&.Mui-disabled': { color: 'action.disabled', backgroundColor: 'transparent', cursor: 'wait', },
//                                                             '&:hover:not(.Mui-disabled)': { backgroundColor: 'action.selected', color: 'error.main', },
//                                                         }}
//                                                     >
//                                                         {isLoadingUsage && config.checkUsage ? ( <CircularProgress size={20} sx={{ color: 'text.secondary' }}/> ) : ( <img src="/assets/delete-bin-2-line.svg" alt={`Delete ${displayName}`} width={20} height={20} style={{ display: 'block' }} /> )}
//                                                     </Button>
//                                                 </ListItem>
//                                                 {index < installedPlugins.length - 1 && <Divider component="li" variant="inset" sx={{ ml: { xs: 2, md: 'calc(40px + 16px + 16px)' } }} />}
//                                             </React.Fragment>
//                                         );
//                                     })}
//                                 </List>
//                             </Box>
//                         )}

//                         {/* --- CONDITION 2: No Plugins Installed --- */}
//                         {!loading && !error && installedCount === 0 && (
//                              <Box sx={{ p: { xs: 3, md: 10 } }}>
//                                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
//                                    <img src="/assets/Intergration Page.svg" alt="No plugins installed" style={{ width: "400px", maxWidth:'80%', marginBottom: '24px' }}/>
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" mb={1}>No plugins are added yet</Typography>
//                                     <Typography marginBottom="24px" fontSize="16px" fontFamily="Gilroy-Regular" color="text.secondary">Click below button to explore and add new plugins</Typography>
//                                     <Button
//                                         sx={{ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1, '&:hover': { backgroundColor: "dimgray" }, }}
//                                         variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")} >
//                                             Add New Plugin
//                                     </Button>
//                                 </Box>
//                             </Box>
//                         )}
//                     </Box>
//                 </Container>
//             </Box>

//             {/* --- Snackbar --- */}
//             <Snackbar
//                 open={snackbarOpen}
//                 autoHideDuration={5000}
//                 onClose={handleSnackbarClose}
//                 anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
//             >
//                 <Alert onClose={handleSnackbarClose} severity="success" variant="filled" sx={{ width: '100%', boxShadow: 3 }}>
//                     {snackbarMessage}
//                 </Alert>
//             </Snackbar>

//             {/* Usage Information Dialog */}
//             <Dialog
//                 open={isUsageDialogOpen}
//                 onClose={handleUsageDialogClose}
//                 aria-labelledby="usage-dialog-title"
//                 aria-describedby="usage-dialog-description"
//             >
//                 <DialogTitle id="usage-dialog-title">
//                     Cannot Delete {dialogPluginInfo?.pluginName ?? 'Plugin'}
//                 </DialogTitle>
//                 <DialogContent>
//                     <DialogContentText id="usage-dialog-description" component="div">
//                         {dialogPluginInfo?.error ? (
//                             <Typography color="error"> Failed to check usage: {dialogPluginInfo.error} </Typography>
//                         ) : dialogPluginInfo?.linkedEntities ? (
//                             <>
//                                 <Typography gutterBottom> This plugin cannot be deleted because it is associated with the following: </Typography>
//                                 {/* Section only shows if clusters exist */}
//                                 {dialogPluginInfo.linkedEntities.clusters?.length > 0 && (
//                                     <Box mt={1}>
//                                         <Typography variant="body2" fontWeight="bold">Clusters:</Typography>
//                                         <List dense disablePadding sx={{ pl: 2 }}>
//                                             {dialogPluginInfo.linkedEntities.clusters.map(c => <ListItem key={c} disableGutters sx={{ py: 0.2 }}><ListItemText primary={c} sx={{ m: 0 }} /></ListItem>)}
//                                         </List>
//                                     </Box>
//                                 )}
//                                 {/* Section only shows if apps exist */}
//                                 {dialogPluginInfo.linkedEntities.applications?.length > 0 && (
//                                      <Box mt={1}>
//                                         <Typography variant="body2" fontWeight="bold">Applications:</Typography>
//                                         <List dense disablePadding sx={{ pl: 2 }}>
//                                             {dialogPluginInfo.linkedEntities.applications.map(a => <ListItem key={a} disableGutters sx={{ py: 0.2 }}><ListItemText primary={a} sx={{ m: 0 }} /></ListItem>)}
//                                         </List>
//                                     </Box>
//                                 )}
//                                 {/* Section only shows if services exist */}
//                                 {dialogPluginInfo.linkedEntities.services?.length > 0 && (
//                                      <Box mt={1}>
//                                         <Typography variant="body2" fontWeight="bold">Services:</Typography>
//                                          <List dense disablePadding sx={{ pl: 2 }}>
//                                             {dialogPluginInfo.linkedEntities.services.map(s => <ListItem key={s} disableGutters sx={{ py: 0.2 }}><ListItemText primary={s} sx={{ m: 0 }} /></ListItem>)}
//                                          </List>
//                                     </Box>
//                                 )}
//                                 {/* Fallback only shown if results existed but all lists are empty */}
//                                 {!dialogPluginInfo.linkedEntities.clusters?.length &&
//                                  !dialogPluginInfo.linkedEntities.applications?.length &&
//                                  !dialogPluginInfo.linkedEntities.services?.length &&
//                                  <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 1 }}>
//                                      General usage detected (no specific entities listed).
//                                  </Typography>
//                                 }
//                             </>
//                          ) : (
//                              // Fallback if dialog is open but info is somehow missing
//                              <Typography>Usage information is unavailable.</Typography>
//                          )}
//                     </DialogContentText>
//                 </DialogContent>
//                 <DialogActions>
//                     <Button onClick={handleUsageDialogClose} autoFocus> OK </Button>
//                 </DialogActions>
//             </Dialog>
//         </>
//     );
// }
// export default Integrations;
// import React, { useState, useEffect, useCallback } from "react";
// import { useNavigate, useLocation } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress,
//     Alert,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     ListItemIcon,
//     Chip,
//     Snackbar,
//     // Tooltip, // No longer needed for this specific explanation
//     Dialog,             // Import Dialog components
//     DialogActions,
//     DialogContent,
//     DialogContentText,
//     DialogTitle
// } from "@mui/material";
// // import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// // import DeleteIcon from '@mui/icons-material/Delete';

// import queryGraphql from "../../helpers/ApiInterface"; // Make sure path is correct
// import MainDrawer from "../MainDrawer"; // Make sure path is correct

// // --- Plugin Configuration (Keep as is) ---
// const pluginDisplayConfig: {
//     [key: string]: {
//         displayName: string;
//         icon?: React.ReactElement;
//         category?: string;
//         description?: string;
//         route?: string;
//         checkUsage?: boolean;
//     }
//  } = {
//     checkov: { displayName: "Checkov", icon: <img src="/assets/checkov.svg" alt="Checkov Logo" width={40} height={40} />, category: "POLICIES", description: "Automated policy checks for infrastructure as code.", route: "/integrations/checkov-details", checkUsage: true },
//     costAgent: { displayName: "GCP Cost Agent", icon: <img src="/assets/gcp.svg" alt="GCP Logo" width={40} height={40} />, category: "COST MANAGEMENT", description: "Tracks and reports Google Cloud Platform costs.", route: "/integrations/gcp-cost-agent", },
//     kyverno: { displayName: "Kyverno", icon: <img src="/assets/kyverno.svg" alt="Kyverno Logo" width={40} height={40} />, category: "POLICIES", description: "Kubernetes native policy management.", route: "/integrations/kyverno-details", checkUsage: false },
//     prometheus: { displayName: "Prometheus", icon: <img src="/assets/logos/prometheus.png" alt="Prometheus Logo" width={40} height={40} />, category: "MONITORING", description: "Open-source systems monitoring and alerting toolkit.", },
//     gitlab: { displayName: "Gitlab", icon: <img src="/assets/logos/gitlab.svg" alt="Gitlab Logo" width={40} height={40} />, category: "GIT PROVIDER", description: "Code hosting, CI/CD, and project management.", },
//     github: { displayName: "GitHub", icon: <img src="/assets/logos/github.svg" alt="GitHub Logo" width={40} height={40} />, category: "GIT PROVIDER", description: "Code hosting, version control, and collaboration.", },
//     jenkins: { displayName: "Jenkins", icon: <img src="/assets/logos/jenkins.png" alt="Jenkins Logo" width={40} height={40} />, category: "CICD", description: "Open source automation server.", },
//     default: { displayName: "Plugin", icon: <svg width="40" height="40" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>, category: "GENERAL", description: "View details for this plugin.", }
// };

// // --- Helper Functions (Keep as is) ---
// const getInstalledPlugins = (data: any): { key: string; details: any }[] => {
//     if (!data || typeof data !== 'object') return [];
//     const plugins: { key: string; details: any }[] = [];
//     const excludedKeys = ['id', '__typename'];
//     for (const key in data) {
//         if (!excludedKeys.includes(key) && typeof data[key] === 'object' && data[key] !== null && data[key].enabled === true) {
//            plugins.push({ key: key, details: data[key] });
//         }
//     }
//     return plugins;
// };
// const formatKeyForDisplay = (key: string): string => {
//     if (!key) return '';
//     const result = key.replace(/([A-Z])/g, ' $1');
//     return result.charAt(0).toUpperCase() + result.slice(1);
// };
// const formatDateForDisplay = (timestamp: string | number | null | undefined): string => {
//     if (timestamp === null || timestamp === undefined) return "N/A";
//     let date: Date | null = null;
//     try {
//         if (typeof timestamp === 'number') { date = new Date(timestamp); }
//         else if (typeof timestamp === 'string') {
//             const nTs = Number(timestamp);
//              if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) { date = new Date(nTs); }
//              else { date = new Date(timestamp); }
//         }
//         if (date && !isNaN(date.getTime())) {
//              return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
//         } else { console.error(`Invalid date: ${timestamp}`); return "Invalid Date"; }
//     } catch (e) { console.error(`Error parsing date (${timestamp}):`, e); return "Error"; }
// };


// // --- GraphQL Queries (Keep as is) ---
// const checkovUsageQuery = `
//   query GetCheckovResults {
//     getCheckovResults { id cluster { clusterName } application { applicationName } service { serviceName } }
//   }
// `;
// const kyvernoUsageQuery = `
//   query GetKyvernoResults { 
//   getKyvernoResults { 
//   id 
//   cluster { clusterName }
//   } 
//   }
// `;
// const pluginUsageQueries: { [key: string]: string } = {
//     checkov: checkovUsageQuery,
//     kyverno: kyvernoUsageQuery,
// };

// // --- Plugin Usage Status Interface (Keep as is) ---
// interface PluginUsageStatus {
//     [key: string]: {
//         loading: boolean;
//         hasResults: boolean;
//         error: string | null;
//         checked: boolean;
//         linkedEntities?: { clusters: string[]; applications: string[]; services: string[]; };
//     };
// }

// // Interface/Type for Dialog Info State
// interface UsageDialogInfo {
//     pluginName: string;
//     error?: string | null;
//     linkedEntities?: PluginUsageStatus[string]['linkedEntities'];
// }


// // ========================================================================
// // --- Main Integrations Component ---
// // ========================================================================
// const Integrations: React.FC = () => {
//     const navigate = useNavigate();
//     const location = useLocation();

//     // --- State ---
//     const [integrations, setIntegrations] = useState<any>(null);
//     const [loading, setLoading] = useState<boolean>(true);
//     const [error, setError] = useState<any>(null);
//     const [snackbarOpen, setSnackbarOpen] = useState(false);
//     const [snackbarMessage, setSnackbarMessage] = useState("");
//     const [pluginUsageStatus, setPluginUsageStatus] = useState<PluginUsageStatus>({});

//     // State for the Usage Dialog
//     const [isUsageDialogOpen, setIsUsageDialogOpen] = useState<boolean>(false);
//     const [dialogPluginInfo, setDialogPluginInfo] = useState<UsageDialogInfo | null>(null);


//     // --- Fetch Initial Integration Status (Keep as is) ---
//     useEffect(() => {
//         let isMounted = true;
//         (async () => {
//             try {
//                 setLoading(true); setError(null); setPluginUsageStatus({});
//                 const data = await queryGraphql(/* GetIntegrationStatus query */ `
//                     query GetIntegrationStatus {
//                       getIntegrations {
//                         checkov { enabled updatedAt }
//                         costAgent { enabled updatedAt }
//                         kyverno { enabled updatedAt }
//                         prometheus { enabled updatedAt }
//                         id
//                       }
//                     }
//                 `);
//                 if (isMounted) {
//                     if (data.getIntegrations) { setIntegrations(data.getIntegrations); console.log("Fetched integrations:", data.getIntegrations); }
//                     else { console.log("No integration data returned."); setIntegrations(null); }
//                 }
//             } catch (err) { console.error("Err fetching integrations:", err); if (isMounted) setError(err); if (isMounted) setIntegrations(null); }
//             finally { if (isMounted) setLoading(false); }
//         })();
//         return () => { isMounted = false };
//      }, []);

//     // --- useEffect to Show Snackbar (Keep as is) ---
//     useEffect(() => {
//         if (location.state?.installedPlugin) {
//             setSnackbarMessage(`${location.state.installedPlugin} installed successfully!`);
//             setSnackbarOpen(true);
//             navigate('.', { replace: true, state: {} });
//         }
//     }, [location.state, navigate]);

//     // --- Fetch Plugin Usage Function (Keep as is) ---
//     const fetchPluginUsage = useCallback(async (pluginKey: string) => {
//         const query = pluginUsageQueries[pluginKey];
//         if (!query) { console.log(`No usage query defined for plugin: ${pluginKey}`); return; }
//         setPluginUsageStatus(prev => ({ ...prev, [pluginKey]: { loading: true, hasResults: false, error: null, checked: false } }));
//         try {
//             console.log(`Workspaceing usage data for ${pluginKey}...`); // Corrected log message
//             const data = await queryGraphql(query);
//             let resultsArray: any[] | undefined;
//             if (pluginKey === 'checkov' && data.getCheckovResults) resultsArray = data.getCheckovResults;
//             else if (pluginKey === 'kyverno' && data.getKyvernoPolicies) resultsArray = data.getKyvernoPolicies;
//             if (resultsArray === undefined) throw new Error(`Could not find results array in response for ${pluginKey}`);

//             const hasResults = Array.isArray(resultsArray) && resultsArray.length > 0;
//             let linkedEntities: PluginUsageStatus[string]['linkedEntities'] = undefined;
//             if (hasResults) {
//                 const clusterNames = new Set<string>(); const appNames = new Set<string>(); const serviceNames = new Set<string>();
//                 resultsArray.forEach(item => {
//                     if (item.cluster?.clusterName) clusterNames.add(item.cluster.clusterName);
//                     if (item.application?.applicationName) appNames.add(item.application.applicationName);
//                     if (item.service?.serviceName) serviceNames.add(item.service.serviceName);
//                 });
//                 linkedEntities = { clusters: Array.from(clusterNames), applications: Array.from(appNames), services: Array.from(serviceNames) };
//                 console.log(`${pluginKey} usage check: Results found. Linked entities:`, linkedEntities);
//             } else { console.log(`${pluginKey} usage check: No results found.`); }
//             setPluginUsageStatus(prev => ({ ...prev, [pluginKey]: { loading: false, hasResults: hasResults, error: null, checked: true, linkedEntities: linkedEntities } }));
//         } catch (err: any) { console.error(`Error fetching usage for ${pluginKey}:`, err); setPluginUsageStatus(prev => ({ ...prev, [pluginKey]: { loading: false, hasResults: false, error: err.message || 'Failed to fetch usage', checked: true } })); }
//      }, []); // Removed queryGraphql from dependency array as it's likely stable

//     // --- useEffect to trigger usage checks (Keep as is) ---
//     useEffect(() => {
//         if (integrations) {
//             const installed = getInstalledPlugins(integrations);
//             installed.forEach(plugin => {
//                 const config = pluginDisplayConfig[plugin.key] || pluginDisplayConfig.default;
//                 if (config.checkUsage && (!pluginUsageStatus[plugin.key] || !pluginUsageStatus[plugin.key].checked)) {
//                     fetchPluginUsage(plugin.key);
//                 }
//             });
//         }
//     }, [integrations, fetchPluginUsage, pluginUsageStatus]); // Added pluginUsageStatus dependency


//     // --- Derived Data (Keep as is) ---
//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     // --- Event Handlers ---
//     const handlePluginClick = (pluginKey: string) => {
//         const config = pluginDisplayConfig[pluginKey] || pluginDisplayConfig.default;
//         const route = config?.route || `/integrations/${pluginKey}`;
//         console.log(`Navigating to: ${route}`);
//         navigate(route);
//     };
//     const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//         if (reason === 'clickaway') return;
//         setSnackbarOpen(false);
//     };

//     // Dialog Close Handler
//     const handleUsageDialogClose = () => {
//         setIsUsageDialogOpen(false);
//         setDialogPluginInfo(null); // Clear dialog info when closing
//     };


//     // --- Render Logic ---
//     return (
//         <>
//             <Box sx={{ display: "Flex" }}>
//                 <MainDrawer index={"integrations"} />
//                 <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                     {/* --- Header / Sub-Header (Keep as is) --- */}
//                     <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                          <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
//                              {!loading && !error && installedCount > 0 &&(
//                                  <Button
//                                     sx={{
//                                         backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold",
//                                         fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1,
//                                         '&:hover': { backgroundColor: "dimgray" },
//                                         '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
//                                     }}
//                                     variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")}>
//                                     Add New Plugin
//                                 </Button>
//                              )}
//                         </Box>
//                         <Divider sx={{ marginY: 1 }} />
//                     </Box>
//                      <Box m={1} sx={{ pl: "25px", pt: 2, pb: 1 }}>
//                         <Typography fontSize="16px" fontFamily="Gilroy-SemiBold"> INSTALLED PLUGINS ({loading ? '...' : installedCount}) </Typography>
//                         <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="text.secondary"> List of all the installed plugins </Typography>
//                     </Box>

//                     {/* --- Content Area --- */}
//                     <Box>
//                         {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
//                         {error && !loading && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations list. Please try again later.</Alert> </Box> )}

//                         {/* --- CONDITION 1: Plugins Installed --- */}
//                         {!loading && !error && installedCount > 0 && (
//                             <Box sx={{ p: 3, pt: 1 }}>
//                                 <List sx={{ bgcolor: 'background.paper', borderRadius: '8px', p: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
//                                     {installedPlugins.map((plugin, index) => {
//                                         const config = pluginDisplayConfig[plugin.key] || pluginDisplayConfig.default;
//                                         const displayName = config.displayName || formatKeyForDisplay(plugin.key);
//                                         const displayIcon = config.icon;
//                                         const category = config.category || '';
//                                         const description = config.description || `View details for ${displayName}.`;
//                                         const installedDate = formatDateForDisplay(plugin.details?.updatedAt);

//                                         const usageStatus = pluginUsageStatus[plugin.key];
//                                         // Button is only disabled strictly while loading
//                                         const isLoadingUsage = !!usageStatus?.loading;

//                                         return (
//                                             <React.Fragment key={plugin.key}>
//                                                 <ListItem
//                                                     disablePadding
//                                                     secondaryAction={
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', pr: { xs: 2, md: '6vh' } , height: '100%' }}>
//                                                             <img src="/assets/calendar-line.svg" alt="" width={14} height={14} style={{ marginRight: '4px', verticalAlign: 'middle' }}/>
//                                                             <Typography variant="caption" color="text.secondary" fontFamily="Gilroy-Regular" whiteSpace="nowrap">
//                                                                Installed On: {installedDate}
//                                                             </Typography>
//                                                         </Box>
//                                                     }
//                                                     sx={{
//                                                         display: 'flex', alignItems: 'center', border: '1px solid transparent',
//                                                         transition: 'border-color 0s ease-in-out, background-color 0.2s ease-in-out',
//                                                         '&:hover': { borderColor: 'black', backgroundColor: 'action.hover', borderRadius: '1vh', '& .list-item-delete-button': { visibility: 'visible', opacity: 1, }, },
//                                                         position: 'relative',
//                                                     }}
//                                                 >
//                                                     <ListItemButton onClick={() => handlePluginClick(plugin.key)} sx={{ pt: 1.5, pb: 1.5, pl: 2, pr: 1, flexGrow: 1, display: 'flex', alignItems: 'center', }}>
//                                                         <ListItemIcon sx={{ mr: 2, minWidth: 'auto', display: 'flex', alignItems: 'center' }}>
//                                                             {displayIcon}
//                                                         </ListItemIcon>
//                                                         <ListItemText
//                                                             primary={
//                                                                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
//                                                                     <Typography component="span" variant="body1" sx={{ fontFamily: 'Gilroy-SemiBold' }}>
//                                                                         {displayName}
//                                                                     </Typography>
//                                                                     {category && (
//                                                                         <Chip label={category} size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' }}}/>
//                                                                     )}
//                                                                 </Box>
//                                                             }
//                                                             secondary={ <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> {description} </Typography> }
//                                                             sx={{ mr: 1 }}
//                                                         />
//                                                     </ListItemButton>

//                                                     {/* Delete Button Logic */}
//                                                     <Button
//                                                         className="list-item-delete-button"
//                                                         aria-label={isLoadingUsage ? `Checking ${displayName} usage...` : `Delete ${displayName}`}
//                                                         title={isLoadingUsage ? `Checking ${displayName} usage...` : `Delete ${displayName}`} // Simple title
//                                                         disabled={isLoadingUsage} // Only disable while loading
//                                                         onClick={async (e) => {
//                                                             e.stopPropagation();
//                                                             const currentUsageStatus = pluginUsageStatus[plugin.key]; // Re-check status on click

//                                                             // Check if usage check is required and blocks deletion
//                                                             if (config.checkUsage && (currentUsageStatus?.hasResults || !!currentUsageStatus?.error)) {
//                                                                 // If blocked, set dialog info and open it
//                                                                 setDialogPluginInfo({
//                                                                     pluginName: displayName,
//                                                                     error: currentUsageStatus.error,
//                                                                     linkedEntities: currentUsageStatus.linkedEntities
//                                                                 });
//                                                                 setIsUsageDialogOpen(true);
//                                                                 return; // Stop: Don't delete
//                                                             }

//                                                             // If loading, do nothing (button is disabled anyway)
//                                                             if (isLoadingUsage) {
//                                                                 console.log("Still checking usage, please wait.");
//                                                                 return; // Stop: Wait for check to finish
//                                                             }

//                                                             // --- Proceed with actual deletion (if not blocked and not loading) ---
//                                                             try {
//                                                                 console.log(`Attempting to disable plugin: ${plugin.key}`);
//                                                                 const pluginUpdateData = { [plugin.key]: { enabled: false } };
//                                                                 const data = await queryGraphql(
//                                                                     `mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { id } }`,
//                                                                     { "input": pluginUpdateData }
//                                                                 );
//                                                                 if (data.errors && data.errors.length > 0) throw new Error(data.errors[0].message || 'GraphQL mutation failed.');
//                                                                 console.log(`Successfully disabled plugin: ${plugin.key}, reloading page.`);
//                                                                 window.location.reload(); // Consider state update instead
//                                                             } catch (err) {
//                                                                 console.error(`Failed to disable plugin ${plugin.key}:`, err);
//                                                                 setSnackbarMessage(`Error disabling ${displayName}: ${err instanceof Error ? err.message : 'Unknown error'}`);
//                                                                 setSnackbarOpen(true);
//                                                             }
//                                                         }}
//                                                         sx={{ /* Button styles remain the same */
//                                                             minWidth: 0, padding: '8px', borderRadius: '50%', color: 'text.secondary', mx: 1, alignSelf: 'center',
//                                                             visibility: 'hidden', opacity: 0, transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out, background-color 0.2s ease-in-out',
//                                                             position: 'relative', zIndex: 1,
//                                                             '&.Mui-disabled': {
//                                                                 color: 'action.disabled',
//                                                                 backgroundColor: 'transparent',
//                                                                 cursor: 'wait',
//                                                             },
//                                                             '&:hover:not(.Mui-disabled)': { backgroundColor: 'action.selected', color: 'error.main', },
//                                                         }}
//                                                     >
//                                                         {/* Show spinner only when loading and check is required */}
//                                                         {isLoadingUsage && config.checkUsage ? (
//                                                             <CircularProgress size={20} sx={{ color: 'text.secondary' }}/>
//                                                         ) : (
//                                                             <img src="/assets/delete-bin-2-line.svg" alt={`Delete ${displayName}`} width={20} height={20} style={{ display: 'block' }} />
//                                                         )}
//                                                     </Button>

//                                                 </ListItem>
//                                                 {index < installedPlugins.length - 1 && <Divider component="li" variant="inset" sx={{ ml: { xs: 2, md: 'calc(40px + 16px + 16px)' } }} />}
//                                             </React.Fragment>
//                                         );
//                                     })}
//                                 </List>
//                             </Box>
//                         )}

//                         {/* --- CONDITION 2: No Plugins Installed (Keep as is) --- */}
//                         {!loading && !error && installedCount === 0 && (
//                              <Box sx={{ p: { xs: 3, md: 10 } }}>
//                                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
//                                    <img src="/assets/Intergration Page.svg" alt="No plugins installed" style={{ width: "400px", maxWidth:'80%', marginBottom: '24px' }}/>
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" mb={1}>No plugins are added yet</Typography>
//                                     <Typography marginBottom="24px" fontSize="16px" fontFamily="Gilroy-Regular" color="text.secondary">Click below button to explore and add new plugins</Typography>
//                                     <Button
//                                         sx={{ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1, '&:hover': { backgroundColor: "dimgray" }, }}
//                                         variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")} >
//                                             Add New Plugin
//                                     </Button>
//                                 </Box>
//                             </Box>
//                         )}
//                     </Box>
//                 </Container>
//             </Box>

//             {/* --- Snackbar (Keep as is) --- */}
//             <Snackbar
//                 open={snackbarOpen}
//                 autoHideDuration={5000}
//                 onClose={handleSnackbarClose}
//                 anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
//             >
//                 <Alert onClose={handleSnackbarClose} severity="success" variant="filled" sx={{ width: '100%', boxShadow: 3 }}>
//                     {snackbarMessage}
//                 </Alert>
//             </Snackbar>

//             {/* Usage Information Dialog (Keep as is) */}
//             <Dialog
//                 open={isUsageDialogOpen}
//                 onClose={handleUsageDialogClose}
//                 aria-labelledby="usage-dialog-title"
//                 aria-describedby="usage-dialog-description"
//             >
//                 <DialogTitle id="usage-dialog-title">
//                     Cannot Delete {dialogPluginInfo?.pluginName ?? 'Plugin'}
//                 </DialogTitle>
//                 <DialogContent>
//                     <DialogContentText id="usage-dialog-description" component="div">
//                         {dialogPluginInfo?.error ? (
//                             <Typography color="error"> Failed to check usage: {dialogPluginInfo.error} </Typography>
//                         ) : dialogPluginInfo?.linkedEntities ? (
//                             <>
//                                 <Typography gutterBottom> This plugin cannot be deleted because it is associated with the following: </Typography>
//                                 {dialogPluginInfo.linkedEntities.clusters?.length > 0 && (
//                                     <Box mt={1}>
//                                         <Typography variant="body2" fontWeight="bold">Clusters:</Typography>
//                                         <List dense disablePadding sx={{ pl: 2 }}> {dialogPluginInfo.linkedEntities.clusters.map(c => <ListItem key={c} disableGutters sx={{ py: 0.2 }}><ListItemText primary={c} sx={{ m: 0 }} /></ListItem>)} </List>
//                                     </Box>
//                                 )}
//                                 {dialogPluginInfo.linkedEntities.applications?.length > 0 && (
//                                      <Box mt={1}>
//                                         <Typography variant="body2" fontWeight="bold">Applications:</Typography>
//                                         <List dense disablePadding sx={{ pl: 2 }}> {dialogPluginInfo.linkedEntities.applications.map(a => <ListItem key={a} disableGutters sx={{ py: 0.2 }}><ListItemText primary={a} sx={{ m: 0 }} /></ListItem>)} </List>
//                                     </Box>
//                                 )}
//                                 {dialogPluginInfo.linkedEntities.services?.length > 0 && (
//                                      <Box mt={1}>
//                                         <Typography variant="body2" fontWeight="bold">Services:</Typography>
//                                          <List dense disablePadding sx={{ pl: 2 }}> {dialogPluginInfo.linkedEntities.services.map(s => <ListItem key={s} disableGutters sx={{ py: 0.2 }}><ListItemText primary={s} sx={{ m: 0 }} /></ListItem>)} </List>
//                                     </Box>
//                                 )}
//                                 {!dialogPluginInfo.linkedEntities.clusters?.length && !dialogPluginInfo.linkedEntities.applications?.length && !dialogPluginInfo.linkedEntities.services?.length &&
//                                  <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 1 }}> General usage detected (no specific entities listed). </Typography>
//                                 }
//                             </>
//                          ) : (
//                              <Typography>Usage information is unavailable.</Typography>
//                          )}
//                     </DialogContentText>
//                 </DialogContent>
//                 <DialogActions>
//                     <Button onClick={handleUsageDialogClose} autoFocus> OK </Button>
//                 </DialogActions>
//             </Dialog>
//         </>
//     );
// }
// export default Integrations;
// import React, { useState, useEffect, useCallback } from "react";
// import { useNavigate, useLocation } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress,
//     Alert,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     ListItemIcon,
//     Chip,
//     Snackbar,
//     // --- MODIFICATION START ---
//     // Tooltip, // No longer needed for this specific explanation, removed import
//     Dialog,             // Import Dialog components
//     DialogActions,
//     DialogContent,
//     DialogContentText,
//     DialogTitle
//     // --- MODIFICATION END ---
// } from "@mui/material";
// // import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// // import DeleteIcon from '@mui/icons-material/Delete';

// import queryGraphql from "../../helpers/ApiInterface"; // Make sure path is correct
// import MainDrawer from "../MainDrawer"; // Make sure path is correct

// // --- Plugin Configuration (Keep as is) ---
// const pluginDisplayConfig: {
//     [key: string]: {
//         displayName: string;
//         icon?: React.ReactElement;
//         category?: string;
//         description?: string;
//         route?: string;
//         checkUsage?: boolean;
//     }
//  } = {
//     checkov: { displayName: "Checkov", icon: <img src="/assets/checkov.svg" alt="Checkov Logo" width={40} height={40} />, category: "POLICIES", description: "Automated policy checks for infrastructure as code.", route: "/integrations/checkov-details", checkUsage: true },
//     costAgent: { displayName: "GCP Cost Agent", icon: <img src="/assets/gcp.svg" alt="GCP Logo" width={40} height={40} />, category: "COST MANAGEMENT", description: "Tracks and reports Google Cloud Platform costs.", route: "/integrations/gcp-cost-agent", },
//     kyverno: { displayName: "Kyverno", icon: <img src="/assets/kyverno.svg" alt="Kyverno Logo" width={40} height={40} />, category: "POLICIES", description: "Kubernetes native policy management.", route: "/integrations/kyverno-details", checkUsage: true },
//     prometheus: { displayName: "Prometheus", icon: <img src="/assets/logos/prometheus.png" alt="Prometheus Logo" width={40} height={40} />, category: "MONITORING", description: "Open-source systems monitoring and alerting toolkit.", },
//     gitlab: { displayName: "Gitlab", icon: <img src="/assets/logos/gitlab.svg" alt="Gitlab Logo" width={40} height={40} />, category: "GIT PROVIDER", description: "Code hosting, CI/CD, and project management.", },
//     github: { displayName: "GitHub", icon: <img src="/assets/logos/github.svg" alt="GitHub Logo" width={40} height={40} />, category: "GIT PROVIDER", description: "Code hosting, version control, and collaboration.", },
//     jenkins: { displayName: "Jenkins", icon: <img src="/assets/logos/jenkins.png" alt="Jenkins Logo" width={40} height={40} />, category: "CICD", description: "Open source automation server.", },
//     default: { displayName: "Plugin", icon: <svg width="40" height="40" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>, category: "GENERAL", description: "View details for this plugin.", }
// };

// // --- Helper Functions (Keep as is) ---
// const getInstalledPlugins = (data: any): { key: string; details: any }[] => {
//     if (!data || typeof data !== 'object') return [];
//     const plugins: { key: string; details: any }[] = [];
//     const excludedKeys = ['id', '__typename'];
//     for (const key in data) {
//         if (!excludedKeys.includes(key) && typeof data[key] === 'object' && data[key] !== null && data[key].enabled === true) {
//            plugins.push({ key: key, details: data[key] });
//         }
//     }
//     return plugins;
// };
// const formatKeyForDisplay = (key: string): string => {
//     if (!key) return '';
//     const result = key.replace(/([A-Z])/g, ' $1');
//     return result.charAt(0).toUpperCase() + result.slice(1);
// };
// const formatDateForDisplay = (timestamp: string | number | null | undefined): string => {
//     if (timestamp === null || timestamp === undefined) return "N/A";
//     let date: Date | null = null;
//     try {
//         if (typeof timestamp === 'number') { date = new Date(timestamp); }
//         else if (typeof timestamp === 'string') {
//             const nTs = Number(timestamp);
//              if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) { date = new Date(nTs); }
//              else { date = new Date(timestamp); }
//         }
//         if (date && !isNaN(date.getTime())) {
//              return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
//         } else { console.error(`Invalid date: ${timestamp}`); return "Invalid Date"; }
//     } catch (e) { console.error(`Error parsing date (${timestamp}):`, e); return "Error"; }
// };


// // --- GraphQL Queries (Keep as is) ---
// const checkovUsageQuery = `
//   query GetCheckovResults {
//     getCheckovResults { id cluster { clusterName } application { applicationName } service { serviceName } }
//   }
// `;
// const kyvernoUsageQuery = `
//   query GetKyvernoResults { getKyvernoPolicies { id } }
// `;
// const pluginUsageQueries: { [key: string]: string } = {
//     checkov: checkovUsageQuery,
//     kyverno: kyvernoUsageQuery,
// };

// // --- Plugin Usage Status Interface (Keep as is) ---
// interface PluginUsageStatus {
//     [key: string]: {
//         loading: boolean;
//         hasResults: boolean;
//         error: string | null;
//         checked: boolean;
//         linkedEntities?: { clusters: string[]; applications: string[]; services: string[]; };
//     };
// }

// // --- MODIFICATION START ---
// // Interface/Type for Dialog Info State
// interface UsageDialogInfo {
//     pluginName: string;
//     error?: string | null;
//     linkedEntities?: PluginUsageStatus[string]['linkedEntities'];
// }
// // --- MODIFICATION END ---


// // ========================================================================
// // --- Main Integrations Component ---
// // ========================================================================
// const Integrations: React.FC = () => {
//     const navigate = useNavigate();
//     const location = useLocation();

//     // --- State ---
//     const [integrations, setIntegrations] = useState<any>(null);
//     const [loading, setLoading] = useState<boolean>(true);
//     const [error, setError] = useState<any>(null);
//     const [snackbarOpen, setSnackbarOpen] = useState(false);
//     const [snackbarMessage, setSnackbarMessage] = useState("");
//     const [pluginUsageStatus, setPluginUsageStatus] = useState<PluginUsageStatus>({});

//     // --- MODIFICATION START ---
//     // State for the Usage Dialog
//     const [isUsageDialogOpen, setIsUsageDialogOpen] = useState<boolean>(false);
//     const [dialogPluginInfo, setDialogPluginInfo] = useState<UsageDialogInfo | null>(null);
//     // --- MODIFICATION END ---


//     // --- Fetch Initial Integration Status (Keep as is) ---
//     useEffect(() => {
//         let isMounted = true;
//         (async () => {
//             try {
//                 setLoading(true); setError(null); setPluginUsageStatus({});
//                 const data = await queryGraphql(/* GetIntegrationStatus query */ `
//                     query GetIntegrationStatus {
//                       getIntegrations {
//                         checkov { enabled updatedAt }
//                         costAgent { enabled updatedAt }
//                         kyverno { enabled updatedAt }
//                         prometheus { enabled updatedAt }
//                         id
//                       }
//                     }
//                 `);
//                 if (isMounted) {
//                     if (data.getIntegrations) { setIntegrations(data.getIntegrations); console.log("Fetched integrations:", data.getIntegrations); }
//                     else { console.log("No integration data returned."); setIntegrations(null); }
//                 }
//             } catch (err) { console.error("Err fetching integrations:", err); if (isMounted) setError(err); if (isMounted) setIntegrations(null); }
//             finally { if (isMounted) setLoading(false); }
//         })();
//         return () => { isMounted = false };
//      }, []);

//     // --- useEffect to Show Snackbar (Keep as is) ---
//     useEffect(() => {
//         if (location.state?.installedPlugin) {
//             setSnackbarMessage(`${location.state.installedPlugin} installed successfully!`);
//             setSnackbarOpen(true);
//             navigate('.', { replace: true, state: {} });
//         }
//     }, [location.state, navigate]);

//     // --- Fetch Plugin Usage Function (Keep as is) ---
//     const fetchPluginUsage = useCallback(async (pluginKey: string) => {
//         const query = pluginUsageQueries[pluginKey];
//         if (!query) { console.log(`No usage query defined for plugin: ${pluginKey}`); return; }
//         setPluginUsageStatus(prev => ({ ...prev, [pluginKey]: { loading: true, hasResults: false, error: null, checked: false } }));
//         try {
//             console.log(`Workspaceing usage data for ${pluginKey}...`);
//             const data = await queryGraphql(query);
//             let resultsArray: any[] | undefined;
//             if (pluginKey === 'checkov' && data.getCheckovResults) resultsArray = data.getCheckovResults;
//             else if (pluginKey === 'kyverno' && data.getKyvernoPolicies) resultsArray = data.getKyvernoPolicies;
//             if (resultsArray === undefined) throw new Error(`Could not find results array in response for ${pluginKey}`);

//             const hasResults = Array.isArray(resultsArray) && resultsArray.length > 0;
//             let linkedEntities: PluginUsageStatus[string]['linkedEntities'] = undefined;
//             if (hasResults) {
//                 const clusterNames = new Set<string>(); const appNames = new Set<string>(); const serviceNames = new Set<string>();
//                 resultsArray.forEach(item => {
//                     if (item.cluster?.clusterName) clusterNames.add(item.cluster.clusterName);
//                     if (item.application?.applicationName) appNames.add(item.application.applicationName);
//                     if (item.service?.serviceName) serviceNames.add(item.service.serviceName);
//                 });
//                 linkedEntities = { clusters: Array.from(clusterNames), applications: Array.from(appNames), services: Array.from(serviceNames) };
//                 console.log(`${pluginKey} usage check: Results found. Linked entities:`, linkedEntities);
//             } else { console.log(`${pluginKey} usage check: No results found.`); }
//             setPluginUsageStatus(prev => ({ ...prev, [pluginKey]: { loading: false, hasResults: hasResults, error: null, checked: true, linkedEntities: linkedEntities } }));
//         } catch (err: any) { console.error(`Error fetching usage for ${pluginKey}:`, err); setPluginUsageStatus(prev => ({ ...prev, [pluginKey]: { loading: false, hasResults: false, error: err.message || 'Failed to fetch usage', checked: true } })); }
//      }, []);

//     // --- useEffect to trigger usage checks (Keep as is) ---
//     useEffect(() => {
//         if (integrations) {
//             const installed = getInstalledPlugins(integrations);
//             installed.forEach(plugin => {
//                 const config = pluginDisplayConfig[plugin.key] || pluginDisplayConfig.default;
//                 if (config.checkUsage && (!pluginUsageStatus[plugin.key] || !pluginUsageStatus[plugin.key].checked)) {
//                     fetchPluginUsage(plugin.key);
//                 }
//             });
//         }
//     }, [integrations, fetchPluginUsage, pluginUsageStatus]);


//     // --- Derived Data (Keep as is) ---
//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     // --- Event Handlers ---
//     const handlePluginClick = (pluginKey: string) => {
//         const config = pluginDisplayConfig[pluginKey] || pluginDisplayConfig.default;
//         const route = config?.route || `/integrations/${pluginKey}`;
//         console.log(`Navigating to: ${route}`);
//         navigate(route);
//     };
//     const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//         if (reason === 'clickaway') return;
//         setSnackbarOpen(false);
//     };

//     // --- MODIFICATION START ---
//     // Dialog Close Handler
//     const handleUsageDialogClose = () => {
//         setIsUsageDialogOpen(false);
//         setDialogPluginInfo(null); // Clear dialog info when closing
//     };
//     // --- MODIFICATION END ---


//     // --- Render Logic ---
//     return (
//         <>
//             <Box sx={{ display: "Flex" }}>
//                 <MainDrawer index={"integrations"} />
//                 <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                     {/* --- Header / Sub-Header (Keep as is) --- */}
//                     <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                          <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
//                              {!loading && !error && (
//                                  <Button
//                                     sx={{
//                                         backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold",
//                                         fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1,
//                                         '&:hover': { backgroundColor: "dimgray" },
//                                         '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
//                                     }}
//                                     variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")}>
//                                     Add New Plugin
//                                 </Button>
//                              )}
//                         </Box>
//                         <Divider sx={{ marginY: 1 }} />
//                     </Box>
//                      <Box m={1} sx={{ pl: "25px", pt: 2, pb: 1 }}>
//                         <Typography fontSize="16px" fontFamily="Gilroy-SemiBold"> INSTALLED PLUGINS ({loading ? '...' : installedCount}) </Typography>
//                         <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="text.secondary"> List of all the installed plugins </Typography>
//                     </Box>

//                     {/* --- Content Area --- */}
//                     <Box>
//                         {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
//                         {error && !loading && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations list. Please try again later.</Alert> </Box> )}

//                         {/* --- CONDITION 1: Plugins Installed --- */}
//                         {!loading && !error && installedCount > 0 && (
//                             <Box sx={{ p: 3, pt: 1 }}>
//                                 <List sx={{ bgcolor: 'background.paper', borderRadius: '8px', p: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
//                                     {installedPlugins.map((plugin, index) => {
//                                         const config = pluginDisplayConfig[plugin.key] || pluginDisplayConfig.default;
//                                         const displayName = config.displayName || formatKeyForDisplay(plugin.key);
//                                         const displayIcon = config.icon;
//                                         const category = config.category || '';
//                                         const description = config.description || `View details for ${displayName}.`;
//                                         const installedDate = formatDateForDisplay(plugin.details?.updatedAt);

//                                         const usageStatus = pluginUsageStatus[plugin.key];
//                                         // Button is only disabled strictly while loading
//                                         const isLoadingUsage = !!usageStatus?.loading; // Use ?? false if needed, but should be defined after fetch starts
//                                         // Determine if deletion should be blocked (used in onClick)
//                                         const isDeletionBlocked = config.checkUsage && (usageStatus?.hasResults || !!usageStatus?.error);

//                                         return (
//                                             <React.Fragment key={plugin.key}>
//                                                 <ListItem
//                                                     disablePadding
//                                                     secondaryAction={
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', pr: { xs: 2, md: '6vh' } , height: '100%' }}>
//                                                             <img src="/assets/calendar-line.svg" alt="" width={14} height={14} style={{ marginRight: '4px', verticalAlign: 'middle' }}/>
//                                                             <Typography variant="caption" color="text.secondary" fontFamily="Gilroy-Regular" whiteSpace="nowrap">
//                                                                Installed On: {installedDate}
//                                                             </Typography>
//                                                         </Box>
//                                                     }
//                                                     sx={{
//                                                         display: 'flex', alignItems: 'center', border: '1px solid transparent',
//                                                         transition: 'border-color 0s ease-in-out, background-color 0.2s ease-in-out',
//                                                         '&:hover': { borderColor: 'black', backgroundColor: 'action.hover', borderRadius: '1vh', '& .list-item-delete-button': { visibility: 'visible', opacity: 1, }, },
//                                                         position: 'relative',
//                                                     }}
//                                                 >
//                                                     <ListItemButton onClick={() => handlePluginClick(plugin.key)} sx={{ pt: 1.5, pb: 1.5, pl: 2, pr: 1, flexGrow: 1, display: 'flex', alignItems: 'center', }}>
//                                                         <ListItemIcon sx={{ mr: 2, minWidth: 'auto', display: 'flex', alignItems: 'center' }}>
//                                                             {displayIcon}
//                                                         </ListItemIcon>
//                                                         <ListItemText
//                                                             primary={
//                                                                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
//                                                                     <Typography component="span" variant="body1" sx={{ fontFamily: 'Gilroy-SemiBold' }}>
//                                                                         {displayName}
//                                                                     </Typography>
//                                                                     {category && (
//                                                                         <Chip label={category} size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' }}}/>
//                                                                     )}
//                                                                 </Box>
//                                                             }
//                                                             secondary={ <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> {description} </Typography> }
//                                                             sx={{ mr: 1 }}
//                                                         />
//                                                     </ListItemButton>

//                                                     {/* --- MODIFICATION START --- */}
//                                                     {/* Remove Tooltip/span wrapper, handle click directly */}
//                                                     <Button
//                                                         className="list-item-delete-button"
//                                                         aria-label={isLoadingUsage ? `Checking ${displayName} usage...` : `Delete ${displayName}`}
//                                                         title={isLoadingUsage ? `Checking ${displayName} usage...` : `Delete ${displayName}`} // Simple title
//                                                         disabled={isLoadingUsage} // Only disable while loading
//                                                         onClick={async (e) => {
//                                                             e.stopPropagation();
//                                                             const currentUsageStatus = pluginUsageStatus[plugin.key]; // Re-check status on click

//                                                             // If deletion is blocked by usage results or error
//                                                             if (config.checkUsage && (currentUsageStatus?.hasResults || !!currentUsageStatus?.error)) {
//                                                                 setDialogPluginInfo({ // Set info for the dialog
//                                                                     pluginName: displayName,
//                                                                     error: currentUsageStatus.error,
//                                                                     linkedEntities: currentUsageStatus.linkedEntities
//                                                                 });
//                                                                 setIsUsageDialogOpen(true); // Open the dialog
//                                                                 return; // Stop further execution
//                                                             }

//                                                             // If it's loading, do nothing (button is disabled anyway)
//                                                             if (isLoadingUsage) {
//                                                                 console.log("Still checking usage, please wait.");
//                                                                 return;
//                                                             }

//                                                             // --- Proceed with actual deletion ---
//                                                             try {
//                                                                 console.log(`Attempting to disable plugin: ${plugin.key}`);
//                                                                 const pluginUpdateData = { [plugin.key]: { enabled: false } };
//                                                                 const data = await queryGraphql(
//                                                                     `mutation Mutation($input: UpdateIntegrationsInput) { updateIntegration(input: $input) { id } }`,
//                                                                     { "input": pluginUpdateData }
//                                                                 );
//                                                                 if (data.errors && data.errors.length > 0) throw new Error(data.errors[0].message || 'GraphQL mutation failed.');
//                                                                 console.log(`Successfully disabled plugin: ${plugin.key}, reloading page.`);
//                                                                 window.location.reload(); // Consider state update instead
//                                                             } catch (err) {
//                                                                 console.error(`Failed to disable plugin ${plugin.key}:`, err);
//                                                                 setSnackbarMessage(`Error disabling ${displayName}: ${err instanceof Error ? err.message : 'Unknown error'}`);
//                                                                 setSnackbarOpen(true);
//                                                             }
//                                                         }}
//                                                         sx={{ /* Button styles remain the same, ensure hover visibility works */
//                                                             minWidth: 0, padding: '8px', borderRadius: '50%', color: 'text.secondary', mx: 1, alignSelf: 'center',
//                                                             visibility: 'hidden', opacity: 0, transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out, background-color 0.2s ease-in-out',
//                                                             position: 'relative', zIndex: 1,
//                                                             // Specific disabled style only for loading
//                                                             '&.Mui-disabled': {
//                                                                 color: 'action.disabled',
//                                                                 backgroundColor: 'transparent',
//                                                                 cursor: 'wait', // Indicate loading
//                                                                 // Ensure spinner is visible when disabled
//                                                                 // visibility: 'visible', // Might be needed if parent hover controls visibility
//                                                                 // opacity: 0.7,
//                                                             },
//                                                             '&:hover:not(.Mui-disabled)': { backgroundColor: 'action.selected', color: 'error.main', },
//                                                         }}
//                                                     >
//                                                         {/* Show spinner only when loading */}
//                                                         {isLoadingUsage && config.checkUsage ? ( // Added config.checkUsage here for safety
//                                                             <CircularProgress size={20} sx={{ color: 'text.secondary' }}/>
//                                                         ) : (
//                                                             <img src="/assets/delete-bin-2-line.svg" alt={`Delete ${displayName}`} width={20} height={20} style={{ display: 'block' }} />
//                                                         )}
//                                                     </Button>
//                                                      {/* --- MODIFICATION END --- */}

//                                                 </ListItem>
//                                                 {index < installedPlugins.length - 1 && <Divider component="li" variant="inset" sx={{ ml: { xs: 2, md: 'calc(40px + 16px + 16px)' } }} />}
//                                             </React.Fragment>
//                                         );
//                                     })}
//                                 </List>
//                             </Box>
//                         )}

//                         {/* --- CONDITION 2: No Plugins Installed (Keep as is) --- */}
//                         {!loading && !error && installedCount === 0 && (
//                              <Box sx={{ p: { xs: 3, md: 10 } }}>
//                                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
//                                    <img src="/assets/Intergration Page.svg" alt="No plugins installed" style={{ width: "400px", maxWidth:'80%', marginBottom: '24px' }}/>
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" mb={1}>No plugins are added yet</Typography>
//                                     <Typography marginBottom="24px" fontSize="16px" fontFamily="Gilroy-Regular" color="text.secondary">Click below button to explore and add new plugins</Typography>
//                                     <Button
//                                         sx={{ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1, '&:hover': { backgroundColor: "dimgray" }, }}
//                                         variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")} >
//                                             Add New Plugin
//                                     </Button>
//                                 </Box>
//                             </Box>
//                         )}
//                     </Box>
//                 </Container>
//             </Box>

//             {/* --- Snackbar (Keep as is) --- */}
//             <Snackbar
//                 open={snackbarOpen}
//                 autoHideDuration={5000}
//                 onClose={handleSnackbarClose}
//                 anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
//             >
//                 <Alert onClose={handleSnackbarClose} severity="success" variant="filled" sx={{ width: '100%', boxShadow: 3 }}>
//                     {snackbarMessage}
//                 </Alert>
//             </Snackbar>

//             {/* --- MODIFICATION START --- */}
//             {/* Usage Information Dialog */}
//             <Dialog
//                 open={isUsageDialogOpen}
//                 onClose={handleUsageDialogClose}
//                 aria-labelledby="usage-dialog-title"
//                 aria-describedby="usage-dialog-description"
//             >
//                 {/* Use DialogPluginInfo safely */}
//                 <DialogTitle id="usage-dialog-title">
//                     Cannot Delete {dialogPluginInfo?.pluginName ?? 'Plugin'}
//                 </DialogTitle>
//                 <DialogContent>
//                     <DialogContentText id="usage-dialog-description" component="div"> {/* Use component="div" for richer content */}
//                         {dialogPluginInfo?.error ? (
//                             // Display error message
//                             <Typography color="error">
//                                 Failed to check usage: {dialogPluginInfo.error}
//                             </Typography>
//                         ) : dialogPluginInfo?.linkedEntities ? ( // Check if linkedEntities exists
//                             // Display linked entities
//                             <>
//                                 <Typography gutterBottom>
//                                     This plugin cannot be deleted because it is associated with the following:
//                                 </Typography>
//                                 {dialogPluginInfo.linkedEntities.clusters?.length > 0 && (
//                                     <Box mt={1}>
//                                         <Typography variant="body2" fontWeight="bold">Clusters:</Typography>
//                                         <List dense disablePadding sx={{ pl: 2 }}>
//                                             {dialogPluginInfo.linkedEntities.clusters.map(c => <ListItem key={c} disableGutters sx={{ py: 0.2 }}><ListItemText primary={c} sx={{ m: 0 }} /></ListItem>)}
//                                         </List>
//                                     </Box>
//                                 )}
//                                 {dialogPluginInfo.linkedEntities.applications?.length > 0 && (
//                                      <Box mt={1}>
//                                         <Typography variant="body2" fontWeight="bold">Applications:</Typography>
//                                         <List dense disablePadding sx={{ pl: 2 }}>
//                                             {dialogPluginInfo.linkedEntities.applications.map(a => <ListItem key={a} disableGutters sx={{ py: 0.2 }}><ListItemText primary={a} sx={{ m: 0 }} /></ListItem>)}
//                                         </List>
//                                     </Box>
//                                 )}
//                                 {dialogPluginInfo.linkedEntities.services?.length > 0 && (
//                                      <Box mt={1}>
//                                         <Typography variant="body2" fontWeight="bold">Services:</Typography>
//                                          <List dense disablePadding sx={{ pl: 2 }}>
//                                             {dialogPluginInfo.linkedEntities.services.map(s => <ListItem key={s} disableGutters sx={{ py: 0.2 }}><ListItemText primary={s} sx={{ m: 0 }} /></ListItem>)}
//                                         </List>
//                                     </Box>
//                                 )}
//                                 {/* Fallback if no specific entities were found but results exist */}
//                                 {!dialogPluginInfo.linkedEntities.clusters?.length &&
//                                  !dialogPluginInfo.linkedEntities.applications?.length &&
//                                  !dialogPluginInfo.linkedEntities.services?.length &&
//                                  <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 1 }}>
//                                      General usage detected (no specific entities listed).
//                                  </Typography>
//                                 }
//                             </>
//                          ) : (
//                              // Fallback if dialog is open but info is somehow missing (shouldn't happen with current logic)
//                              <Typography>Usage information is unavailable.</Typography>
//                          )}
//                     </DialogContentText>
//                 </DialogContent>
//                 <DialogActions>
//                     <Button onClick={handleUsageDialogClose} autoFocus>
//                         OK
//                     </Button>
//                 </DialogActions>
//             </Dialog>
//             {/* --- MODIFICATION END --- */}
//         </>
//     );
// }
// export default Integrations;
// import React, { useState, useEffect, useCallback } from "react";
// import { useNavigate, useLocation } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress,
//     Alert,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     ListItemIcon,
//     Chip,
//     Snackbar,
//     Tooltip // Keep Tooltip
// } from "@mui/material";
// // import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// // import DeleteIcon from '@mui/icons-material/Delete';

// import queryGraphql from "../../helpers/ApiInterface";
// import MainDrawer from "../MainDrawer";

// // --- Plugin Configuration (Keep as is from previous version) ---
// const pluginDisplayConfig: {
//     [key: string]: {
//         displayName: string;
//         icon?: React.ReactElement;
//         category?: string;
//         description?: string;
//         route?: string;
//         checkUsage?: boolean; // Keep this flag
//     }
//  } = {
//     checkov: {
//         displayName: "Checkov",
//         icon: <img src="/assets/checkov.svg" alt="Checkov Logo" width={40} height={40} />,
//         category: "POLICIES",
//         description: "Automated policy checks for infrastructure as code.",
//         route: "/integrations/checkov-details",
//         checkUsage: true
//     },
//     costAgent: {
//         displayName: "GCP Cost Agent",
//         icon: <img src="/assets/gcp.svg" alt="GCP Logo" width={40} height={40} />,
//         category: "COST MANAGEMENT",
//         description: "Tracks and reports Google Cloud Platform costs.",
//         route: "/integrations/gcp-cost-agent",
//     },
//     kyverno: {
//         displayName: "Kyverno",
//         icon: <img src="/assets/kyverno.svg" alt="Kyverno Logo" width={40} height={40} />,
//         category: "POLICIES",
//         description: "Kubernetes native policy management.",
//         route: "/integrations/kyverno-details",
//         checkUsage: true // Keep for future proofing
//     },
//     prometheus: {
//         displayName: "Prometheus",
//         icon: <img src="/assets/logos/prometheus.png" alt="Prometheus Logo" width={40} height={40} />,
//         category: "MONITORING",
//         description: "Open-source systems monitoring and alerting toolkit.",
//     },
//     gitlab: {
//         displayName: "Gitlab",
//         icon: <img src="/assets/logos/gitlab.svg" alt="Gitlab Logo" width={40} height={40} />,
//         category: "GIT PROVIDER",
//         description: "Code hosting, CI/CD, and project management.",
//     },
//      github: {
//         displayName: "GitHub",
//         icon: <img src="/assets/logos/github.svg" alt="GitHub Logo" width={40} height={40} />,
//         category: "GIT PROVIDER",
//         description: "Code hosting, version control, and collaboration.",
//     },
//      jenkins: {
//         displayName: "Jenkins",
//          icon: <img src="/assets/logos/jenkins.png" alt="Jenkins Logo" width={40} height={40} />,
//         category: "CICD",
//         description: "Open source automation server.",
//     },
//     default: {
//         displayName: "Plugin",
//         icon: <svg width="40" height="40" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>,
//         category: "GENERAL",
//         description: "View details for this plugin.",
//     }
// };

// // --- Helper Functions (Keep as is) ---
// const getInstalledPlugins = (data: any): { key: string; details: any }[] => {
//     if (!data || typeof data !== 'object') return [];
//     const plugins: { key: string; details: any }[] = [];
//     const excludedKeys = ['id', '__typename'];
//     for (const key in data) {
//         if (!excludedKeys.includes(key) && typeof data[key] === 'object' && data[key] !== null && data[key].enabled === true) {
//            plugins.push({ key: key, details: data[key] });
//         }
//     }
//     return plugins;
// };

// const formatKeyForDisplay = (key: string): string => {
//     if (!key) return '';
//     const result = key.replace(/([A-Z])/g, ' $1');
//     return result.charAt(0).toUpperCase() + result.slice(1);
// };

// const formatDateForDisplay = (timestamp: string | number | null | undefined): string => {
//     if (timestamp === null || timestamp === undefined) return "N/A";
//     let date: Date | null = null;
//     try {
//         if (typeof timestamp === 'number') {
//             date = new Date(timestamp);
//         } else if (typeof timestamp === 'string') {
//             const nTs = Number(timestamp);
//              if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) {
//                  date = new Date(nTs);
//             } else {
//                  date = new Date(timestamp);
//             }
//         }
//         if (date && !isNaN(date.getTime())) {
//              return date.toLocaleDateString('en-GB', {
//                 day: '2-digit',
//                 month: '2-digit',
//                 year: 'numeric'
//             });
//         } else {
//             console.error(`Invalid or unparseable date format received: ${timestamp}`);
//             return "Invalid Date";
//         }
//     } catch (e) {
//         console.error(`Error parsing date (${timestamp}):`, e);
//         return "Error";
//     }
// };


// // --- MODIFICATION START ---
// // Update Checkov query to fetch entity names
// const checkovUsageQuery = `
//   query GetCheckovResults {
//     getCheckovResults {
//       id
//       cluster {
//         clusterName # <-- Fetch cluster name
//       }
//       application {
//         applicationName # <-- Fetch application name
//       }
//       service {
//         serviceName # <-- Fetch service name
//       }
//     }
//   }
// `;

// // Keep placeholder Kyverno query (adjust if needed)
// const kyvernoUsageQuery = `
//   query GetKyvernoResults {
//     # Replace with your actual Kyverno results query structure
//     getKyvernoPolicies { # Example field name
//       id # Just need to know if any exist
//       # Add fields for related entities if you want similar detail for Kyverno
//     }
//   }
// `;
// // --- MODIFICATION END ---

// // Map plugin keys to their usage check queries (Keep as is)
// const pluginUsageQueries: { [key: string]: string } = {
//     checkov: checkovUsageQuery,
//     kyverno: kyvernoUsageQuery,
// };

// // --- MODIFICATION START ---
// // Update interface to store linked entity details
// interface PluginUsageStatus {
//     [key: string]: {
//         loading: boolean;
//         hasResults: boolean;
//         error: string | null;
//         checked: boolean;
//         linkedEntities?: { // Optional property to store details
//             clusters: string[];
//             applications: string[];
//             services: string[];
//         };
//     };
// }
// // --- MODIFICATION END ---


// // ========================================================================
// // --- Main Integrations Component ---
// // ========================================================================
// const Integrations: React.FC = () => {
//     const navigate = useNavigate();
//     const location = useLocation();

//     // --- State (Keep as is) ---
//     const [integrations, setIntegrations] = useState<any>(null);
//     const [loading, setLoading] = useState<boolean>(true);
//     const [error, setError] = useState<any>(null);
//     const [snackbarOpen, setSnackbarOpen] = useState(false);
//     const [snackbarMessage, setSnackbarMessage] = useState("");
//     const [pluginUsageStatus, setPluginUsageStatus] = useState<PluginUsageStatus>({});


//     // --- Fetch Initial Integration Status (Keep as is) ---
//     useEffect(() => {
//         let isMounted = true;
//         (async () => {
//             try {
//                 setLoading(true);
//                 setError(null);
//                 setPluginUsageStatus({});
//                 const data = await queryGraphql(`
//                     query GetIntegrationStatus {
//                       getIntegrations {
//                         checkov { enabled updatedAt }
//                         costAgent { enabled updatedAt }
//                         kyverno { enabled updatedAt }
//                         prometheus { enabled updatedAt }
//                         id
//                       }
//                     }
//                 `);
//                  if (isMounted) {
//                     if (data.getIntegrations) {
//                         setIntegrations(data.getIntegrations);
//                         console.log("Fetched integrations:", data.getIntegrations);
//                     } else {
//                         console.log("No integration data returned.");
//                         setIntegrations(null);
//                     }
//                 }
//             } catch (err) {
//                 console.error("Error fetching integrations:", err);
//                  if (isMounted) setError(err);
//                  if (isMounted) setIntegrations(null);
//             } finally {
//                  if (isMounted) setLoading(false);
//             }
//         })();
//         return () => { isMounted = false };
//     }, []);

//     // --- useEffect to Show Snackbar (Keep as is) ---
//     useEffect(() => {
//         if (location.state?.installedPlugin) {
//             setSnackbarMessage(`${location.state.installedPlugin} installed successfully!`);
//             setSnackbarOpen(true);
//             navigate('.', { replace: true, state: {} });
//         }
//     }, [location.state, navigate]);

//     // --- MODIFICATION START ---
//     // Updated function to fetch and process usage data including entity names
//     const fetchPluginUsage = useCallback(async (pluginKey: string) => {
//         const query = pluginUsageQueries[pluginKey];
//         if (!query) {
//             console.log(`No usage query defined for plugin: ${pluginKey}`);
//             return;
//         }

//         setPluginUsageStatus(prevStatus => ({
//             ...prevStatus,
//             [pluginKey]: { loading: true, hasResults: false, error: null, checked: false }
//         }));

//         try {
//             console.log(`Workspaceing usage data for ${pluginKey}...`);
//             const data = await queryGraphql(query);

//             let resultsArray: any[] | undefined;
//             // --- Determine results array based on plugin ---
//             if (pluginKey === 'checkov' && data.getCheckovResults) {
//                 resultsArray = data.getCheckovResults;
//             } else if (pluginKey === 'kyverno' && data.getKyvernoPolicies) { // Example for Kyverno
//                 resultsArray = data.getKyvernoPolicies;
//                  // Note: Kyverno parsing logic below assumes a similar structure. Adjust if needed.
//             }
//             // Add other 'else if' blocks for future plugins

//             if (resultsArray === undefined) {
//                  throw new Error(`Could not find results array in response for ${pluginKey}`);
//             }

//             const hasResults = Array.isArray(resultsArray) && resultsArray.length > 0;
//             let linkedEntities: PluginUsageStatus[string]['linkedEntities'] = undefined;

//             // --- Process results to extract entity names (if results exist) ---
//             if (hasResults) {
//                 const clusterNames = new Set<string>();
//                 const appNames = new Set<string>();
//                 const serviceNames = new Set<string>();

//                 resultsArray.forEach(item => {
//                     if (item.cluster?.clusterName) {
//                         clusterNames.add(item.cluster.clusterName);
//                     }
//                     if (item.application?.applicationName) {
//                         appNames.add(item.application.applicationName);
//                     }
//                     if (item.service?.serviceName) {
//                         serviceNames.add(item.service.serviceName);
//                     }
//                     // Add similar checks for Kyverno or other plugins if needed
//                 });

//                 linkedEntities = {
//                     clusters: Array.from(clusterNames),
//                     applications: Array.from(appNames),
//                     services: Array.from(serviceNames)
//                 };
//                 console.log(`${pluginKey} usage check: Results found. Linked entities:`, linkedEntities);
//             } else {
//                  console.log(`${pluginKey} usage check: No results found.`);
//             }

//             // --- Update state with result and linked entities ---
//             setPluginUsageStatus(prevStatus => ({
//                 ...prevStatus,
//                 [pluginKey]: {
//                      loading: false,
//                      hasResults: hasResults,
//                      error: null,
//                      checked: true,
//                      linkedEntities: linkedEntities // Store the extracted details
//                  }
//             }));

//         } catch (err: any) {
//             console.error(`Error fetching usage data for ${pluginKey}:`, err);
//             setPluginUsageStatus(prevStatus => ({
//                 ...prevStatus,
//                 [pluginKey]: { loading: false, hasResults: false, error: err.message || 'Failed to fetch usage data', checked: true }
//             }));
//         }
//     }, []); // useCallback depends on queryGraphql
//     // --- MODIFICATION END ---

//     // useEffect to trigger usage checks (Keep as is)
//     useEffect(() => {
//         if (integrations) {
//             const installed = getInstalledPlugins(integrations);
//             installed.forEach(plugin => {
//                 const config = pluginDisplayConfig[plugin.key] || pluginDisplayConfig.default;
//                 if (config.checkUsage && (!pluginUsageStatus[plugin.key] || !pluginUsageStatus[plugin.key].checked)) {
//                     fetchPluginUsage(plugin.key);
//                 }
//             });
//         }
//     }, [integrations, fetchPluginUsage, pluginUsageStatus]);


//     // --- Derived Data (Keep as is) ---
//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     // --- Event Handlers (Keep as is) ---
//     const handlePluginClick = (pluginKey: string) => {
//         const config = pluginDisplayConfig[pluginKey] || pluginDisplayConfig.default;
//         const route = config?.route || `/integrations/${pluginKey}`;
//         console.log(`Navigating to: ${route}`);
//         navigate(route);
//     };

//     const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//         if (reason === 'clickaway') {
//             return;
//         }
//         setSnackbarOpen(false);
//     };


//     // --- Render Logic ---
//     return (
//         <>
//             <Box sx={{ display: "Flex" }}>
//                 <MainDrawer index={"integrations"} />
//                 <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                     {/* --- Header (Keep as is) --- */}
//                     <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                          <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
//                              {!loading && !error && (
//                                 <Button
//                                     sx={{
//                                         backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold",
//                                         fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1,
//                                         '&:hover': { backgroundColor: "dimgray" },
//                                         '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
//                                     }}
//                                     variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")}>
//                                     Add New Plugin
//                                 </Button>
//                              )}
//                         </Box>
//                         <Divider sx={{ marginY: 1 }} />
//                     </Box>

//                     {/* --- Installed Plugins Sub-Header (Keep as is) --- */}
//                      <Box m={1} sx={{ pl: "25px", pt: 2, pb: 1 }}>
//                         <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                             INSTALLED PLUGINS ({loading ? '...' : installedCount})
//                         </Typography>
//                         <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="text.secondary">
//                             List of all the installed plugins
//                         </Typography>
//                     </Box>

//                     {/* --- Content Area --- */}
//                     <Box>
//                         {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
//                         {error && !loading && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations list. Please try again later.</Alert> </Box> )}

//                         {/* --- CONDITION 1: Plugins Installed --- */}
//                         {!loading && !error && installedCount > 0 && (
//                             <Box sx={{ p: 3, pt: 1 }}>
//                                 <List sx={{ bgcolor: 'background.paper', borderRadius: '8px', p: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
//                                     {installedPlugins.map((plugin, index) => {
//                                         const config = pluginDisplayConfig[plugin.key] || pluginDisplayConfig.default;
//                                         const displayName = config.displayName || formatKeyForDisplay(plugin.key);
//                                         const displayIcon = config.icon;
//                                         const category = config.category || '';
//                                         const description = config.description || `View details for ${displayName}.`;
//                                         const installedDate = formatDateForDisplay(plugin.details?.updatedAt);

//                                         const usageStatus = pluginUsageStatus[plugin.key];
//                                         const isLoadingUsage = usageStatus?.loading ?? (config.checkUsage && !usageStatus?.checked);
//                                         const hasUsageResults = usageStatus?.hasResults ?? false;
//                                         const usageCheckError = usageStatus?.error ?? null;
//                                         const linkedEntities = usageStatus?.linkedEntities; // Get linked entities

//                                         const isDeleteDisabled = config.checkUsage && (isLoadingUsage || hasUsageResults || !!usageCheckError);

//                                         // --- MODIFICATION START ---
//                                         // Generate detailed tooltip title
//                                         let deleteButtonTitle = `Delete ${displayName}`;
//                                         if (config.checkUsage) {
//                                             if (isLoadingUsage) {
//                                                 deleteButtonTitle = `Checking ${displayName} usage...`;
//                                             } else if (usageCheckError) {
//                                                 deleteButtonTitle = `Cannot delete ${displayName}: Error checking usage (${usageCheckError})`;
//                                             } else if (hasUsageResults) {
//                                                 // Construct the detailed message
//                                                 const parts: string[] = [];
//                                                 if (linkedEntities?.clusters?.length) {
//                                                     parts.push(`Clusters: ${linkedEntities.clusters.join(', ')}`);
//                                                 }
//                                                 if (linkedEntities?.applications?.length) {
//                                                     parts.push(`Apps: ${linkedEntities.applications.join(', ')}`);
//                                                 }
//                                                 if (linkedEntities?.services?.length) {
//                                                     parts.push(`Services: ${linkedEntities.services.join(', ')}`);
//                                                 }

//                                                 if (parts.length > 0) {
//                                                     deleteButtonTitle = `Cannot delete ${displayName}: In use by ${parts.join('; ')}.`;
//                                                 } else {
//                                                     // Fallback if results exist but no specific entities were parsed (shouldn't normally happen with current logic)
//                                                     deleteButtonTitle = `Cannot delete ${displayName}: Associated usage found.`;
//                                                 }
//                                             }
//                                         }
//                                         // --- MODIFICATION END ---


//                                         return (
//                                             <React.Fragment key={plugin.key}>
//                                                 <ListItem
//                                                     disablePadding
//                                                     secondaryAction={ /* Secondary action remains the same */
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', pr: { xs: 2, md: '6vh' } , height: '100%' }}>
//                                                             <img src="/assets/calendar-line.svg" alt="" width={14} height={14} style={{ marginRight: '4px', verticalAlign: 'middle' }}/>
//                                                             <Typography variant="caption" color="text.secondary" fontFamily="Gilroy-Regular" whiteSpace="nowrap">
//                                                                Installed On: {installedDate}
//                                                             </Typography>
//                                                         </Box>
//                                                     }
//                                                     sx={{ /* List item styles remain the same */
//                                                         display: 'flex', alignItems: 'center', border: '1px solid transparent',
//                                                         transition: 'border-color 0s ease-in-out, background-color 0.2s ease-in-out',
//                                                         '&:hover': { borderColor: 'black', backgroundColor: 'action.hover', borderRadius: '1vh', '& .list-item-delete-button': { visibility: 'visible', opacity: 1, }, },
//                                                         position: 'relative',
//                                                     }}
//                                                 >
//                                                     <ListItemButton onClick={() => handlePluginClick(plugin.key)} sx={{ pt: 1.5, pb: 1.5, pl: 2, pr: 1, flexGrow: 1, display: 'flex', alignItems: 'center', }}>
//                                                        {/* List item content remains the same */}
//                                                         <ListItemIcon sx={{ mr: 2, minWidth: 'auto', display: 'flex', alignItems: 'center' }}>
//                                                             {displayIcon}
//                                                         </ListItemIcon>
//                                                         <ListItemText
//                                                             primary={
//                                                                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
//                                                                     <Typography component="span" variant="body1" sx={{ fontFamily: 'Gilroy-SemiBold' }}>
//                                                                         {displayName}
//                                                                     </Typography>
//                                                                     {category && (
//                                                                         <Chip label={category} size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' }}}/>
//                                                                     )}
//                                                                 </Box>
//                                                             }
//                                                             secondary={ <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> {description} </Typography> }
//                                                             sx={{ mr: 1 }}
//                                                         />
//                                                     </ListItemButton>

//                                                     {/* Tooltip and Delete Button (Keep structure, title is now dynamic) */}
//                                                     <Tooltip title={deleteButtonTitle} placement="top" arrow>
//                                                         <span>
//                                                             <Button
//                                                                 className="list-item-delete-button"
//                                                                 aria-label={deleteButtonTitle}
//                                                                 disabled={isDeleteDisabled}
//                                                                 onClick={async (e) => { /* onClick logic remains the same */
//                                                                     e.stopPropagation();
//                                                                     if (isDeleteDisabled) return;
//                                                                     try {
//                                                                         console.log(`Attempting to disable plugin: ${plugin.key}`);
//                                                                         const pluginUpdateData = { [plugin.key]: { enabled: false } };
//                                                                         const data = await queryGraphql(
//                                                                             `mutation Mutation($input: UpdateIntegrationsInput) {
//                                                                                 updateIntegration(input: $input) { id }
//                                                                             }`, { "input": pluginUpdateData }
//                                                                         );
//                                                                         if (data.errors && data.errors.length > 0) throw new Error(data.errors[0].message || 'GraphQL mutation failed.');
//                                                                         console.log(`Successfully disabled plugin: ${plugin.key}, reloading page.`);
//                                                                         window.location.reload();
//                                                                     } catch (err) {
//                                                                         console.error(`Failed to disable plugin ${plugin.key}:`, err);
//                                                                         setSnackbarMessage(`Error disabling ${displayName}: ${err instanceof Error ? err.message : 'Unknown error'}`);
//                                                                         setSnackbarOpen(true);
//                                                                     }
//                                                                 }}
//                                                                 sx={{ /* Button styles remain the same */
//                                                                     minWidth: 0, padding: '8px', borderRadius: '50%', color: 'text.secondary', mx: 1, alignSelf: 'center',
//                                                                     visibility: 'hidden', opacity: 0, transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out, background-color 0.2s ease-in-out',
//                                                                     position: 'relative', zIndex: 1,
//                                                                     '&.Mui-disabled': { color: 'action.disabled', backgroundColor: 'transparent', },
//                                                                     '&:hover:not(.Mui-disabled)': { backgroundColor: 'action.selected', color: 'error.main', },
//                                                                 }}
//                                                             >
//                                                                 {isLoadingUsage && config.checkUsage ? (
//                                                                      <CircularProgress size={20} sx={{ color: 'text.secondary' }}/>
//                                                                  ) : (
//                                                                     <img src="/assets/delete-bin-2-line.svg" alt={`Delete ${displayName}`} width={20} height={20} style={{ display: 'block' }} />
//                                                                 )}
//                                                             </Button>
//                                                         </span>
//                                                     </Tooltip>

//                                                 </ListItem>
//                                                 {index < installedPlugins.length - 1 && <Divider component="li" variant="inset" sx={{ ml: { xs: 2, md: 'calc(40px + 16px + 16px)' } }} />}
//                                             </React.Fragment>
//                                         );
//                                     })}
//                                 </List>
//                             </Box>
//                         )}

//                         {/* --- CONDITION 2: No Plugins Installed (Keep as is) --- */}
//                         {!loading && !error && installedCount === 0 && (
//                              <Box sx={{ p: { xs: 3, md: 10 } }}>
//                                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
//                                    <img src="/assets/Intergration Page.svg" alt="No plugins installed" style={{ width: "400px", maxWidth:'80%', marginBottom: '24px' }}/>
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" mb={1}>No plugins are added yet</Typography>
//                                     <Typography marginBottom="24px" fontSize="16px" fontFamily="Gilroy-Regular" color="text.secondary">Click below button to explore and add new plugins</Typography>
//                                     <Button
//                                         sx={{ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1, '&:hover': { backgroundColor: "dimgray" }, }}
//                                         variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")} >
//                                             Add New Plugin
//                                     </Button>
//                                 </Box>
//                             </Box>
//                         )}
//                     </Box>
//                 </Container>
//             </Box>

//              {/* --- Snackbar (Keep as is) --- */}
//             <Snackbar
//                 open={snackbarOpen}
//                 autoHideDuration={5000}
//                 onClose={handleSnackbarClose}
//                 anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
//             >
//                 <Alert onClose={handleSnackbarClose} severity="success" variant="filled" sx={{ width: '100%', boxShadow: 3 }}>
//                     {snackbarMessage}
//                 </Alert>
//             </Snackbar>
//         </>
//     );
// }
// export default Integrations;
// import React, { useState, useEffect, useCallback } from "react";
// // --- Added useLocation ---
// import { useNavigate, useLocation } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress,
//     Alert, // <-- Keep Alert
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     ListItemIcon,
//     Chip,
//     Snackbar, // <-- Added Snackbar
//     // SvgIcon, // Keep only if needed elsewhere or as a fallback
// } from "@mui/material";
// // import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // No longer needed if fallback is custom icon too
// // import DeleteIcon from '@mui/icons-material/Delete'; // Replaced with custom SVG

// import queryGraphql from "../../helpers/ApiInterface"; // Make sure path is correct
// import MainDrawer from "../MainDrawer"; // Make sure path is correct

// // --- Plugin Configuration (Assume this is correct as provided) ---
// const pluginDisplayConfig: {
//     [key: string]: {
//         displayName: string;
//         icon?: React.ReactElement;
//         category?: string;
//         description?: string;
//         route?: string;
//     }
//  } = {
//     checkov: {
//         displayName: "Checkov",
//         icon: <img src="/assets/checkov.svg" alt="Checkov Logo" width={40} height={40} />,
//         category: "POLICIES",
//         description: "Automated policy checks for infrastructure as code.",
//         route: "/integrations/checkov-details",
//     },
//     costAgent: {
//         displayName: "GCP Cost Agent",
//         icon: <img src="/assets/gcp.svg" alt="GCP Logo" width={40} height={40} />,
//         category: "COST MANAGEMENT",
//         description: "Tracks and reports Google Cloud Platform costs.",
//         route: "/integrations/gcp-cost-agent",
//     },
//     kyverno: {
//         displayName: "Kyverno",
//         icon: <img src="/assets/kyverno.svg" alt="Kyverno Logo" width={40} height={40} />,
//         category: "POLICIES",
//         description: "Kubernetes native policy management.",
//         route: "/integrations/kyverno-details",
//     },
//     prometheus: {
//         displayName: "Prometheus",
//         icon: <img src="/assets/logos/prometheus.png" alt="Prometheus Logo" width={40} height={40} />,
//         category: "MONITORING",
//         description: "Open-source systems monitoring and alerting toolkit.",
//     },
//     gitlab: {
//         displayName: "Gitlab",
//         icon: <img src="/assets/logos/gitlab.svg" alt="Gitlab Logo" width={40} height={40} />,
//         category: "GIT PROVIDER",
//         description: "Code hosting, CI/CD, and project management.",
//     },
//      github: {
//         displayName: "GitHub",
//         icon: <img src="/assets/logos/github.svg" alt="GitHub Logo" width={40} height={40} />,
//         category: "GIT PROVIDER",
//         description: "Code hosting, version control, and collaboration.",
//     },
//      jenkins: {
//         displayName: "Jenkins",
//          icon: <img src="/assets/logos/jenkins.png" alt="Jenkins Logo" width={40} height={40} />,
//         category: "CICD",
//         description: "Open source automation server.",
//     },
//     default: {
//         displayName: "Plugin",
//         icon: <svg width="40" height="40" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>,
//         category: "GENERAL",
//         description: "View details for this plugin.",
//     }
// };

// // --- Helper Functions (Assume these are correct as provided) ---
// const getInstalledPlugins = (data: any): { key: string; details: any }[] => {
//     if (!data || typeof data !== 'object') return [];
//     const plugins: { key: string; details: any }[] = [];
//     const excludedKeys = ['id', '__typename'];
//     for (const key in data) {
//         if (!excludedKeys.includes(key) && typeof data[key] === 'object' && data[key] !== null && data[key].enabled === true) {
//            plugins.push({ key: key, details: data[key] });
//         }
//     }
//     return plugins;
// };

// const formatKeyForDisplay = (key: string): string => {
//     if (!key) return '';
//     const result = key.replace(/([A-Z])/g, ' $1');
//     return result.charAt(0).toUpperCase() + result.slice(1);
// };

// // --- NEW: Robust Date Formatting Function (adapted from KyvernoDetails) ---
// const formatDateForDisplay = (timestamp: string | number | null | undefined): string => {
//     if (timestamp === null || timestamp === undefined) return "N/A";
//     let date: Date | null = null;
//     try {
//         if (typeof timestamp === 'number') {
//             // Assume milliseconds if number. If it could be seconds, multiply by 1000 here.
//             date = new Date(timestamp);
//         } else if (typeof timestamp === 'string') {
//             // Try parsing as a number first (common for millisecond timestamps as strings)
//             const nTs = Number(timestamp);
//             // Check if it looks like a valid numeric timestamp string
//             if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) {
//                  date = new Date(nTs); // Use the parsed number
//             } else {
//                 // Otherwise, try parsing the string directly (e.g., ISO 8601 format)
//                 date = new Date(timestamp);
//             }
//         }

//         // Final validation check
//         if (date && !isNaN(date.getTime())) {
//             // Use consistent formatting
//             return date.toLocaleDateString('en-GB', {
//                 day: '2-digit',   // Ensures DD (e.g., 05, 10)
//                 month: '2-digit',  // Ensures MM (e.g., 04, 11)
//                 year: 'numeric'   // Ensures YYYY (e.g., 2025)
//             });
//         } else {
//             console.error(`Invalid or unparseable date format received: ${timestamp}`);
//             return "Invalid Date"; // Or return "N/A" if preferred for invalid dates
//         }
//     } catch (e) {
//         console.error(`Error parsing date (${timestamp}):`, e);
//         return "Error"; // Or return "N/A"
//     }
// };


// // ========================================================================
// // --- Main Integrations Component ---
// // ========================================================================
// const Integrations: React.FC = () => {
//     const navigate = useNavigate();
//     const location = useLocation(); // <-- Get location object

//     // --- State ---
//     const [integrations, setIntegrations] = useState<any>(null);
//     const [loading, setLoading] = useState<boolean>(true);
//     const [error, setError] = useState<any>(null);

//     // --- State for Snackbar Notification ---
//     const [snackbarOpen, setSnackbarOpen] = useState(false);
//     const [snackbarMessage, setSnackbarMessage] = useState("");

//     // --- Fetch Data (Keep as is, runs on initial mount) ---
//     useEffect(() => {
//         let isMounted = true;
//         (async () => {
//             try {
//                 setLoading(true);
//                 setError(null);
//                 const data = await queryGraphql(`
//                     query GetIntegrationStatus {
//                       getIntegrations {
//                         checkov { enabled updatedAt }
//                         costAgent { enabled updatedAt }
//                         kyverno { enabled updatedAt }
//                         prometheus { enabled updatedAt }
//                         # Assuming gitlab, github, jenkins might also be enabled/queried here eventually
//                         # gitlab { enabled updatedAt }
//                         # github { enabled updatedAt }
//                         # jenkins { enabled updatedAt }
//                         id
//                       }
//                     }
//                 `);
//                  if (isMounted) {
//                     if (data.getIntegrations) {
//                         setIntegrations(data.getIntegrations);
//                         console.log("Fetched integrations:", data.getIntegrations); // Log fetched data
//                     } else {
//                         console.log("No integration data returned.");
//                         setIntegrations(null);
//                     }
//                 }
//             } catch (err) {
//                 console.error("Error fetching integrations:", err);
//                  if (isMounted) setError(err);
//                  if (isMounted) setIntegrations(null);
//             } finally {
//                  if (isMounted) setLoading(false);
//             }
//         })();
//         return () => { isMounted = false };
//     }, []); // Empty dependency array: run once on mount

//     // --- useEffect to Show Snackbar on Successful Navigation ---
//     // This effect runs when the component mounts *and* whenever location.state changes
//     useEffect(() => {
//         // Check if we navigated here with installation success state
//         if (location.state?.installedPlugin) {
//             setSnackbarMessage(`${location.state.installedPlugin} installed successfully!`);
//             setSnackbarOpen(true);
//             // Clear the state from location history so the snackbar doesn't reappear
//             // on refresh or navigating back/forward to this specific history entry.
//             navigate('.', { replace: true, state: {} });
//         }
//     }, [location.state, navigate]); // Depend on location.state and navigate


//     // --- Derived Data ---
//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     // --- Event Handlers ---
//     const handlePluginClick = (pluginKey: string) => {
//         const config = pluginDisplayConfig[pluginKey] || pluginDisplayConfig.default;
//         const route = config?.route || `/integrations/${pluginKey}`;
//         console.log(`Navigating to: ${route}`);
//         navigate(route);
//     };

//     // --- Snackbar Close Handler ---
//     const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//         if (reason === 'clickaway') {
//             return;
//         }
//         setSnackbarOpen(false);
//     };

//     // --- Render Logic ---
//     return (
//         // Wrap in Fragment to allow Snackbar as sibling
//         <>
//             <Box sx={{ display: "Flex" }}>
//                 <MainDrawer index={"integrations"} />
//                 <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                     {/* --- Header (Keep as is) --- */}
//                     <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                          <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
//                             {/* Conditionally show Add button based on loading/error */}
//                             {!loading && !error && installedCount >0 &&( // Show button even if count is 0
//                                 <Button
//                                     sx={{
//                                         backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold",
//                                         fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1,
//                                         '&:hover': { backgroundColor: "dimgray" },
//                                         '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
//                                     }}
//                                     variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")}>
//                                     Add New Plugin
//                                 </Button>
//                              )}
//                         </Box>
//                         <Divider sx={{ marginY: 1 }} />
//                     </Box>

//                     {/* --- Installed Plugins Sub-Header (Keep as is) --- */}
//                      <Box m={1} sx={{ pl: "25px", pt: 2, pb: 1 }}>
//                         <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                             INSTALLED PLUGINS ({loading ? '...' : installedCount})
//                         </Typography>
//                         <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="text.secondary">
//                             List of all the installed plugins
//                         </Typography>
//                     </Box>

//                     {/* --- Content Area --- */}
//                     <Box>
//                         {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
//                         {error && !loading && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations list. Please try again later.</Alert> </Box> )}

//                         {/* --- CONDITION 1: Plugins Installed --- */}
//                         {!loading && !error && installedCount > 0 && (
//                             <Box sx={{ p: 3, pt: 1 }}>
//                                 <List sx={{ bgcolor: 'background.paper', borderRadius: '8px', p: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
//                                     {installedPlugins.map((plugin, index) => {
//                                         const config = pluginDisplayConfig[plugin.key] || pluginDisplayConfig.default;
//                                         const displayName = config.displayName || formatKeyForDisplay(plugin.key);
//                                         const displayIcon = config.icon;
//                                         const category = config.category || '';
//                                         const description = config.description || `View details for ${displayName}.`;

//                                         // --- MODIFIED: Use the robust date formatting function ---
//                                         const installedDate = formatDateForDisplay(plugin.details?.updatedAt);
//                                         // --- End Modification ---

//                                         return (
//                                             <React.Fragment key={plugin.key}>
//                                                 <ListItem
//                                                     disablePadding
//                                                     secondaryAction={
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', pr: { xs: 2, md: '6vh' } , height: '100%' }}>
//                                                             <img
//                                                                 src="/assets/calendar-line.svg" // <-- REPLACE with the correct path to YOUR icon
//                                                                 alt="" // Alt text can be empty if purely decorative
//                                                                 width={14} // Adjust size as needed
//                                                                 height={14} // Adjust size as needed
//                                                                 style={{ marginRight: '4px', verticalAlign: 'middle' }} // Add space between icon and text
//                                                             />
//                                                             <Typography variant="caption" color="text.secondary" fontFamily="Gilroy-Regular" whiteSpace="nowrap">
//                                                                Installed On: {installedDate}
//                                                             </Typography>
//                                                         </Box>
//                                                     }
//                                                     sx={{ /* Styles remain the same */
//                                                         display: 'flex', alignItems: 'center', border: '1px solid transparent',
//                                                         transition: 'border-color 0s ease-in-out, background-color 0.2s ease-in-out',
//                                                         '&:hover': { borderColor: 'black', backgroundColor: 'action.hover', borderRadius: '1vh', '& .list-item-delete-button': { visibility: 'visible', opacity: 1, }, },
//                                                         position: 'relative',
//                                                     }}
//                                                 >
//                                                     <ListItemButton onClick={() => handlePluginClick(plugin.key)} sx={{ pt: 1.5, pb: 1.5, pl: 2, pr: 1, flexGrow: 1, display: 'flex', alignItems: 'center', }}>
//                                                         <ListItemIcon sx={{ mr: 2, minWidth: 'auto', display: 'flex', alignItems: 'center' }}>
//                                                             {displayIcon}
//                                                         </ListItemIcon>
//                                                         <ListItemText
//                                                             primary={
//                                                                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
//                                                                     <Typography component="span" variant="body1" sx={{ fontFamily: 'Gilroy-SemiBold' }}>
//                                                                         {displayName}
//                                                                     </Typography>
//                                                                     {category && (
//                                                                         <Chip label={category} size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' }}}/>
//                                                                     )}
//                                                                 </Box>
//                                                             }
//                                                             secondary={ <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> {description} </Typography> }
//                                                             sx={{ mr: 1 }}
//                                                         />
//                                                     </ListItemButton>

//                                                     {/* Delete Button (Keep existing logic and styling) */}
//                                                     <Button
//                                                         className="list-item-delete-button"
//                                                         aria-label={`Delete ${displayName}`}
//                                                         title={`Delete ${displayName}`}
//                                                         onClick={async (e) => { /* API call logic remains the same */
//                                                             e.stopPropagation();
//                                                             try {
//                                                                 console.log(`Attempting to disable plugin: ${plugin.key}`);
//                                                                 const pluginUpdateData = { [plugin.key]: { enabled: false } };
//                                                                 const data = await queryGraphql(
//                                                                     `mutation Mutation($input: UpdateIntegrationsInput) {
//                                                                         updateIntegration(input: $input) { id }
//                                                                     }`, { "input": pluginUpdateData }
//                                                                 );
//                                                                 if (data.errors && data.errors.length > 0) throw new Error(data.errors[0].message || 'GraphQL mutation failed.');
//                                                                 console.log(`Successfully disabled plugin: ${plugin.key}, reloading page.`);
//                                                                 window.location.reload(); // Consider updating state instead of full reload for better UX
//                                                             } catch (err) {
//                                                                 console.error(`Failed to disable plugin ${plugin.key}:`, err);
//                                                                 // Removed alert() as requested
//                                                                 // Optional: Show a snackbar error here
//                                                                 setSnackbarMessage(`Error disabling ${displayName}: ${err instanceof Error ? err.message : 'Unknown error'}`);
//                                                                 setSnackbarOpen(true); // Reuse snackbar for errors too? Needs styling adjustment (severity="error")
//                                                             }
//                                                         }}
//                                                         sx={{ /* Styles remain the same */
//                                                             minWidth: 0, padding: '8px', borderRadius: '50%', color: 'text.secondary', mx: 1, alignSelf: 'center',
//                                                             visibility: 'hidden', opacity: 0, transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out, background-color 0.2s ease-in-out',
//                                                             '&:hover': { backgroundColor: 'action.selected', color: 'error.main', },
//                                                             position: 'relative', zIndex: 1,
//                                                         }}
//                                                     >
//                                                         <img src="/assets/delete-bin-2-line.svg" alt={`Delete ${displayName}`} width={20} height={20} style={{ display: 'block' }} />
//                                                     </Button>
//                                                 </ListItem>
//                                                 {index < installedPlugins.length - 1 && <Divider component="li" variant="inset" sx={{ ml: { xs: 2, md: 'calc(40px + 16px + 16px)' } }} />}
//                                             </React.Fragment>
//                                         );
//                                     })}
//                                 </List>
//                             </Box>
//                         )}

//                         {/* --- CONDITION 2: No Plugins Installed (Keep as is) --- */}
//                         {!loading && !error && installedCount === 0 && (
//                              <Box sx={{ p: { xs: 3, md: 10 } }}>
//                                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
//                                    <img src="/assets/Intergration Page.svg" alt="No plugins installed" style={{ width: "400px", maxWidth:'80%', marginBottom: '24px' }}/>
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" mb={1}>No plugins are added yet</Typography>
//                                     <Typography marginBottom="24px" fontSize="16px" fontFamily="Gilroy-Regular" color="text.secondary">Click below button to explore and add new plugins</Typography>
//                                     {/* Button is already shown outside this block */}
//                                     <Button
//                                         sx={{ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1, '&:hover': { backgroundColor: "dimgray" }, }}
//                                         variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")} >
//                                             Add New Plugin
//                                     </Button>
//                                 </Box>
//                             </Box>
//                         )}
//                     </Box>
//                 </Container>
//             </Box>

//              {/* --- Snackbar for Success Notification --- */}
//             <Snackbar
//                 open={snackbarOpen}
//                 autoHideDuration={5000} // 5 seconds
//                 onClose={handleSnackbarClose}
//                 anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Position top-center
//             >
//                 {/* You might want to add logic here to change severity based on message content (e.g., if it includes "Error") */}
//                 <Alert onClose={handleSnackbarClose} severity="success" variant="filled" sx={{ width: '100%', boxShadow: 3 }}>
//                     {snackbarMessage}
//                 </Alert>
//             </Snackbar>
//         </>
//     );
// }
// export default Integrations;
// import React, { useState, useEffect, useCallback } from "react";
// // --- Added useLocation ---
// import { useNavigate, useLocation } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress,
//     Alert, // <-- Keep Alert
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     ListItemIcon,
//     Chip,
//     Snackbar, // <-- Added Snackbar
//     // SvgIcon, // Keep only if needed elsewhere or as a fallback
// } from "@mui/material";
// // import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // No longer needed if fallback is custom icon too
// // import DeleteIcon from '@mui/icons-material/Delete'; // Replaced with custom SVG

// import queryGraphql from "../../helpers/ApiInterface"; // Make sure path is correct
// import MainDrawer from "../MainDrawer"; // Make sure path is correct

// // --- Plugin Configuration (Assume this is correct as provided) ---
// const pluginDisplayConfig: {
//     [key: string]: {
//         displayName: string;
//         icon?: React.ReactElement;
//         category?: string;
//         description?: string;
//         route?: string;
//     }
//  } = {
//     checkov: {
//         displayName: "Checkov",
//         icon: <img src="/assets/checkov.svg" alt="Checkov Logo" width={40} height={40} />,
//         category: "POLICIES",
//         description: "Automated policy checks for infrastructure as code.",
//         route: "/integrations/checkov-details",
//     },
//     costAgent: {
//         displayName: "GCP Cost Agent",
//         icon: <img src="/assets/gcp.svg" alt="GCP Logo" width={40} height={40} />,
//         category: "COST MANAGEMENT",
//         description: "Tracks and reports Google Cloud Platform costs.",
//         route: "/integrations/gcp-cost-agent",
//     },
//     kyverno: {
//         displayName: "Kyverno",
//         icon: <img src="/assets/kyverno.svg" alt="Kyverno Logo" width={40} height={40} />,
//         category: "POLICIES",
//         description: "Kubernetes native policy management.",
//         route: "/integrations/kyverno-details",
//     },
//     prometheus: {
//         displayName: "Prometheus",
//         icon: <img src="/assets/logos/prometheus.png" alt="Prometheus Logo" width={40} height={40} />,
//         category: "MONITORING",
//         description: "Open-source systems monitoring and alerting toolkit.",
//     },
//     gitlab: {
//         displayName: "Gitlab",
//         icon: <img src="/assets/logos/gitlab.svg" alt="Gitlab Logo" width={40} height={40} />,
//         category: "GIT PROVIDER",
//         description: "Code hosting, CI/CD, and project management.",
//     },
//      github: {
//         displayName: "GitHub",
//         icon: <img src="/assets/logos/github.svg" alt="GitHub Logo" width={40} height={40} />,
//         category: "GIT PROVIDER",
//         description: "Code hosting, version control, and collaboration.",
//     },
//      jenkins: {
//         displayName: "Jenkins",
//          icon: <img src="/assets/logos/jenkins.png" alt="Jenkins Logo" width={40} height={40} />,
//         category: "CICD",
//         description: "Open source automation server.",
//     },
//     default: {
//         displayName: "Plugin",
//         icon: <svg width="40" height="40" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>,
//         category: "GENERAL",
//         description: "View details for this plugin.",
//     }
// };

// // --- Helper Functions (Assume these are correct as provided) ---
// const getInstalledPlugins = (data: any): { key: string; details: any }[] => {
//     if (!data || typeof data !== 'object') return [];
//     const plugins: { key: string; details: any }[] = [];
//     const excludedKeys = ['id', '__typename'];
//     for (const key in data) {
//         if (!excludedKeys.includes(key) && typeof data[key] === 'object' && data[key] !== null && data[key].enabled === true) {
//            plugins.push({ key: key, details: data[key] });
//         }
//     }
//     return plugins;
// };

// const formatKeyForDisplay = (key: string): string => {
//     if (!key) return '';
//     const result = key.replace(/([A-Z])/g, ' $1');
//     return result.charAt(0).toUpperCase() + result.slice(1);
// };


// // ========================================================================
// // --- Main Integrations Component ---
// // ========================================================================
// const Integrations: React.FC = () => {
//     const navigate = useNavigate();
//     const location = useLocation(); // <-- Get location object

//     // --- State ---
//     const [integrations, setIntegrations] = useState<any>(null);
//     const [loading, setLoading] = useState<boolean>(true);
//     const [error, setError] = useState<any>(null);

//     // --- State for Snackbar Notification ---
//     const [snackbarOpen, setSnackbarOpen] = useState(false);
//     const [snackbarMessage, setSnackbarMessage] = useState("");

//     // --- Fetch Data (Keep as is, runs on initial mount) ---
//     useEffect(() => {
//         let isMounted = true;
//         (async () => {
//             try {
//                 setLoading(true);
//                 setError(null);
//                 const data = await queryGraphql(`
//                     query GetIntegrationStatus {
//                       getIntegrations {
//                         checkov { enabled updatedAt }
//                         costAgent { enabled updatedAt }
//                         kyverno { enabled updatedAt }
//                         prometheus { enabled updatedAt }
//                         id
//                       }
//                     }
//                 `);
//                  if (isMounted) {
//                     if (data.getIntegrations) {
//                         setIntegrations(data.getIntegrations);
//                     } else {
//                         console.log("No integration data returned.");
//                         setIntegrations(null);
//                     }
//                 }
//             } catch (err) {
//                 console.error("Error fetching integrations:", err);
//                  if (isMounted) setError(err);
//                  if (isMounted) setIntegrations(null);
//             } finally {
//                  if (isMounted) setLoading(false);
//             }
//         })();
//         return () => { isMounted = false };
//     }, []); // Empty dependency array: run once on mount

//     // --- useEffect to Show Snackbar on Successful Navigation ---
//     // This effect runs when the component mounts *and* whenever location.state changes
//     useEffect(() => {
//         // Check if we navigated here with installation success state
//         if (location.state?.installedPlugin) {
//             setSnackbarMessage(`${location.state.installedPlugin} installed successfully!`);
//             setSnackbarOpen(true);
//             // Clear the state from location history so the snackbar doesn't reappear
//             // on refresh or navigating back/forward to this specific history entry.
//             navigate('.', { replace: true, state: {} });
//         }
//     }, [location.state, navigate]); // Depend on location.state and navigate


//     // --- Derived Data ---
//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     // --- Event Handlers ---
//     const handlePluginClick = (pluginKey: string) => {
//         const config = pluginDisplayConfig[pluginKey] || pluginDisplayConfig.default;
//         const route = config?.route || `/integrations/${pluginKey}`;
//         console.log(`Navigating to: ${route}`);
//         navigate(route);
//     };

//     // --- Snackbar Close Handler ---
//     const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//         if (reason === 'clickaway') {
//             return;
//         }
//         setSnackbarOpen(false);
//     };

//     // --- Render Logic ---
//     return (
//         // Wrap in Fragment to allow Snackbar as sibling
//         <>
//             <Box sx={{ display: "Flex" }}>
//                 <MainDrawer index={"integrations"} />
//                 <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                     {/* --- Header (Keep as is) --- */}
//                     <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                          <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
//                             {/* Conditionally show Add button based on loading/error */}
//                             {!loading && !error && installedCount > 0 &&(
//                                 <Button
//                                     sx={{
//                                         backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold",
//                                         fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1,
//                                         '&:hover': { backgroundColor: "dimgray" },
//                                         '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
//                                     }}
//                                     variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")}>
//                                     Add New Plugin
//                                 </Button>
//                              )}
//                         </Box>
//                         <Divider sx={{ marginY: 1 }} />
//                     </Box>

//                     {/* --- Installed Plugins Sub-Header (Keep as is) --- */}
//                      <Box m={1} sx={{ pl: "25px", pt: 2, pb: 1 }}>
//                         <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                             INSTALLED PLUGINS ({loading ? '...' : installedCount})
//                         </Typography>
//                         <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="text.secondary">
//                             List of all the installed plugins
//                         </Typography>
//                     </Box>

//                     {/* --- Content Area --- */}
//                     <Box>
//                         {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
//                         {error && !loading && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations list. Please try again later.</Alert> </Box> )}

//                         {/* --- CONDITION 1: Plugins Installed --- */}
//                         {!loading && !error && installedCount > 0 && (
//                             <Box sx={{ p: 3, pt: 1 }}>
//                                 <List sx={{ bgcolor: 'background.paper', borderRadius: '8px', p: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
//                                     {installedPlugins.map((plugin, index) => {
//                                         const config = pluginDisplayConfig[plugin.key] || pluginDisplayConfig.default;
//                                         const displayName = config.displayName || formatKeyForDisplay(plugin.key);
//                                         const displayIcon = config.icon;
//                                         const category = config.category || '';
//                                         const description = config.description || `View details for ${displayName}.`;
//                                         let installedDate = 'N/A';
//                                         if (plugin.details?.updatedAt) {
//                                             const dateObj = new Date(plugin.details.updatedAt);
//                                             if (dateObj instanceof Date && !isNaN(dateObj.getTime())) {
//                                                 installedDate = dateObj.toLocaleDateString();
//                                             } else {
//                                                 console.error(`Invalid date format: ${plugin.key} - ${plugin.details.updatedAt}`);
//                                             }
//                                         }

//                                         return (
//                                             <React.Fragment key={plugin.key}>
//                                                 <ListItem
//                                                     disablePadding
//                                                     secondaryAction={
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', pr: { xs: 2, md: '6vh' } , height: '100%' }}>
//                                                             <Typography variant="caption" color="text.secondary" fontFamily="Gilroy-Regular" whiteSpace="nowrap">
//                                                                Installed On: {installedDate}
//                                                             </Typography>
//                                                         </Box>
//                                                     }
//                                                     sx={{ /* Styles remain the same */
//                                                         display: 'flex', alignItems: 'center', border: '1px solid transparent',
//                                                         transition: 'border-color 0s ease-in-out, background-color 0.2s ease-in-out',
//                                                         '&:hover': { borderColor: 'black', backgroundColor: 'action.hover', borderRadius: '1vh', '& .list-item-delete-button': { visibility: 'visible', opacity: 1, }, },
//                                                         position: 'relative',
//                                                     }}
//                                                 >
//                                                     <ListItemButton onClick={() => handlePluginClick(plugin.key)} sx={{ pt: 1.5, pb: 1.5, pl: 2, pr: 1, flexGrow: 1, display: 'flex', alignItems: 'center', }}>
//                                                         <ListItemIcon sx={{ mr: 2, minWidth: 'auto', display: 'flex', alignItems: 'center' }}>
//                                                             {displayIcon}
//                                                         </ListItemIcon>
//                                                         <ListItemText
//                                                             primary={
//                                                                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
//                                                                     <Typography component="span" variant="body1" sx={{ fontFamily: 'Gilroy-SemiBold' }}>
//                                                                         {displayName}
//                                                                     </Typography>
//                                                                     {category && (
//                                                                         <Chip label={category} size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' }}}/>
//                                                                     )}
//                                                                 </Box>
//                                                             }
//                                                             secondary={ <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> {description} </Typography> }
//                                                             sx={{ mr: 1 }}
//                                                         />
//                                                     </ListItemButton>

//                                                     {/* Delete Button (Keep existing logic and styling) */}
//                                                     <Button
//                                                         className="list-item-delete-button"
//                                                         aria-label={`Delete ${displayName}`}
//                                                         title={`Delete ${displayName}`}
//                                                         onClick={async (e) => { /* API call logic remains the same */
//                                                             e.stopPropagation();
//                                                             try {
//                                                                 console.log(`Attempting to disable plugin: ${plugin.key}`);
//                                                                 const pluginUpdateData = { [plugin.key]: { enabled: false } };
//                                                                 const data = await queryGraphql(
//                                                                     `mutation Mutation($input: UpdateIntegrationsInput) {
//                                                                         updateIntegration(input: $input) { id }
//                                                                     }`, { "input": pluginUpdateData }
//                                                                 );
//                                                                 if (data.errors && data.errors.length > 0) throw new Error(data.errors[0].message || 'GraphQL mutation failed.');
//                                                                 console.log(`Successfully disabled plugin: ${plugin.key}, reloading page.`);
//                                                                 window.location.reload();
//                                                             } catch (err) {
//                                                                 console.error(`Failed to disable plugin ${plugin.key}:`, err);
//                                                                 // Removed alert() as requested
//                                                             }
//                                                         }}
//                                                         sx={{ /* Styles remain the same */
//                                                             minWidth: 0, padding: '8px', borderRadius: '50%', color: 'text.secondary', mx: 1, alignSelf: 'center',
//                                                             visibility: 'hidden', opacity: 0, transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out, background-color 0.2s ease-in-out',
//                                                             '&:hover': { backgroundColor: 'action.selected', color: 'error.main', },
//                                                             position: 'relative', zIndex: 1,
//                                                         }}
//                                                     >
//                                                         <img src="/assets/delete-bin-2-line.svg" alt={`Delete ${displayName}`} width={20} height={20} style={{ display: 'block' }} />
//                                                     </Button>
//                                                 </ListItem>
//                                                 {index < installedPlugins.length - 1 && <Divider component="li" variant="inset" sx={{ ml: { xs: 2, md: 'calc(40px + 16px + 16px)' } }} />}
//                                             </React.Fragment>
//                                         );
//                                     })}
//                                 </List>
//                             </Box>
//                         )}

//                         {/* --- CONDITION 2: No Plugins Installed (Keep as is) --- */}
//                         {!loading && !error && installedCount === 0 && (
//                              <Box sx={{ p: { xs: 3, md: 10 } }}>
//                                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
//                                    <img src="/assets/Intergration Page.svg" alt="No plugins installed" style={{ width: "400px", maxWidth:'80%', marginBottom: '24px' }}/>
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" mb={1}>No plugins are added yet</Typography>
//                                     <Typography marginBottom="24px" fontSize="16px" fontFamily="Gilroy-Regular" color="text.secondary">Click below button to explore and add new plugins</Typography>
//                                     <Button
//                                         sx={{ backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1, '&:hover': { backgroundColor: "dimgray" }, }}
//                                         variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")} >
//                                             Add New Plugin
//                                     </Button>
//                                 </Box>
//                             </Box>
//                         )}
//                     </Box>
//                 </Container>
//             </Box>

//              {/* --- Snackbar for Success Notification --- */}
//             <Snackbar
//                 open={snackbarOpen}
//                 autoHideDuration={5000} // 5 seconds
//                 onClose={handleSnackbarClose}
//                 anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Position top-center
//             >
//                 {/* Severity="success" gives it the green style like the image */}
//                 <Alert onClose={handleSnackbarClose} severity="success" variant="filled" sx={{ width: '100%', boxShadow: 3 }}>
//                     {snackbarMessage}
//                 </Alert>
//             </Snackbar>
//         </>
//     );
// }
// export default Integrations;
// import React, { useState, useEffect, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress,
//     Alert,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     ListItemIcon,
//     Chip,
//     // SvgIcon, // Keep only if needed elsewhere or as a fallback
// } from "@mui/material";
// // import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // No longer needed if fallback is custom icon too
// // import DeleteIcon from '@mui/icons-material/Delete'; // Replaced with custom SVG

// import queryGraphql from "../../helpers/ApiInterface"; // Make sure path is correct
// import MainDrawer from "../MainDrawer"; // Make sure path is correct

// // --- Plugin Configuration (Assume this is correct as provided) ---
// const pluginDisplayConfig: {
//     [key: string]: {
//         displayName: string;
//         icon?: React.ReactElement;
//         category?: string;
//         description?: string;
//         route?: string;
//     }
//  } = {
//     checkov: {
//         displayName: "Checkov",
//         icon: <img src="/assets/checkov.svg" alt="Checkov Logo" width={40} height={40} />, // Adjusted size slightly
//         category: "POLICIES",
//         description: "Automated policy checks for infrastructure as code.",
//         route: "/integrations/checkov-details",
//     },
//     costAgent: {
//         displayName: "GCP Cost Agent",
//         icon: <img src="/assets/gcp.svg" alt="GCP Logo" width={40} height={40} />, // Adjusted size slightly
//         category: "COST MANAGEMENT",
//         description: "Tracks and reports Google Cloud Platform costs.",
//         route: "/integrations/gcp-cost-agent",
//     },
//     kyverno: {
//         displayName: "Kyverno",
//         icon: <img src="/assets/kyverno.svg" alt="Kyverno Logo" width={40} height={40} />, // Adjusted size slightly
//         category: "POLICIES",
//         description: "Kubernetes native policy management.",
//         route: "/integrations/kyverno-details",
//     },
//     prometheus: {
//         displayName: "Prometheus",
//         icon: <img src="/assets/logos/prometheus.png" alt="Prometheus Logo" width={40} height={40} />, // Adjusted size slightly
//         category: "MONITORING",
//         description: "Open-source systems monitoring and alerting toolkit.",
//     },
//     gitlab: {
//         displayName: "Gitlab",
//         icon: <img src="/assets/logos/gitlab.svg" alt="Gitlab Logo" width={40} height={40} />, // Adjusted size slightly
//         category: "GIT PROVIDER",
//         description: "Code hosting, CI/CD, and project management.",
//     },
//      github: {
//         displayName: "GitHub",
//         icon: <img src="/assets/logos/github.svg" alt="GitHub Logo" width={40} height={40} />, // Adjusted size slightly
//         category: "GIT PROVIDER",
//         description: "Code hosting, version control, and collaboration.",
//     },
//      jenkins: {
//         displayName: "Jenkins",
//          icon: <img src="/assets/logos/jenkins.png" alt="Jenkins Logo" width={40} height={40} />, // Adjusted size slightly
//         category: "CICD",
//         description: "Open source automation server.",
//     },
//     // Add a default/fallback icon if needed
//     default: {
//         displayName: "Plugin",
//         // Example using an inline SVG or placeholder
//         icon: <svg width="40" height="40" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>,
//         category: "GENERAL",
//         description: "View details for this plugin.",
//     }
// };

// // --- Helper Functions (Assume these are correct as provided) ---
// const getInstalledPlugins = (data: any): { key: string; details: any }[] => {
//     if (!data || typeof data !== 'object') return [];
//     const plugins: { key: string; details: any }[] = [];
//     const excludedKeys = ['id', '__typename'];
//     for (const key in data) {
//         if (!excludedKeys.includes(key) && typeof data[key] === 'object' && data[key] !== null && data[key].enabled === true) {
//            plugins.push({ key: key, details: data[key] });
//         }
//     }
//     return plugins;
// };

// const formatKeyForDisplay = (key: string): string => {
//     if (!key) return '';
//     const result = key.replace(/([A-Z])/g, ' $1');
//     return result.charAt(0).toUpperCase() + result.slice(1);
// };


// // ========================================================================
// // --- Main Integrations Component ---
// // ========================================================================
// const Integrations: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State ---
//     const [integrations, setIntegrations] = useState<any>(null);
//     const [loading, setLoading] = useState<boolean>(true);
//     const [error, setError] = useState<any>(null);

//     // --- Fetch Data (Keep as is) ---
//     useEffect(() => {
//         let isMounted = true;
//         (async () => {
//             try {
//                 setLoading(true);
//                 setError(null);
//                 const data = await queryGraphql(`
//                     query GetIntegrationStatus {
//                       getIntegrations {
//                         checkov { enabled updatedAt }
//                         costAgent { enabled updatedAt }
//                         kyverno { enabled updatedAt }
//                         prometheus { enabled updatedAt }
//                         id
//                       }
//                     }
//                 `);
//                  if (isMounted) {
//                     if (data.getIntegrations) {
//                         setIntegrations(data.getIntegrations);
//                     } else {
//                         console.log("No integration data returned.");
//                         setIntegrations(null);
//                     }
//                 }
//             } catch (err) {
//                 console.error("Error fetching integrations:", err);
//                  if (isMounted) setError(err);
//                  if (isMounted) setIntegrations(null);
//             } finally {
//                  if (isMounted) setLoading(false);
//             }
//         })();
//         return () => { isMounted = false };
//     }, []);

//     // --- Derived Data ---
//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     // --- Event Handlers ---
//     const handlePluginClick = (pluginKey: string) => {
//         const config = pluginDisplayConfig[pluginKey] || pluginDisplayConfig.default;
//         const route = config?.route || `/integrations/${pluginKey}`;
//         console.log(`Navigating to: ${route}`);
//         navigate(route);
//     };

//     // --- Render Logic ---
//     return (
//         <Box sx={{ display: "Flex" }}>
//             <MainDrawer index={"integrations"} />
//             <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                 {/* --- Header (Keep as is) --- */}
//                 <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                      <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
//                         <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
//                         {!loading && !error && installedCount > 0 &&( // Show button even if count is 0
//                             <Button
//                                 sx={{
//                                     backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold",
//                                     fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1,
//                                     '&:hover': { backgroundColor: "dimgray" },
//                                     '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
//                                 }}
//                                 variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")}>
//                                 Add New Plugin
//                             </Button>
//                         )}
//                     </Box>
//                     <Divider sx={{ marginY: 1 }} />
//                 </Box>

//                 {/* --- Installed Plugins Sub-Header (Keep as is) --- */}
//                  <Box m={1} sx={{ pl: "25px", pt: 2, pb: 1 }}>
//                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                         INSTALLED PLUGINS ({loading ? '...' : installedCount})
//                     </Typography>
//                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="text.secondary">
//                         List of all the installed plugins
//                     </Typography>
//                 </Box>

//                 {/* --- Content Area --- */}
//                 <Box>
//                     {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
//                     {error && !loading && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations list. Please try again later.</Alert> </Box> )}

//                     {/* --- CONDITION 1: Plugins Installed --- */}
//                     {!loading && !error && installedCount > 0 && (
//                         <Box sx={{ p: 3, pt: 1 }}>
//                             <List sx={{ bgcolor: 'background.paper', borderRadius: '8px', p: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' /* Optional: subtle shadow */ }}>
//                                 {installedPlugins.map((plugin, index) => {
//                                     // --- Get display info ---
//                                     const config = pluginDisplayConfig[plugin.key] || pluginDisplayConfig.default; // Use default config as fallback
//                                     const displayName = config.displayName || formatKeyForDisplay(plugin.key);
//                                     const displayIcon = config.icon; // Icon comes directly from config now
//                                     const category = config.category || '';
//                                     const description = config.description || `View details for ${displayName}.`;

//                                     // --- Calculate 'Installed On' Date ---
//                                     let installedDate = 'N/A';
//                                     if (plugin.details?.updatedAt) {
//                                         const dateObj = new Date(plugin.details.updatedAt);
//                                         if (dateObj instanceof Date && !isNaN(dateObj.getTime())) {
//                                             installedDate = dateObj.toLocaleDateString(); // Consider using toLocaleDateString for better formatting
//                                         } else {
//                                             console.error(`Invalid date format: ${plugin.key} - ${plugin.details.updatedAt}`);
//                                         }
//                                     }

//                                     return (
//                                         <React.Fragment key={plugin.key}>
//                                             <ListItem
//                                                 disablePadding
//                                                 secondaryAction={
//                                                     // Keep the date display, adjust padding if needed
//                                                     <Box sx={{ display: 'flex', alignItems: 'center', pr: { xs: 2, md: '6vh' } , height: '100%' }}>
//                                                         <Typography variant="caption" color="text.secondary" fontFamily="Gilroy-Regular" whiteSpace="nowrap">
//                                                            Installed On: {installedDate}
//                                                         </Typography>
//                                                     </Box>
//                                                 }
//                                                 sx={{
//                                                     display: 'flex',
//                                                     alignItems: 'center',
//                                                     // Added hover border
//                                                     border: '1px solid transparent', // Start with transparent border
//                                                     transition: 'border-color 0s ease-in-out, background-color 0.2s ease-in-out', // Smooth transitions
//                                                     '&:hover': {
//                                                         borderColor: 'black', // Black border on hover
//                                                         backgroundColor: 'action.hover', // Optional: subtle background highlight
//                                                         borderRadius: '1vh',
//                                                         '& .list-item-delete-button': { // Target delete button on parent hover
//                                                             visibility: 'visible',
//                                                             opacity: 1,
                                                            
//                                                         },
//                                                     },
//                                                     position: 'relative',
//                                                 }}
//                                             >
//                                                 {/* Clickable area for navigation */}
//                                                 <ListItemButton
//                                                     onClick={() => handlePluginClick(plugin.key)}
//                                                     sx={{
//                                                         pt: 1.5, pb: 1.5, pl: 2, pr: 1, // Adjust padding as needed
//                                                         flexGrow: 1, // Takes available space
//                                                         display: 'flex', // Use flex inside button for better control
//                                                         alignItems: 'center',
//                                                     }}
//                                                  >
//                                                     {/* Icon */}
//                                                     <ListItemIcon sx={{ mr: 2, minWidth: 'auto', display: 'flex', alignItems: 'center' }}> {/* Ensure icon aligns */}
//                                                         {displayIcon}
//                                                     </ListItemIcon>
//                                                     {/* Text Content */}
//                                                     <ListItemText
//                                                         primary={
//                                                             <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
//                                                                 <Typography component="span" variant="body1" sx={{ fontFamily: 'Gilroy-SemiBold' }}>
//                                                                     {displayName}
//                                                                 </Typography>
//                                                                 {category && (
//                                                                     <Chip /* Keep chip styling as is */
//                                                                         label={category} size="small"
//                                                                         sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }}/>
//                                                                 )}
//                                                             </Box>
//                                                         }
//                                                         secondary={
//                                                             <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>
//                                                                 {description}
//                                                             </Typography>
//                                                         }
//                                                         sx={{ mr: 1 }} // Add some margin before the delete button appears
//                                                     />
//                                                 </ListItemButton>

//                                                 {/* Delete Button */}
//                                                 <Button
//                                                     className="list-item-delete-button" // For hover targeting
//                                                     aria-label={`Delete ${displayName}`}
//                                                     title={`Delete ${displayName}`}
//                                                     onClick={async (e) => { // Keep async/API logic
//                                                         e.stopPropagation(); // Prevent ListItemButton click

//                                                         try {
//                                                             console.log(`Attempting to disable plugin: ${plugin.key}`);
//                                                             const pluginUpdateData = { [plugin.key]: { enabled: false } };
//                                                             const data = await queryGraphql(
//                                                                 `mutation Mutation($input: UpdateIntegrationsInput) {
//                                                                     updateIntegration(input: $input) { id } # Minimal response needed
//                                                                 }`, { "input": pluginUpdateData }
//                                                             );

//                                                             if (data.errors && data.errors.length > 0) {
//                                                                 throw new Error(data.errors[0].message || 'GraphQL mutation failed.');
//                                                             }
//                                                             console.log(`Successfully disabled plugin: ${plugin.key}, reloading page.`);
//                                                             window.location.reload(); // Reload to reflect changes
//                                                         } catch (err) {
//                                                             console.error(`Failed to disable plugin ${plugin.key}:`, err);
//                                                             // --- CHANGE START ---
//                                                             // The alert() line below was removed as requested.
//                                                             // alert(`Failed to delete ${displayName}. See console for details.`);
//                                                             // --- CHANGE END ---
//                                                         }
//                                                     }}
//                                                     // Updated styling for custom icon & clickability
//                                                     sx={{
//                                                         minWidth: 0,        // Crucial for icon buttons
//                                                         padding: '8px',     // Use fixed padding
//                                                         borderRadius: '50%', // Make it circular
//                                                         color: 'text.secondary', // Icon color (inherited) or set explicitly
//                                                         mx: 1,             // Margin left/right for spacing
//                                                         alignSelf: 'center', // Vertical alignment
//                                                         // Hover visibility/opacity (controlled by parent hover)
//                                                         visibility: 'hidden',
//                                                         opacity: 0,
//                                                         transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out, background-color 0.2s ease-in-out',
//                                                         '&:hover': {
//                                                             backgroundColor: 'action.selected', // Slightly darker hover for the button itself
//                                                             color: 'error.main', // Optional: turn icon red on hover
//                                                         },
//                                                         position: 'relative', // Keep in flow
//                                                         zIndex: 1, // Ensure it's clickable if any overlap occurs
//                                                     }}
//                                                 >
//                                                     {/* Use Custom SVG Icon */}
//                                                     <img
//                                                         // !!! IMPORTANT: Make sure this path is correct for your project !!!
//                                                         src="/assets/delete-bin-2-line.svg"
//                                                         alt={`Delete ${displayName}`}
//                                                         width={20} // Adjust size as needed
//                                                         height={20}
//                                                         style={{ display: 'block' }} // Prevents potential extra space below img
//                                                     />
//                                                 </Button>
//                                             </ListItem>
//                                             {/* Divider */}
//                                             {index < installedPlugins.length - 1 && <Divider component="li" variant="inset" sx={{ ml: { xs: 2, md: 'calc(40px + 16px + 16px)' } }} /* Adjust inset based on icon+padding */ />}
//                                         </React.Fragment>
//                                     );
//                                 })}
//                             </List>
//                         </Box>
//                     )}

//                     {/* --- CONDITION 2: No Plugins Installed (Keep as is) --- */}
//                     {!loading && !error && installedCount === 0 && (
//                          <Box sx={{ p: { xs: 3, md: 10 } }}> {/* Responsive padding */}
//                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
//                                <img src="/assets/Intergration Page.svg" alt="No plugins installed" style={{ width: "400px", maxWidth:'80%', marginBottom: '24px' }}/> {/* Increased margin */}
//                                 <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" mb={1}>No plugins are added yet</Typography> {/* Added margin */}
//                                 <Typography marginBottom="24px" fontSize="16px" fontFamily="Gilroy-Regular" color="text.secondary">Click below button to explore and add new plugins</Typography> {/* Increased margin & color */}
//                                 <Button
//                                     sx={{
//                                         backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold",
//                                         fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1,
//                                         '&:hover': { backgroundColor: "dimgray" },
//                                      }}
//                                     variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")} >
//                                         Add New Plugin
//                                 </Button>
//                             </Box>
//                         </Box>
//                     )}
//                 </Box>
//             </Container>
//         </Box>
//     );
// }
// export default Integrations;
// import React, { useState, useEffect, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress,
//     Alert,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     ListItemIcon,
//     Chip,
//     SvgIcon, // Keep for potential fallback/default icons
// } from "@mui/material";
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import DeleteIcon from '@mui/icons-material/Delete';
// import queryGraphql from "../../helpers/ApiInterface"; // Make sure path is correct
// import MainDrawer from "../MainDrawer"; // Make sure path is correct

// // --- Plugin Configuration ---
// // Defines display properties and navigation routes for known plugins.
// // Add your custom logos via the 'icon' property.
// const pluginDisplayConfig: {
//     [key: string]: {
//         displayName: string;
//         icon?: React.ReactElement;
//         category?: string;
//         description?: string;
//         route?: string; // Optional: Define specific detail page route
//     }
//  } = {
//     checkov: {
//         displayName: "Checkov",
//         icon: <img src="/assets/checkov.svg" alt="Checkov Logo" width={50} height={50} />,
//         category: "POLICIES",
//         description: "Automated policy checks for infrastructure as code.",
//         route: "/integrations/checkov-details", // Example specific route
//     },
//     costAgent: {
//         displayName: "GCP Cost Agent",
//         icon: <img src="/assets/gcp.svg" alt="GCP Logo" width={50} height={50} />,
//         category: "COST MANAGEMENT",
//         description: "Tracks and reports Google Cloud Platform costs.",
//         route: "/integrations/cost-agent",
//     },
//     kyverno: {
//         displayName: "Kyverno",
//         icon: <img src="/assets/kyverno.svg" alt="Kyverno Logo" width={50} height={50} />,
//         category: "POLICIES",
//         description: "Kubernetes native policy management.",
//         route: "/integrations/kyverno-details",
//     },
//     prometheus: {
//         displayName: "Prometheus",
//         icon: <img src="/assets/logos/prometheus.png" alt="Prometheus Logo" width={24} height={24} />,
//         category: "MONITORING",
//         description: "Open-source systems monitoring and alerting toolkit.",
//         // Route will default to /integrations/prometheus
//     },
//     gitlab: {
//         displayName: "Gitlab",
//         icon: <img src="/assets/logos/gitlab.svg" alt="Gitlab Logo" width={24} height={24} />,
//         category: "GIT PROVIDER",
//         description: "Code hosting, CI/CD, and project management.",
//     },
//      github: {
//         displayName: "GitHub",
//         icon: <img src="/assets/logos/github.svg" alt="GitHub Logo" width={24} height={24} />,
//         category: "GIT PROVIDER",
//         description: "Code hosting, version control, and collaboration.",
//     },
//      jenkins: {
//         displayName: "Jenkins",
//          icon: <img src="/assets/logos/jenkins.png" alt="Jenkins Logo" width={24} height={24} />,
//         category: "CICD",
//         description: "Open source automation server.",
//     },
// };

// // --- Helper Functions ---

// /** Discovers enabled plugins from fetched data */
// const getInstalledPlugins = (data: any): { key: string; details: any }[] => {
//     // Only needs to find keys with { enabled: true, ... }
//     if (!data || typeof data !== 'object') return [];
//     const plugins: { key: string; details: any }[] = [];
//     const excludedKeys = ['id', '__typename'];
//     for (const key in data) {
//         if (!excludedKeys.includes(key) && typeof data[key] === 'object' && data[key] !== null && data[key].enabled === true) {
//            // Include details object even if only updatedAt is used here,
//            // might be useful for passing basic info during navigation later.
//            plugins.push({ key: key, details: data[key] });
//         }
//     }
//     return plugins;
// };

// /** Formats key for display */
// const formatKeyForDisplay = (key: string): string => {
//     if (!key) return '';
//     const result = key.replace(/([A-Z])/g, ' $1');
//     return result.charAt(0).toUpperCase() + result.slice(1);
// };


// // ========================================================================
// // --- Main Integrations Component ---
// // ========================================================================
// const Integrations: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State ---
//     const [integrations, setIntegrations] = useState<any>(null); // Fetched data
//     const [loading, setLoading] = useState<boolean>(true);      // Loading state
//     const [error, setError] = useState<any>(null);              // Error state

//     // --- Fetch Data ---
//     useEffect(() => {
//         let isMounted = true;
//         (async () => {
//             try {
//                 setLoading(true);
//                 setError(null);
//                  // Optimized Query: Fetch only necessary fields for THIS list view
//                 const data = await queryGraphql(`
//                     query GetIntegrationStatus {
//                       getIntegrations {
//                         # Request only fields needed for the list display:
//                         # 'enabled' (to filter), 'updatedAt' (for date).
//                         checkov { enabled updatedAt }
//                         costAgent { enabled updatedAt }
//                         kyverno { enabled updatedAt }
//                         prometheus { enabled updatedAt }
//                         # Add other plugins here if they might exist at top level
//                         # gitlab { enabled updatedAt }
//                         # github { enabled updatedAt }
//                         # jenkins { enabled updatedAt }
//                         id # Usually good to fetch the ID
//                       }
//                     }
//                 `);
//                  if (isMounted) {
//                     if (data.getIntegrations) {
//                         setIntegrations(data.getIntegrations);
//                     } else {
//                         console.log("No integration data returned.");
//                         setIntegrations(null);
//                     }
//                 }
//             } catch (err) {
//                 console.error("Error fetching integrations:", err);
//                  if (isMounted) setError(err);
//                  if (isMounted) setIntegrations(null);
//             } finally {
//                  if (isMounted) setLoading(false);
//             }
//         })();
//         return () => { isMounted = false }; // Cleanup on unmount
//     }, []); // Run only once on mount

//     // --- Derived Data ---
//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     // --- Event Handlers ---
//     const handlePluginClick = (pluginKey: string) => {
//         // Navigate to a detail page for the clicked plugin
//         const config = pluginDisplayConfig[pluginKey];
//         const route = config?.route || `/integrations/${pluginKey}`; // Use configured route or default
//         console.log(`Navigating to: ${route}`);
//         navigate(route);
//         // The detail page component (e.g., at '/integrations/cost-agent')
//         // will be responsible for fetching and displaying detailed data.
//     };

//     // --- Render Logic ---
//     return (
//         <Box sx={{ display: "Flex" }}>
//             <MainDrawer index={"integrations"} />
//             <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                 {/* --- Header --- */}
//                 <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                     <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
//                         <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
//                         {!loading && !error && installedCount > 0 &&(
//                             <Button
//                                 sx={{ /* Button Styles */
//                                     backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold",
//                                     fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1,
//                                     '&:hover': { backgroundColor: "dimgray" },
//                                     '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
//                                 }}
//                                 variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")}>
//                                 Add New Plugin
//                             </Button>
//                         )}
//                     </Box>
//                     <Divider sx={{ marginY: 1 }} />
//                 </Box>

//                 {/* --- Installed Plugins Sub-Header --- */}
//                 <Box m={1} sx={{ pl: "25px", pt: 2, pb: 1 }}>
//                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                         Installed Plugins ({loading ? '...' : installedCount})
//                     </Typography>
//                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="text.secondary">
//                         List of all the installed plugins
//                     </Typography>
//                 </Box>

//                 {/* --- Content Area --- */}
//                 <Box>
//                     {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
//                     {error && !loading && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations list. Please try again later.</Alert> </Box> )}

//                     {/* --- CONDITION 1: Plugins Installed --- */}
//                     {!loading && !error && installedCount > 0 && (
//                         <Box sx={{ p: 3, pt: 1 }}>
//                             <List sx={{ bgcolor: 'background.paper', borderRadius: '8px', p: 0 }}>
//                                 {installedPlugins.map((plugin, index) => {
//                                     // --- Get display info (mostly from config now) ---
//                                     const config = pluginDisplayConfig[plugin.key] || {};
//                                     const displayName = config.displayName || formatKeyForDisplay(plugin.key);
//                                     const displayIcon = config.icon || <CheckCircleOutlineIcon color="disabled" />; // Fallback icon
//                                     const category = config.category || '';
//                                     const description = config.description || `View details for ${displayName}.`; // More generic default description
                                    
//                                     // --- Calculate 'Installed On' Date (Validated) ---
//                                     let installedDate = 'N/A';
//                                     if (plugin.details?.updatedAt) {
//                                         const dateObj = new Date(plugin.details.updatedAt);
//                                         if (dateObj instanceof Date && !isNaN(dateObj.getTime())) {
//                                             installedDate = dateObj.toLocaleDateString();
//                                         } else {
//                                             console.error(`Invalid date format encountered: ${plugin.key} - ${plugin.details.updatedAt}`);
//                                         }
//                                     }

//                                     return (
//                                         <React.Fragment key={plugin.key}>
//                                             <ListItem
//                                                 disablePadding
//                                                 secondaryAction={ // Keep original secondary action (only date)
//                                                     <Box sx={{ display: 'flex', alignItems: 'center', pr: '6vh', height: '100%' }}>
//                                                         <Typography variant="caption" color="text.secondary" fontFamily="Gilroy-Regular">
//                                                            Installed On : {installedDate}
//                                                         </Typography>
//                                                     </Box>
//                                                 }
//                                                 // Add hover style and flex layout
//                                                 sx={{
//                                                     // Keep hover selector for the button
//                                                     '&:hover .list-item-delete-button': {
//                                                         visibility: 'visible',
//                                                         opacity: 1,
                                                        
//                                                     },
//                                                     // Use Flexbox layout for direct children (ListItemButton and Button)
//                                                     // This prevents overlap by placing them side-by-side.
//                                                     display: 'flex',
//                                                     alignItems: 'center', // Vertically center items
//                                                 }}
//                                             >
//                                                 {/* Entire item is clickable */}
//                                                 <ListItemButton
//                                                     onClick={() => handlePluginClick(plugin.key)}
//                                                     sx={{
//                                                         pt: 1.5,
//                                                         pb: 1.5,
//                                                         flexGrow: 1, // Allow this part to take up available horizontal space
//                                                         // This pushes the delete button towards the right, before the secondaryAction area
//                                                     }}
//                                                  >
//                                                     {/* Icon */}
//                                                     <ListItemIcon sx={{ mr: 2, minWidth: 40, alignSelf: 'center' }}>
//                                                         {displayIcon}
//                                                     </ListItemIcon>
//                                                     {/* Text Content */}
//                                                     <ListItemText
//                                                         primary={ // Name + Category Chip
//                                                             <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
//                                                                 <Typography component="span" variant="body1" sx={{ fontFamily: 'Gilroy-SemiBold' }}> {/* Adjusted Font */}
//                                                                     {displayName}
//                                                                 </Typography>
//                                                                 {category && (
//                                                                     <Chip
//                                                                         label={category}
//                                                                         size="small"
//                                                                         sx={{ /* Styles for category chip */
//                                                                             ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px',
//                                                                             bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular',
//                                                                             fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase',
//                                                                             letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' }
//                                                                         }}
//                                                                     />
//                                                                 )}
//                                                             </Box>
//                                                         }
//                                                         secondary={ // ONLY Description from config
//                                                             <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>
//                                                                 {description}
//                                                             </Typography>
//                                                         }
//                                                     />
//                                                 </ListItemButton>
//                                                 <Button
//                                                     className="list-item-delete-button" // Class for hover targeting
//                                                     aria-label={`Delete ${displayName}`}
//                                                     title={`Delete ${displayName}`} // Tooltip
//                                                     onClick={async (e) => {
//                                                         e.stopPropagation(); // Prevent ListItemButton click

//                                                         // Minimal try/catch for API call safety
//                                                         try {
//                                                             console.log(`Attempting to delete plugin: ${plugin.key}`); // Logging start

//                                                             // **MODIFIED**: Use dynamic plugin.key and set value to null for deletion
//                                                             const pluginUpdateData = {
//                                                                 [plugin.key]:{
//                                                                     enabled:false
//                                                                 } // Use dynamic key and null value
//                                                             };

//                                                             const data = await queryGraphql(
//                                                                 `
//                                                                 mutation Mutation($input: UpdateIntegrationsInput) {
//                                                                     updateIntegration(input: $input) {
//                                                                         # Minimal fields needed, adjust if necessary
//                                                                         costAgent { enabled }
//                                                                         checkov { enabled }
//                                                                         kyverno { enabled }
//                                                                         # ... other plugins
//                                                                     }
//                                                                 }
//                                                                 `,
//                                                                 {
//                                                                     "input": pluginUpdateData // Pass the dynamic data
//                                                                 }
//                                                             );

//                                                             // Basic check if mutation seemed to execute without GraphQL errors
//                                                             if (data.errors && data.errors.length > 0) {
//                                                                 throw new Error(data.errors[0].message || 'GraphQL mutation failed.');
//                                                             }

//                                                             console.log(`Deletion successful for plugin: ${plugin.key}, reloading page.`);

//                                                             // **ADDED**: Refresh page on successful deletion
//                                                             window.location.reload();

//                                                         } catch (err) {
//                                                             console.error(`Failed to delete plugin ${plugin.key}:`, err);
//                                                             // No UI error state update as requested, just log it
//                                                             alert(`Failed to delete ${displayName}. See console for details.`); // Basic alert fallback
//                                                         }
//                                                     }}
//                                                     // Styling to make Button look like a small icon button & handle hover visibility
//                                                     sx={{
//                                                         // Removed absolute positioning
//                                                         // Style as icon button
//                                                         minWidth: 0,
//                                                         padding: '1.5vh',
//                                                         //paddingRight:'4px',
//                                                         borderRadius: '50%',
//                                                         color: 'text.secondary',
//                                                         '&:hover': { backgroundColor: 'action.hover' },
//                                                         // Visibility handled by parent hover
//                                                         visibility: 'hidden',
//                                                         opacity: 2,
//                                                         transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
//                                                         // Added margin & alignment
//                                                         ml: 1, // Add space between text and button
//                                                         alignSelf: 'center', // Try to vertically center
//                                                     }}
//                                                 >
//                                                     {/* Use DeleteIcon inside the button */}
//                                                     <DeleteIcon fontSize="medium" />
//                                                 </Button>
//                                             </ListItem>
//                                             {/* Divider */}
//                                             {index < installedPlugins.length - 1 && <Divider component="li" variant="inset"/>}
//                                         </React.Fragment>
//                                     );
//                                 })}
//                             </List>
//                         </Box>
//                     )}

//                     {/* --- CONDITION 2: No Plugins Installed --- */}
//                     {!loading && !error && installedCount === 0 && (
//                          <Box sx={{ p: 10 }}>
//                              {/* Empty state content */}
//                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
//                                <img src="/assets/Intergration Page.svg" alt="No plugins installed" style={{ width: "400px", maxWidth:'80%', marginBottom: '16px' }}/>
//                                 <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">No plugins are added yet</Typography>
//                                 <Typography marginTop="0.5vh" marginBottom="16px" fontSize="16px" fontFamily="Gilroy-Regular">Click below button to explore and add new plugins</Typography>
//                                 <Button
//                                     sx={{ /* Button Styles */
//                                         backgroundColor: "black", color: "white", fontFamily: "Gilroy-Bold",
//                                         fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1,
//                                         '&:hover': { backgroundColor: "dimgray" },
//                                      }}
//                                     variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")} >
//                                         Add New Plugin
//                                 </Button>
//                             </Box>
//                         </Box>
//                     )}
//                 </Box>
//             </Container>
//         </Box>
//     );
// }
// export default Integrations;

// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress,
//     Alert,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     ListItemIcon,
//     Chip,
//     SvgIcon, // Keep for potential fallback/default icons
// } from "@mui/material";
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// // Remove react-icons imports if only using custom images
// // import { SiCheckio, SiPrometheus, SiKubernetes } from 'react-icons/si';
// // import { FaGoogle } from 'react-icons/fa';

// import queryGraphql from "../../helpers/ApiInterface"; // Make sure path is correct
// import MainDrawer from "../MainDrawer"; // Make sure path is correct

// // --- Plugin Configuration ---
// // Using custom <img> tags for icons. Update paths as needed.
// const pluginDisplayConfig: {
//     [key: string]: {
//         displayName: string;
//         icon?: React.ReactElement; // Can still be a React element if needed
//         category?: string;
//         description?: string;
//         route?: string;
//     }
//  } = {
//     checkov: {
//         displayName: "Checkov",
//         // Use an img tag pointing to your logo in the public folder
//         icon: <img src="/assets/logos/checkov.svg" alt="Checkov Logo" width={24} height={24} />,
//         category: "POLICIES",
//         description: "Automated policy checks for infrastructure as code.",
//         route: "/integrations/checkov-details",
//     },
//     costAgent: {
//         displayName: "GCP Cost Agent",
//         icon: <img src="/assets/gcp.svg" alt="GCP Logo" width={48} height={48} />, // Example path
//         category: "COST AGENT", // Changed to match example UI text better
//         description: "Tracks and reports Google Cloud Platform costs.",
//         route: "/integrations/cost-agent",
//     },
//     kyverno: {
//         displayName: "Kyverno",
//         icon: <img src="/assets/logos/kyverno.svg" alt="Kyverno Logo" width={24} height={24} />, // Example path
//         category: "POLICIES",
//         description: "Kubernetes native policy management.",
//         route: "/integrations/kyverno-details",
//     },
//     prometheus: {
//         displayName: "Prometheus",
//         icon: <img src="/assets/logos/prometheus.png" alt="Prometheus Logo" width={24} height={24} />, // Example path
//         category: "MONITORING",
//         description: "Open-source systems monitoring and alerting toolkit.",
//     },
//     gitlab: {
//         displayName: "Gitlab",
//         icon: <img src="/assets/logos/gitlab.svg" alt="Gitlab Logo" width={24} height={24} />, // Example path
//         category: "GIT PROVIDER",
//         description: "Code hosting, CI/CD, and project management.",
//     },
//      github: {
//         displayName: "GitHub",
//         icon: <img src="/assets/logos/github.svg" alt="GitHub Logo" width={24} height={24} />, // Example path
//         category: "GIT PROVIDER",
//         description: "Code hosting, version control, and collaboration.",
//     },
//      jenkins: {
//         displayName: "Jenkins",
//          icon: <img src="/assets/logos/jenkins.png" alt="Jenkins Logo" width={24} height={24} />, // Example path
//         category: "CICD",
//         description: "Open source automation server.",
//     },
// };

// // --- Helper Functions ---

// /** Discovers enabled plugins from fetched data */
// const getInstalledPlugins = (data: any): { key: string; details: any }[] => {
//     // ... (implementation remains the same) ...
//     if (!data || typeof data !== 'object') return [];
//     const plugins: { key: string; details: any }[] = [];
//     const excludedKeys = ['id', '__typename'];
//     for (const key in data) {
//         if (!excludedKeys.includes(key) && typeof data[key] === 'object' && data[key] !== null && data[key].enabled === true) {
//            plugins.push({ key: key, details: data[key] });
//         }
//     }
//     return plugins;
// };

// /** Formats key for display */
// const formatKeyForDisplay = (key: string): string => {
//     // ... (implementation remains the same) ...
//     if (!key) return '';
//     const result = key.replace(/([A-Z])/g, ' $1');
//     return result.charAt(0).toUpperCase() + result.slice(1);
// };


// // ========================================================================
// // --- Main Integrations Component ---
// // ========================================================================
// const Integrations: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State ---
//     const [integrations, setIntegrations] = useState<any>(null);
//     const [loading, setLoading] = useState<boolean>(true);
//     const [error, setError] = useState<any>(null);

//     // --- Fetch Data ---
//     useEffect(() => {
//         // ... (fetch logic remains the same) ...
//         let isMounted = true;
//         (async () => {
//             try {
//                 setLoading(true);
//                 setError(null);
//                 const data = await queryGraphql(`
//                     query Query {
//                       getIntegrations {
//                         checkov { enabled updatedAt repoUrl }
//                         costAgent { enabled updatedAt projectId datasetId tableId serviceAccountName }
//                         kyverno { enabled updatedAt repoUrl }
//                         prometheus { enabled updatedAt repoUrl }
//                         id
//                       }
//                     }
//                 `);
//                  if (isMounted) {
//                     if (data.getIntegrations) {
//                         setIntegrations(data.getIntegrations);
//                     } else {
//                         console.log("No integration data returned.");
//                         setIntegrations(null);
//                     }
//                 }
//             } catch (err) {
//                 console.error("Error fetching integrations:", err);
//                  if (isMounted) setError(err);
//                  if (isMounted) setIntegrations(null);
//             } finally {
//                  if (isMounted) setLoading(false);
//             }
//         })();
//         return () => { isMounted = false };
//     }, []);

//     // --- Derived Data ---
//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     // --- Event Handlers ---
//     const handlePluginClick = (pluginKey: string) => {
//         const config = pluginDisplayConfig[pluginKey];
//         const route = config?.route || `/integrations/${pluginKey}`;
//         console.log(`Navigating to: ${route}`);
//         navigate(route);
//     };

//     // --- Render Logic ---
//     return (
//         <Box sx={{ display: "Flex" }}>
//             <MainDrawer index={"integrations"} />
//             <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                 {/* --- Header --- */}
//                 <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                     <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
//                         {/* Apply Gilroy-Bold font */}
//                         <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
//                         {!loading && !error && (
//                             <Button
//                                 sx={{
//                                     backgroundColor: "black", color: "white",
//                                     // Apply Gilroy-Bold font
//                                     fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "4px", // Slight adjustment to border radius maybe
//                                     textTransform: "none", px: 3, py: 1, // Adjusted padding
//                                     '&:hover': { backgroundColor: "dimgray" },
//                                     '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
//                                 }}
//                                 variant="contained"
//                                 size="small"
//                                 onClick={() => navigate("/integrations/explorePlugins")}>
//                                 Add New Plugin
//                             </Button>
//                         )}
//                     </Box>
//                     <Divider sx={{ marginY: 1 }} />
//                      {/* TODO: Add Search Bar Here if needed, using Gilroy-Regular */}
//                      {/* Example: <TextField fullWidth placeholder="Search by plugin name..." sx={{ fontFamily: 'Gilroy-Regular', ... }} /> */}
//                 </Box>

//                 {/* --- Installed Plugins Sub-Header --- */}
//                 <Box m={1} sx={{ pl: "25px", pt: 2, pb: 1 }}>
//                      {/* Apply Gilroy fonts */}
//                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold"> {/* Or Gilroy-Bold */}
//                         Installed Plugins ({loading ? '...' : installedCount})
//                     </Typography>
//                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="text.secondary">
//                         List of all the installed plugins
//                     </Typography>
//                 </Box>

//                 {/* --- Content Area --- */}
//                 <Box>
//                     {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
//                     {error && !loading && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations list. Please try again later.</Alert> </Box> )}

//                     {/* --- CONDITION 1: Plugins Installed --- */}
//                     {!loading && !error && installedCount > 0 && (
//                         <Box sx={{ p: 3, pt: 1 }}>
//                             <List sx={{ bgcolor: 'background.paper', borderRadius: '8px', p: 0 }}> {/* Use theme radius */}
//                                 {installedPlugins.map((plugin, index) => {
//                                     const config = pluginDisplayConfig[plugin.key] || {};
//                                     const displayName = config.displayName || formatKeyForDisplay(plugin.key);
//                                     // Use default icon only if config or config.icon is missing
//                                     const displayIcon = config.icon || <CheckCircleOutlineIcon color="disabled" />;
//                                     const category = config.category || '';
//                                     const description = config.description || `Details for ${displayName}.`;
//                                     const installedDate = plugin.details?.updatedAt ? new Date(plugin.details.updatedAt).toLocaleDateString() : 'N/A';

//                                     return (
//                                         <React.Fragment key={plugin.key}>
//                                             <ListItem
//                                                 disablePadding
//                                                 secondaryAction={
//                                                     <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', pr: 2, pt: 1.5, height: '100%' }}>
//                                                          {/* Apply Gilroy-Regular font */}
//                                                         <Typography variant="caption" color="text.secondary" fontFamily="Gilroy-Regular">
//                                                            Installed On : {installedDate}
//                                                         </Typography>
//                                                     </Box>
//                                                 }
//                                             >
//                                                 <ListItemButton onClick={() => handlePluginClick(plugin.key)} sx={{ pt: 1.5, pb: 1.5 }}>
//                                                     <ListItemIcon sx={{ mr: 2, minWidth: 40, alignSelf: 'center' }}> {/* Centered icon vertically */}
//                                                         {displayIcon}
//                                                     </ListItemIcon>
//                                                     <ListItemText
//                                                         primary={
//                                                             <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
//                                                                  {/* Apply Gilroy-Bold font */}
//                                                                 <Typography component="span" variant="body1" sx={{ fontFamily: 'Gilroy-SemiBold' }}>
//                                                                     {displayName}
//                                                                 </Typography>
//                                                                 {category && (
//                                                                     <Chip
//                                                                         label={category}
//                                                                         size="small"
//                                                                         sx={{
//                                                                             ml: 0.5, // Spacing
//                                                                             fontSize: '0.65rem', // Smaller font
//                                                                             height: '18px',
//                                                                             lineHeight: '1',
//                                                                             borderRadius: '4px', // Rectangular look
//                                                                             // Apply Gilroy-Regular font
//                                                                             fontFamily: 'Gilroy-Regular',
//                                                                             // fontWeight: 'bold', // Make category text boldish
//                                                                             bgcolor: '#f5f5f5', // Light grey background
//                                                                             color: '#424242', // Darker grey text
//                                                                             '& .MuiChip-label': { px: '8px' }, // Adjust internal padding
//                                                                             letterSpacing: "0.5vh"
//                                                                         }}
//                                                                     />
//                                                                 )}
//                                                             </Box>
//                                                         }
//                                                         secondary={
//                                                             <Box component="div">
//                                                                 {/* Apply Gilroy-Regular font */}
//                                                                 <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontFamily: 'Gilroy-Regular' }}>
//                                                                     {description}
//                                                                 </Typography>
//                                                                 {/* Placeholder for Checkov/Kyverno specific inline details */}
//                                                                 {(plugin.key === 'checkov' || plugin.key === 'kyverno') && (
//                                                                     <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
//                                                                          {/* Apply Gilroy-Regular font to Chips */}
//                                                                         <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: '1rem' }}/>} label="Passed Checks: [N/A]" color="success" size="small" variant="outlined" sx={{fontFamily: 'Gilroy-Regular'}} />
//                                                                         <Chip label="Failed Checks: [N/A]" color="error" size="small" variant="outlined" sx={{fontFamily: 'Gilroy-Regular'}}/>
//                                                                     </Box>
//                                                                 )}
//                                                             </Box>
//                                                         }
//                                                     />
//                                                 </ListItemButton>
//                                             </ListItem>
//                                             {index < installedPlugins.length - 1 && <Divider component="li" variant="inset"/>}
//                                         </React.Fragment>
//                                     );
//                                 })}
//                             </List>
//                         </Box>
//                     )}

//                     {/* --- CONDITION 2: No Plugins Installed --- */}
//                     {!loading && !error && installedCount === 0 && (
//                          <Box sx={{ p: 10 }}>
//                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
//                                <img src="/assets/Intergration Page.svg" alt="No plugins installed" style={{ width: "400px", maxWidth:'80%', marginBottom: '16px' }}/>
//                                 {/* Apply Gilroy fonts */}
//                                 <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">No plugins are added yet</Typography>
//                                 <Typography marginTop="0.5vh" marginBottom="16px" fontSize="16px" fontFamily="Gilroy-Regular">Click below button to explore and add new plugins</Typography>
//                                 <Button
//                                     sx={{
//                                         backgroundColor: "black", color: "white",
//                                         // Apply Gilroy-Bold font
//                                         fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "4px",
//                                         textTransform: "none", px: 3, py: 1,
//                                         '&:hover': { backgroundColor: "dimgray" },
//                                     }}
//                                     variant="contained"
//                                     size="small"
//                                     onClick={() => navigate("/integrations/explorePlugins")} >
//                                         Add New Plugin
//                                 </Button>
//                             </Box>
//                         </Box>
//                     )}
//                 </Box>
//             </Container>
//         </Box>
//     );
// }
// export default Integrations;
// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress,
//     Alert,
//     List,
//     ListItem,
//     ListItemButton, // Use ListItemButton for better click effect
//     ListItemText,
//     ListItemIcon,
//     Chip,
//     // Using a generic icon for now - replace if needed
//     SvgIcon,
// } from "@mui/material";
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// // Example icons (replace with your actual icons)
// import { SiCheckio, SiPrometheus, SiKubernetes } from 'react-icons/si'; // Example: react-icons
// import { FaGoogle } from 'react-icons/fa'; // Example: react-icons

// import queryGraphql from "../../helpers/ApiInterface"; // Make sure path is correct
// import MainDrawer from "../MainDrawer"; // Make sure path is correct

// // --- Plugin Configuration ---
// // Added category and description placeholders.
// // Add actual icons here.
// const pluginDisplayConfig: {
//     [key: string]: {
//         displayName: string;
//         icon?: React.ReactElement;
//         category?: string; // e.g., POLICIES, GITHUB PROVIDER, CICD
//         description?: string; // Short description
//         route?: string; // Optional: specific route if different from /integrations/[key]
//     }
//  } = {
//     checkov: {
//         displayName: "Checkov",
//         icon: <SiCheckio size={24} color="#6D40CF"/>, // Example Icon
//         category: "POLICIES",
//         description: "Automated policy checks for infrastructure as code.",
//         route: "/integrations/checkov-details", // Example specific route
//     },
//     costAgent: {
//         displayName: "GCP Cost Agent",
//         icon: <FaGoogle size={24} color="#4285F4"/>, // Example Icon
//         category: "Cost Agent",
//         description: "Tracks and reports Google Cloud Platform costs.",
//         route: "/integrations/cost-agent", // Example specific route
//     },
//     kyverno: {
//         displayName: "Kyverno",
//         icon: <SiKubernetes size={24} color="#326CE5"/>, // Example Icon
//         category: "POLICIES",
//         description: "Kubernetes native policy management.",
//         route: "/integrations/kyverno-details", // Example specific route
//     },
//     prometheus: {
//         displayName: "Prometheus",
//         icon: <SiPrometheus size={24} color="#E6522C"/>, // Example Icon
//         category: "MONITORING",
//         description: "Open-source systems monitoring and alerting toolkit.",
//         // Example: falls back to default route pattern if 'route' is omitted
//     },
//     // Add other potential plugins like gitlab, github, jenkins if they come from the API
//     gitlab: {
//         displayName: "Gitlab",
//         category: "GIT PROVIDER",
//         description: "Code hosting, CI/CD, and project management.",
//     },
//      github: {
//         displayName: "GitHub",
//         category: "GIT PROVIDER",
//         description: "Code hosting, version control, and collaboration.",
//     },
//      jenkins: {
//         displayName: "Jenkins",
//         category: "CICD",
//         description: "Open source automation server.",
//     },
// };

// // --- Helper Functions ---

// /** Discovers enabled plugins from fetched data */
// const getInstalledPlugins = (data: any): { key: string; details: any }[] => {
//     if (!data || typeof data !== 'object') return [];
//     const plugins: { key: string; details: any }[] = [];
//     const excludedKeys = ['id', '__typename'];
//     for (const key in data) {
//         if (!excludedKeys.includes(key) && typeof data[key] === 'object' && data[key] !== null && data[key].enabled === true) {
//            plugins.push({ key: key, details: data[key] });
//         }
//     }
//     // Example: Manually add plugins if they are enabled but not structured the same way
//     // if (data?.gitlab?.enabled) plugins.push({ key: 'gitlab', details: data.gitlab });
//     return plugins;
// };

// /** Formats key for display */
// const formatKeyForDisplay = (key: string): string => {
//     if (!key) return '';
//     const result = key.replace(/([A-Z])/g, ' $1');
//     return result.charAt(0).toUpperCase() + result.slice(1);
// };


// // ========================================================================
// // --- Main Integrations Component ---
// // ========================================================================
// const Integrations: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State ---
//     const [integrations, setIntegrations] = useState<any>(null); // Holds fetched integration data
//     const [loading, setLoading] = useState<boolean>(true);      // Loading state for fetch
//     const [error, setError] = useState<any>(null);              // Error state for fetch

//     // --- Fetch Data ---
//     useEffect(() => {
//         let isMounted = true; // Prevent state updates if unmounted
//         (async () => {
//             try {
//                 setLoading(true);
//                 setError(null);
//                 const data = await queryGraphql(`
//                     query Query {
//                       getIntegrations {
//                         # Request necessary fields for enabled status and basic info
//                         checkov { enabled updatedAt repoUrl } # Added repoUrl
//                         costAgent { enabled updatedAt projectId datasetId tableId serviceAccountName } # Added specific fields
//                         kyverno { enabled updatedAt repoUrl } # Added repoUrl
//                         prometheus { enabled updatedAt repoUrl } # Added repoUrl
//                         id
//                         # Add other potential top-level integrations if needed
//                       }
//                     }
//                     # --- IMPORTANT ---
//                     # You will likely need a SEPARATE query or modify this one
//                     # significantly if you want data like Checkov/Kyverno
//                     # passed/failed check counts *within this list view*.
//                     # The current structure assumes this data is on the detail page.
//                 `);
//                 if (isMounted) {
//                     if (data.getIntegrations) {
//                         setIntegrations(data.getIntegrations);
//                     } else {
//                         console.log("No integration data returned.");
//                         setIntegrations(null);
//                     }
//                 }
//             } catch (err) {
//                 console.error("Error fetching integrations:", err);
//                  if (isMounted) setError(err);
//                  if (isMounted) setIntegrations(null);
//             } finally {
//                  if (isMounted) setLoading(false);
//             }
//         })();
//         return () => { isMounted = false }; // Cleanup
//     }, []); // Run once on mount

//     // --- Derived Data ---
//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     // --- Event Handlers ---
//     const handlePluginClick = (pluginKey: string) => {
//         // Determine the navigation route
//         const config = pluginDisplayConfig[pluginKey];
//         const route = config?.route || `/integrations/${pluginKey}`; // Use specific route or default pattern
//         console.log(`Navigating to: ${route}`);
//         navigate(route);
//     };

//     // --- Render Logic ---
//     return (
//         <Box sx={{ display: "Flex" }}>
//             <MainDrawer index={"integrations"} />
//             <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                 {/* --- Header --- */}
//                 <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                     <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
//                         <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
//                         {!loading && !error && ( /* Show Add button even if count is 0 */
//                             <Button sx={{ /* styles... */ }} variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")}>
//                                 Add New Plugin
//                             </Button>
//                         )}
//                     </Box>
//                     <Divider sx={{ marginY: 1 }} />
//                 </Box>

//                 {/* --- Installed Plugins Sub-Header --- */}
//                 <Box m={1} sx={{ pl: "25px", pt: 2, pb: 1 }}>
//                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                         Installed Plugins ({loading ? '...' : installedCount})
//                     </Typography>
//                     <Typography fontSize="14px" fontFamily="Gilroy-Regular">
//                         List of all the installed plugins
//                     </Typography>
//                     {/* TODO: Add Search Bar Here if needed */}
//                 </Box>

//                 {/* --- Content Area --- */}
//                 <Box>
//                     {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
//                     {error && !loading && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations list. Please try again later.</Alert> </Box> )}

//                     {/* --- CONDITION 1: Plugins Installed --- */}
//                     {!loading && !error && installedCount > 0 && (
//                         <Box sx={{ p: 3, pt: 1 }}>
//                             <List sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 0 }}>
//                                 {installedPlugins.map((plugin, index) => {
//                                     const config = pluginDisplayConfig[plugin.key] || {};
//                                     const displayName = config.displayName || formatKeyForDisplay(plugin.key);
//                                     const displayIcon = config.icon || <CheckCircleOutlineIcon color="success" />; // Default icon
//                                     const category = config.category || '';
//                                     const description = config.description || `Details for ${displayName}.`; // Placeholder desc
//                                     const installedDate = plugin.details?.updatedAt ? new Date(plugin.details.updatedAt).toLocaleDateString() : 'N/A';

//                                     return (
//                                         <React.Fragment key={plugin.key}>
//                                             <ListItem
//                                                 disablePadding // Remove padding as ListItemButton has it
//                                                 secondaryAction={ // Content aligned to the far right
//                                                     <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', pr: 2, pt: 1.5, height: '100%' }}>
//                                                         <Typography variant="caption" color="text.secondary">
//                                                            Installed On : {installedDate}
//                                                         </Typography>
//                                                         {/* Add delete/other icons here if needed */}
//                                                     </Box>
//                                                 }
//                                             >
//                                                 {/* Use ListItemButton for click effect and navigation */}
//                                                 <ListItemButton onClick={() => handlePluginClick(plugin.key)} sx={{ pt: 1.5, pb: 1.5 }}>
//                                                     <ListItemIcon sx={{ mr: 1, minWidth: 40, alignSelf: 'flex-start', mt: 0.5 }}>
//                                                         {displayIcon}
//                                                     </ListItemIcon>
//                                                     <ListItemText
//                                                         primary={ // Combine Name and Category
//                                                             <Typography component="div" variant="body1" sx={{ fontWeight: 'bold' }}>
//                                                                 {displayName}
//                                                                 {category && <Chip label={category} size="small" sx={{ ml: 1, fontSize: '0.7rem', height: '18px' }} />}
//                                                             </Typography>
//                                                         }
//                                                         secondary={ // Combine Description and Specific Details
//                                                             <Box component="div" sx={{ mt: 0.5 }}>
//                                                                 <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
//                                                                     {description}
//                                                                 </Typography>
//                                                                 {/* --- Placeholder for Checkov/Kyverno specific inline details --- */}
//                                                                 {(plugin.key === 'checkov' || plugin.key === 'kyverno') && (
//                                                                     <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
//                                                                         <Chip icon={<CheckCircleOutlineIcon fontSize="small"/>} label="Passed Checks: [N/A]" color="success" size="small" variant="outlined" />
//                                                                         {/* Add chip for failed checks if needed */}
//                                                                         <Chip label="Failed Checks: [N/A]" color="error" size="small" variant="outlined" />
//                                                                     </Box>
//                                                                 )}
//                                                                 {/* --- Add other inline details for other plugins if needed --- */}
//                                                                 {/* Example for cost agent projectId */}
//                                                                 {/* {plugin.key === 'costAgent' && plugin.details?.projectId && (
//                                                                     <Typography variant="caption" display="block">Project: {plugin.details.projectId}</Typography>
//                                                                 )} */}
//                                                             </Box>
//                                                         }
//                                                     />
//                                                 </ListItemButton>
//                                             </ListItem>
//                                             {/* Divider */}
//                                             {index < installedPlugins.length - 1 && <Divider component="li" variant="inset"/>}
//                                         </React.Fragment>
//                                     );
//                                 })}
//                             </List>
//                         </Box>
//                     )}

//                     {/* --- CONDITION 2: No Plugins Installed --- */}
//                     {!loading && !error && installedCount === 0 && (
//                          <Box sx={{ p: 10 }}>
//                              {/* ... (empty state content remains the same) ... */}
//                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
//                                <img src="/assets/Intergration Page.svg" alt="No plugins installed" style={{ width: "400px", maxWidth:'80%', marginBottom: '16px' }}/>
//                                 <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">No plugins are added yet</Typography>
//                                 <Typography marginTop="0.5vh" marginBottom="16px" fontSize="16px" fontFamily="Gilroy-Regular">Click below button to explore and add new plugins</Typography>
//                                 <Button sx={{ /* styles... */ }} variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")} > Add New Plugin </Button>
//                             </Box>
//                         </Box>
//                     )}
//                 </Box>
//             </Container>
//         </Box>
//     );
// }
// export default Integrations;

// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress,
//     Alert,
//     List,
//     ListItem,
//     ListItemText,
//     ListItemIcon,
//     Chip,
//     IconButton, // Added for expand/collapse
//     Collapse, // Added for dropdown effect
//     // Using a generic icon for now - replace if needed
//     SvgIcon,
// } from "@mui/material";
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // Added
// import ExpandLessIcon from '@mui/icons-material/ExpandLess'; // Added
// import LaunchIcon from '@mui/icons-material/Launch'; // Example icon for buttons

// import queryGraphql from "../../helpers/ApiInterface"; // Make sure path is correct
// import MainDrawer from "../MainDrawer"; // Make sure path is correct

// // --- Plugin Configuration (Optional: For enhancing display of known plugins) ---
// const pluginDisplayConfig: { [key: string]: { displayName: string; icon?: React.ReactElement } } = {
//     checkov: { displayName: "Checkov" /*, icon: <Icon/> */ },
//     costAgent: { displayName: "GCP Cost Agent" /*, icon: <Icon/> */ },
//     kyverno: { displayName: "Kyverno" /*, icon: <Icon/> */ },
//     prometheus: { displayName: "Prometheus" /*, icon: <Icon/> */ },
// };

// // --- More Agnostic Helper Function (using 'any' for data) ---
// const getInstalledPlugins = (data: any): { key: string; details: any }[] => {
//     // ... (Keep the implementation from the previous version)
//     if (!data || typeof data !== 'object') return [];
//     const plugins: { key: string; details: any }[] = [];
//     const excludedKeys = ['id', '__typename'];
//     for (const key in data) {
//         if (!excludedKeys.includes(key) && typeof data[key] === 'object' && data[key] !== null && data[key].enabled === true) {
//            plugins.push({ key: key, details: data[key] });
//         }
//     }
//     return plugins;
// };

// // Helper to format key names for display
// const formatKeyForDisplay = (key: string): string => {
//     // ... (Keep the implementation from the previous version)
//     const result = key.replace(/([A-Z])/g, ' $1');
//     return result.charAt(0).toUpperCase() + result.slice(1);
// };

// // ========================================================================
// // --- Detail Rendering Functions for Each Plugin Type ---
// // ========================================================================

// // --- Cost Agent Details Renderer ---
// const RenderCostAgentDetails = ({ details, navigate }: { details: any, navigate: Function }) => {
//     // Basic check if details exist
//     if (typeof details !== 'object' || details === null) return null;

//     const commonDetails = details.updatedAt ? (
//         <Typography variant="caption" display="block" gutterBottom>
//             Last Updated: {new Date(details.updatedAt).toLocaleString()}
//         </Typography>
//     ) : null;

//     return (
//         <Box sx={{ pl: 7, // Indent details slightly
//         pt: 1, pb: 1, borderTop: '1px dashed #eee'}}>
//             {details.projectId && <Typography variant="body2" gutterBottom>Project ID: {details.projectId}</Typography>}
//             {details.datasetId && <Typography variant="body2" gutterBottom>Dataset ID: {details.datasetId}</Typography>}
//             {details.tableId && <Typography variant="body2" gutterBottom>Table ID: {details.tableId}</Typography>}
//             {details.serviceAccountName && <Typography variant="body2" gutterBottom>Service Account: {details.serviceAccountName}</Typography>}
//             {commonDetails}
//             <Button
//                 variant="outlined"
//                 size="small"
//                 startIcon={<LaunchIcon />}
//                 sx={{ mt: 1, textTransform: 'none' }}
//                 // TODO: Update the navigation path as needed
//                 onClick={() => navigate(`/integrations/cost-agent`)} // Example navigation
//             >
//                 Access Cost Agent
//             </Button>
//         </Box>
//     );
// };

// // --- Checkov/Kyverno Details Renderer ---
// const RenderCheckovKyvernoDetails = ({ details, pluginKey }: { details: any, pluginKey: string }) => {
//      // Basic check if details exist
//      if (typeof details !== 'object' || details === null) return null;

//      const commonDetails = details.updatedAt ? (
//         <Typography variant="caption" display="block" gutterBottom>
//             Last Updated: {new Date(details.updatedAt).toLocaleString()}
//         </Typography>
//     ) : null;

//     // ** IMPORTANT: The source for "Policies Passed Check" is NOT in the current GraphQL query.
//     //    This is placeholder text. You need to fetch this data.
//     const policyCheckStatus = "Policies Passed Check: [Data not available]"; // Placeholder

//     return (
//         <Box sx={{ pl: 7, pt: 1, pb: 1, borderTop: '1px dashed #eee' }}>
//              {details.repoUrl && <Typography variant="body2" gutterBottom>Repo URL: {details.repoUrl}</Typography>}
//              <Typography variant="body2" sx={{ fontStyle: 'italic' }} gutterBottom>
//                 {policyCheckStatus}
//              </Typography>
//              {commonDetails}
//              {/* Optional: Add a button if needed */}
//              {/* <Button variant="outlined" size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={() => navigate(`/integrations/${pluginKey}`)}>View Details</Button> */}
//         </Box>
//     );
// };

// // --- Prometheus Details Renderer ---
// const RenderPrometheusDetails = ({ details, navigate }: { details: any, navigate: Function }) => {
//      // Basic check if details exist
//      if (typeof details !== 'object' || details === null) return null;

//      const commonDetails = details.updatedAt ? (
//         <Typography variant="caption" display="block" gutterBottom>
//             Last Updated: {new Date(details.updatedAt).toLocaleString()}
//         </Typography>
//     ) : null;

//     return (
//         <Box sx={{ pl: 7, pt: 1, pb: 1, borderTop: '1px dashed #eee' }}>
//              {details.repoUrl && <Typography variant="body2" gutterBottom>Repo URL: {details.repoUrl}</Typography>}
//              {commonDetails}
//              <Button
//                 variant="outlined"
//                 size="small"
//                 startIcon={<LaunchIcon />}
//                 sx={{ mt: 1, textTransform: 'none' }}
//                  // TODO: Update the navigation path as needed
//                 onClick={() => navigate(`/integrations/prometheus`)} // Example navigation
//             >
//                 View Details
//             </Button>
//         </Box>
//     );
// };

// // --- Fallback/Generic Details Renderer ---
// // Used if a plugin key doesn't match the specific renderers above
// const RenderGenericPluginDetails = ({ details }: { details: any }) => {
//     if (typeof details !== 'object' || details === null) return null;

//     const detailElements: React.ReactNode[] = [];
//     const ignoreKeys = ['enabled', 'updatedAt', '__typename'];

//     for (const detailKey in details) {
//         if (Object.prototype.hasOwnProperty.call(details, detailKey)) {
//             if (!ignoreKeys.includes(detailKey) && details[detailKey] !== null && details[detailKey] !== undefined) {
//                 detailElements.push(
//                     <Typography key={detailKey} variant="body2" gutterBottom>
//                         {formatKeyForDisplay(detailKey)}: {String(details[detailKey])}
//                     </Typography>
//                 );
//             }
//         }
//     }
//      // Add 'updatedAt' if it exists
//      if (details.updatedAt) {
//         detailElements.push(
//              <Typography key="updatedAt" variant="caption" display="block" gutterBottom>
//                  Last Updated: {new Date(details.updatedAt).toLocaleString()}
//              </Typography>
//         );
//     }

//     return (
//          <Box sx={{ pl: 7, pt: 1, pb: 1, borderTop: '1px dashed #eee' }}>
//             {detailElements.length > 0 ? detailElements : <Typography variant="body2" sx={{ fontStyle: 'italic' }}>No specific details available.</Typography>}
//         </Box>
//     );
// }


// // ========================================================================
// // --- Main Integrations Component ---
// // ========================================================================
// const Integrations: React.FC = () => {
//     const navigate = useNavigate();
//     const [integrations, setIntegrations] = useState<any>(null);
//     const [loading, setLoading] = useState<boolean>(true);
//     const [error, setError] = useState<any>(null);
//     // State to track which plugin detail is expanded (key or null)
//     const [expandedPluginKey, setExpandedPluginKey] = useState<string | null>(null);

//     // --- Fetch Data (Keep as before) ---
//     useEffect(() => {
//         (async () => {
//             try {
//                 setLoading(true);
//                 setError(null);
//                 const data = await queryGraphql(`
//                     query Query {
//                       getIntegrations {
//                         checkov { enabled repoUrl updatedAt }
//                         costAgent { enabled datasetId projectId serviceAccountName tableId updatedAt }
//                         kyverno { enabled repoUrl updatedAt }
//                         prometheus { enabled repoUrl updatedAt }
//                         id
//                         # IMPORTANT: Add query fields here if Checkov/Kyverno need policy check results
//                       }
//                     }
//                 `);
//                 if (data.getIntegrations) {
//                     setIntegrations(data.getIntegrations);
//                 } else {
//                     console.log("No integration data returned.");
//                     setIntegrations(null);
//                 }
//             } catch (err) {
//                 console.error("Error fetching integrations:", err);
//                 setError(err);
//                 setIntegrations(null);
//             } finally {
//                 setLoading(false);
//             }
//         })();
//     }, []);

//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     // Button Styles
//     // Toggle expansion for a plugin
//     const handleToggleExpand = (pluginKey: string) => {
//         setExpandedPluginKey(prevKey => (prevKey === pluginKey ? null : pluginKey));
//     };

//      // --- Function to select the correct detail renderer ---
//      const renderPluginDetailsContent = (plugin: { key: string; details: any }) => {
//         switch (plugin.key) {
//             case 'costAgent':
//                 return <RenderCostAgentDetails details={plugin.details} navigate={navigate} />;
//             case 'checkov':
//             case 'kyverno':
//                 return <RenderCheckovKyvernoDetails details={plugin.details} pluginKey={plugin.key}/>;
//             case 'prometheus':
//                 return <RenderPrometheusDetails details={plugin.details} navigate={navigate} />;
//             default:
//                 // Fallback for unknown plugins found by getInstalledPlugins
//                 return <RenderGenericPluginDetails details={plugin.details} />;
//         }
//     };

//     return (
//         <Box sx={{ display: "Flex" }}>
//             <MainDrawer index={"integrations"} />
//             <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                 {/* --- Header --- */}
//                 <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                    {/* ... (Header content remains the same) ... */}
//                    <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
//                         <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
//                         {!loading && !error && installedCount > 0 && ( 
//                             <Button sx={{backgroundColor: "black", color: "white",
//                                         fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "0.5vh",
//                                         textTransform: "none", px: 5, py:1, position: 'relative',
//                                        transition: 'background-color 0.3s ease',
//                                        '&:hover': { backgroundColor: "dimgray" },
//                                        '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' },
//                                         }} 
//                                        variant="contained" size="small" 
//                                        onClick={() => navigate("/integrations/explorePlugins")}> 
                                       
                                       
//                                        Add New Plugin 
//                                        </Button> )}
//                     </Box>
//                     <Divider sx={{ marginY: 1 }} />
//                 </Box>

//                 {/* --- Installed Plugins Sub-Header --- */}
//                 <Box m={1} sx={{ pl: "25px", pt: 2 }}>
//                    {/* ... (Sub-header content remains the same) ... */}
//                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold"> Installed Plugins ({loading ? '...' : installedCount}) </Typography>
//                     <Typography fontSize="14px" fontFamily="Gilroy-Regular"> List of all the installed plugins </Typography>
//                 </Box>

//                 {/* --- Content Area --- */}
//                 <Box>
//                     {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
//                     {error && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations. Please try again later.</Alert> </Box> )}

//                     {/* --- CONDITION 1: Plugins Installed --- */}
//                     {!loading && !error && installedCount > 0 && (
//                         <Box sx={{ p: 3 }}>
//                             {/* Use List component for proper structure */}
//                             <List sx={{ bgcolor: 'background.paper', borderRadius: 2, p:0 /* Remove padding */ }}>
//                                 {installedPlugins.map((plugin, index) => {
//                                     const isExpanded = expandedPluginKey === plugin.key;
//                                     const displayName = pluginDisplayConfig[plugin.key]?.displayName || formatKeyForDisplay(plugin.key);
//                                     const displayIcon = pluginDisplayConfig[plugin.key]?.icon || <CheckCircleOutlineIcon color="success" />;

//                                     return (
//                                         // Use React.Fragment to group ListItem and Collapse
//                                         <React.Fragment key={plugin.key}>
//                                             <ListItem
//                                                 // divider={index < installedPlugins.length - 1} // Add divider only if not last item and not expanded? maybe remove divider prop from here
//                                                 alignItems="flex-start"
//                                                 // secondaryAction contains elements aligned to the right
//                                                 secondaryAction={
//                                                     <Box sx={{display: 'flex', alignItems: 'center'}}>
//                                                          <Chip label="Enabled" color="success" size="small" sx={{ mr: 1 }} />
//                                                         <IconButton
//                                                             edge="end"
//                                                             aria-label={isExpanded ? "collapse" : "expand"}
//                                                             onClick={() => handleToggleExpand(plugin.key)}
//                                                         >
//                                                             {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//                                                         </IconButton>
//                                                     </Box>
//                                                 }
//                                                 sx={{ pt: 1.5, pb: 1.5 }} // Adjust padding
//                                             >
//                                                 <ListItemIcon sx={{mt: 0.5}}> {displayIcon} </ListItemIcon>
//                                                 <ListItemText
//                                                     primary={displayName}
//                                                     primaryTypographyProps={{fontWeight: 'bold'}}
//                                                     // No secondary text here anymore
//                                                 />

//                                             </ListItem>
//                                             {/* Collapse section for details */}
//                                             <Collapse in={isExpanded} timeout="auto" unmountOnExit>
//                                                 {/* Render specific details based on plugin type */}
//                                                 {renderPluginDetailsContent(plugin)}
//                                             </Collapse>
//                                             {/* Add divider after Collapse if not the last item */}
//                                             {index < installedPlugins.length - 1 && <Divider component="li" />}
//                                         </React.Fragment>
//                                     );
//                                 })}
//                             </List>
//                         </Box>
//                     )}

//                     {/* --- CONDITION 2: No Plugins Installed --- */}
//                     {!loading && !error && installedCount === 0 && (
//                         <Box sx={{ p: 10 }}>
//                            {/* ... (empty state content remains the same) ... */}
//                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
//                                <img src="/assets/Intergration Page.svg" alt="Active Icon" style={{ width: "400px", marginRight: "8px" }}/>
//                                 <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">No plugins are added yet</Typography>
//                                 <Typography marginTop="0.5vh" marginBottom="8px" fontSize="16px" fontFamily="Gilroy-Regular">Click below button to explore and add new plugins</Typography>
//                                 <Button sx={{backgroundColor: "black", color: "white",
//                                         fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "0.5vh",
//                                         textTransform: "none", px: 5, py:1, position: 'relative',
//                                        transition: 'background-color 0.3s ease',
//                                        '&:hover': { backgroundColor: "dimgray" },
//                                        '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' },
//                                         }} 
//                                 variant="contained" size="small" type="submit" 
//                                 onClick={() => navigate("/integrations/explorePlugins")} > 
//                                 Add New Plugin 
//                                 </Button>
//                             </Box>
//                         </Box>
//                     )}
//                 </Box>
//             </Container>
//         </Box>
//     );
// }
// export default Integrations;
// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress,
//     Alert,
//     List,
//     ListItem,
//     ListItemText,
//     ListItemIcon,
//     Chip,
//     SvgIcon, // Using a generic icon for now
// } from "@mui/material";
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import queryGraphql from "../../helpers/ApiInterface"; // Make sure path is correct
// import MainDrawer from "../MainDrawer"; // Make sure path is correct

// // --- Plugin Configuration (Optional: For enhancing display of known plugins) ---
// // This provides user-friendly names or icons for keys we know about.
// // Remains useful even without interfaces for the data itself.
// const pluginDisplayConfig: { [key: string]: { displayName: string; icon?: React.ReactElement } } = {
//     checkov: { displayName: "Checkov" /*, icon: <Icon/> */ },
//     costAgent: { displayName: "GCP Cost Agent" /*, icon: <Icon/> */ },
//     kyverno: { displayName: "Kyverno" /*, icon: <Icon/> */ },
//     prometheus: { displayName: "Prometheus" /*, icon: <Icon/> */ },
//     // Add more known plugins here if you want specific display names/icons
// };

// // --- More Agnostic Helper Function (using 'any' for data) ---
// // Discovers enabled plugins by iterating over keys in the fetched data
// // Parameter 'data' is now 'any'
// // Return type's 'details' is now 'any'
// const getInstalledPlugins = (data: any): { key: string; details: any }[] => {
//     if (!data || typeof data !== 'object') return []; // Added null/type check

//     const plugins: { key: string; details: any }[] = [];
//     const excludedKeys = ['id', '__typename']; // Keys to ignore when looking for plugins

//     for (const key in data) {
//         // Check if the key is not excluded and the value is an object with 'enabled: true'
//         // Runtime checks are now more crucial without interfaces
//         if (!excludedKeys.includes(key) && typeof data[key] === 'object' && data[key] !== null && data[key].enabled === true) {
//            plugins.push({ key: key, details: data[key] }); // No 'as IntegrationDetail' needed
//         }
//     }
//     return plugins;
// };

// // Helper to format key names for display (e.g., "serviceAccountName" -> "Service Account Name")
// const formatKeyForDisplay = (key: string): string => {
//     const result = key.replace(/([A-Z])/g, ' $1');
//     return result.charAt(0).toUpperCase() + result.slice(1);
// };


// const Integrations: React.FC = () => {
//     const navigate = useNavigate();
//     // Use 'any' for state type instead of 'IntegrationData | null'
//     const [integrations, setIntegrations] = useState<any>(null);
//     const [loading, setLoading] = useState<boolean>(true);
//     const [error, setError] = useState<any>(null);

//     // --- Fetch Data ---
//     useEffect(() => {
//         (async () => {
//             try {
//                 setLoading(true);
//                 setError(null);
//                 // GraphQL query remains the same
//                 const data = await queryGraphql(`
//                     query Query {
//                       getIntegrations {
//                         checkov { enabled repoUrl updatedAt }
//                         costAgent { enabled datasetId projectId serviceAccountName tableId updatedAt }
//                         kyverno { enabled repoUrl updatedAt }
//                         prometheus { enabled repoUrl updatedAt }
//                         id
//                       }
//                     }
//                 `);

//                 // Directly set the state, assuming queryGraphql returns the object or null/undefined
//                 if (data.getIntegrations) {
//                     setIntegrations(data.getIntegrations); // No 'as IntegrationData' needed
//                 } else {
//                     console.log("No integration data returned.");
//                     setIntegrations(null);
//                 }
//             } catch (err) {
//                 console.error("Error fetching integrations:", err);
//                 setError(err);
//                 setIntegrations(null);
//             } finally {
//                 setLoading(false);
//             }
//         })();
//     }, []);

//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     // Button Styles
//     const addPluginButtonStyles = {
//         backgroundColor: "black", color: "white",
//         fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "0.5vh",
//         textTransform: "none", px: 5, py: 1, position: 'relative',
//         transition: 'background-color 0.3s ease',
//         '&:hover': { backgroundColor: "dimgray" },
//         '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' },
//     };

//     // --- More Generic Function to Render Plugin Details (using 'any' for details) ---
//     // Iterates over details and displays them, skipping common/internal ones
//     // Parameter 'plugin.details' is now 'any'
//     const renderPluginSpecificDetails = (plugin: { key: string; details: any }) => {
//         const details = plugin.details;
//         // Add extra check in case details is not an object somehow
//         if (typeof details !== 'object' || details === null) return null;

//         const detailElements: React.ReactNode[] = [];
//         const ignoreKeys = ['enabled', 'updatedAt', '__typename']; // Keys to explicitly ignore

//         for (const detailKey in details) {
//              // Ensure the key is directly on the object and not inherited (good practice)
//             if (Object.prototype.hasOwnProperty.call(details, detailKey)) {
//                 if (!ignoreKeys.includes(detailKey) && details[detailKey] !== null && details[detailKey] !== undefined) {
//                     const formattedLabel = formatKeyForDisplay(detailKey);
//                     const value = details[detailKey];
//                     detailElements.push(
//                         <Typography key={detailKey} variant="caption" display="block" gutterBottom sx={{ml:0}}>
//                             {formattedLabel}: {String(value)}
//                         </Typography>
//                     );
//                 }
//             }
//         }

//         // Check and add 'updatedAt' (property access on 'any' type)
//         if (details.updatedAt) {
//             detailElements.push(
//                  <Typography key="updatedAt" variant="caption" display="block" gutterBottom sx={{ml:0}}>
//                      Last Updated: {new Date(details.updatedAt).toLocaleString()}
//                  </Typography>
//             );
//         }

//         return <Box sx={{mt: 0.5}}>{detailElements}</Box>;
//     };

//     return (
//         <Box sx={{ display: "Flex" }}>
//             <MainDrawer index={"integrations"} />
//             <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                 {/* --- Header --- */}
//                 <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                     <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
//                         <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
//                         {!loading && !error && installedCount > 0 && ( <Button sx={addPluginButtonStyles} variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")}> Add New Plugin </Button> )}
//                     </Box>
//                     <Divider sx={{ marginY: 1 }} />
//                 </Box>

//                 {/* --- Installed Plugins Sub-Header --- */}
//                 <Box m={1} sx={{ pl: "25px", pt: 2 }}>
//                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold"> Installed Plugins ({loading ? '...' : installedCount}) </Typography>
//                     <Typography fontSize="14px" fontFamily="Gilroy-Regular"> List of all the installed plugins </Typography>
//                 </Box>

//                 {/* --- Content Area --- */}
//                 <Box>
//                     {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
//                     {error && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations. Please try again later.</Alert> </Box> )}

//                     {/* --- CONDITION 1: Plugins Installed --- */}
//                     {!loading && !error && installedCount > 0 && (
//                         <Box sx={{ p: 3 }}>
//                             <List>
//                                 {installedPlugins.map((plugin) => {
//                                     // Config lookup still works fine
//                                     const displayName = pluginDisplayConfig[plugin.key]?.displayName || formatKeyForDisplay(plugin.key);
//                                     const displayIcon = pluginDisplayConfig[plugin.key]?.icon || <CheckCircleOutlineIcon color="success" />;

//                                     return (
//                                         <ListItem key={plugin.key} divider alignItems="flex-start">
//                                             <ListItemIcon sx={{mt: 1}}> {displayIcon} </ListItemIcon>
//                                             <ListItemText
//                                                 primary={displayName}
//                                                 // Calls the generic renderer (now accepting 'any')
//                                                 secondary={renderPluginSpecificDetails(plugin)}
//                                                 primaryTypographyProps={{fontWeight: 'bold'}}
//                                             />
//                                             <Chip label="Enabled" color="success" size="small" sx={{ ml: 2, mt: 1 }} />
//                                         </ListItem>
//                                     );
//                                 })}
//                             </List>
//                         </Box>
//                     )}

//                     {/* --- CONDITION 2: No Plugins Installed --- */}
//                     {!loading && !error && installedCount === 0 && (
//                         <Box sx={{ p: 10 }}>
//                             <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
//                                <img src="/assets/Intergration Page.svg" alt="Active Icon" style={{ width: "400px", marginRight: "8px" }}/>
//                                 <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">No plugins are added yet</Typography>
//                                 <Typography marginTop="0.5vh" marginBottom="8px" fontSize="16px" fontFamily="Gilroy-Regular">Click below button to explore and add new plugins</Typography>
//                                 <Button sx={addPluginButtonStyles} variant="contained" size="small" type="submit" onClick={() => navigate("/integrations/explorePlugins")} > Add New Plugin </Button>
//                             </Box>
//                         </Box>
//                     )}
//                 </Box>
//             </Container>
//         </Box>
//     );
// }
// export default Integrations;
// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress,
//     Alert,
//     List,
//     ListItem,
//     ListItemText,
//     ListItemIcon,
//     Chip,
//     SvgIcon, // Using a generic icon for now
// } from "@mui/material";
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import queryGraphql from "../../helpers/ApiInterface"; // Make sure path is correct
// import MainDrawer from "../MainDrawer"; // Make sure path is correct

// // --- Define Interfaces ---
// interface IntegrationDetail {
//     enabled: boolean;
//     repoUrl?: string;
//     updatedAt?: string;
//     datasetId?: string;
//     projectId?: string;
//     serviceAccountName?: string;
//     tableId?: string;
// }

// interface IntegrationData {
//     id: string;
//     checkov?: IntegrationDetail;
//     costAgent?: IntegrationDetail;
//     kyverno?: IntegrationDetail;
//     prometheus?: IntegrationDetail;
// }

// // --- Plugin Configuration ---
// const pluginConfig = {
//     checkov: { displayName: "Checkov" /*, icon: <Icon/> */ },
//     costAgent: { displayName: "Cost Agent" /*, icon: <Icon/> */ },
//     kyverno: { displayName: "Kyverno" /*, icon: <Icon/> */ },
//     prometheus: { displayName: "Prometheus" /*, icon: <Icon/> */ },
// };
// type PluginKey = keyof typeof pluginConfig;

// // --- Updated Helper Function ---
// const getInstalledPlugins = (data: IntegrationData | null): { key: PluginKey; details: IntegrationDetail }[] => {
//     if (!data) return [];
//     const plugins: { key: PluginKey; details: IntegrationDetail }[] = [];
//     if (data.checkov?.enabled) plugins.push({ key: "checkov", details: data.checkov });
//     if (data.costAgent?.enabled) plugins.push({ key: "costAgent", details: data.costAgent });
//     if (data.kyverno?.enabled) plugins.push({ key: "kyverno", details: data.kyverno });
//     if (data.prometheus?.enabled) plugins.push({ key: "prometheus", details: data.prometheus });
//     return plugins;
// };

// const Integrations: React.FC = () => {
//     const navigate = useNavigate();
//     const [integrations, setIntegrations] = useState<IntegrationData | null>(null);
//     const [loading, setLoading] = useState<boolean>(true);
//     const [error, setError] = useState<any>(null);

//     // --- Fetch Data ---
//     useEffect(() => {
//         (async () => {
//             try {
//                 setLoading(true);
//                 setError(null);
//                 const data = await queryGraphql(`
//                     query Query {
//                       getIntegrations {
//                         checkov { enabled repoUrl updatedAt }
//                         costAgent { enabled datasetId projectId serviceAccountName tableId updatedAt }
//                         kyverno { enabled repoUrl updatedAt }
//                         prometheus { enabled repoUrl updatedAt }
//                         id
//                       }
//                     }
//                 `);
//                 if (data.getIntegrations) {
//                     setIntegrations(data.getIntegrations);
//                 } else {
//                     console.log("No integration data returned.");
//                     setIntegrations(null);
//                 }
//             } catch (err) {
//                 console.error("Error fetching integrations:", err);
//                 setError(err);
//                 setIntegrations(null);
//             } finally {
//                 setLoading(false);
//             }
//         })();
//     }, []);

//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     // Button Styles
//     const addPluginButtonStyles = {
//         backgroundColor: "black", color: "white",
//         fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "0.5vh",
//         textTransform: "none", px: 5, py: 1, position: 'relative',
//         transition: 'background-color 0.3s ease',
//         '&:hover': { backgroundColor: "dimgray" },
//         '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' },
//     };

//     // --- Function to Render Specific Plugin Details ---
//     const renderPluginSpecificDetails = (plugin: { key: PluginKey; details: IntegrationDetail }) => {
//         const details = plugin.details; // Contains the actual fetched data
//         const commonDetails = ( /* Renders updatedAt if present */
//             <> {details.updatedAt && (<Typography variant="caption" display="block" gutterBottom>Last Updated: {new Date(details.updatedAt).toLocaleString()}</Typography>)}</>
//         );

//         switch (plugin.key) {
//             case 'checkov': case 'kyverno': case 'prometheus':
//                 return ( /* Displays repoUrl from details if present */
//                     <> {details.repoUrl && <Typography variant="caption" display="block" gutterBottom>Repo URL: {details.repoUrl}</Typography>} {commonDetails} </>
//                 );
//             case 'costAgent':
//                 return ( /* Displays costAgent specific fields from details if present */
//                      <>
//                         {details.projectId && <Typography variant="caption" display="block" gutterBottom>Project ID: {details.projectId}</Typography>}
//                         {details.datasetId && <Typography variant="caption" display="block" gutterBottom>Dataset ID: {details.datasetId}</Typography>}
//                         {details.tableId && <Typography variant="caption" display="block" gutterBottom>Table ID: {details.tableId}</Typography>}
//                         {details.serviceAccountName && <Typography variant="caption" display="block" gutterBottom>Service Account: {details.serviceAccountName}</Typography>}
//                         {commonDetails}
//                      </>
//                 );
//             default: return commonDetails;
//         }
//     };

//     return (
//         <Box sx={{ display: "Flex" }}>
//             <MainDrawer index={"integrations"} />
//             <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                 {/* --- Header --- */}
//                 <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                     <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
//                         <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px" mb={0}>Integrations</Typography>
//                         {!loading && !error && installedCount > 0 && ( <Button sx={addPluginButtonStyles} variant="contained" size="small" onClick={() => navigate("/integrations/explorePlugins")}> Add New Plugin </Button> )}
//                     </Box>
//                     <Divider sx={{ marginY: 1 }} />
//                 </Box>

//                 {/* --- Installed Plugins Sub-Header --- */}
//                 <Box m={1} sx={{ pl: "25px", pt: 2 }}>
//                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold"> Installed Plugins ({loading ? '...' : installedCount}) </Typography>
//                     <Typography fontSize="14px" fontFamily="Gilroy-Regular"> List of all the installed plugins </Typography>
//                 </Box>

//                 {/* --- Content Area --- */}
//                 <Box>
//                     {loading && ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}> <CircularProgress /> </Box> )}
//                     {error && ( <Box sx={{ p: 3 }}> <Alert severity="error">Failed to load integrations. Please try again later.</Alert> </Box> )}

//                     {/* --- CONDITION 1: Plugins Installed --- */}
//                     {!loading && !error && installedCount > 0 && (
//                         <Box sx={{ p: 3 }}>
//                             <List>
//                                 {installedPlugins.map((plugin) => {
//                                     const config = pluginConfig[plugin.key] || { displayName: plugin.key };
//                                     return (
//                                         <ListItem key={plugin.key} divider alignItems="flex-start">
//                                             <ListItemIcon sx={{mt: 1}}> {<CheckCircleOutlineIcon color="success" />} </ListItemIcon>
//                                             <ListItemText
//                                                 primary={config.displayName}
//                                                 // This calls the function that renders the fetched data
//                                                 secondary={renderPluginSpecificDetails(plugin)}
//                                                 primaryTypographyProps={{fontWeight: 'bold'}}
//                                             />
//                                             <Chip label="Enabled" color="success" size="small" sx={{ ml: 2, mt: 1 }} />
//                                         </ListItem>
//                                     );
//                                 })}
//                             </List>
//                         </Box>
//                     )}

//                     {/* --- CONDITION 2: No Plugins Installed --- */}
//                     {!loading && !error && installedCount === 0 && (
//                         <Box sx={{ p: 10 }}>
//                             <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh", textAlign: "center", }}>
//                                <img src="/assets/Intergration Page.svg" alt="Active Icon" style={{ width: "400px", marginRight: "8px" }}/>
//                                 <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">No plugins are added yet</Typography>
//                                 <Typography marginTop="0.5vh" marginBottom="8px" fontSize="16px" fontFamily="Gilroy-Regular">Click below button to explore and add new plugins</Typography>
//                                 <Button sx={addPluginButtonStyles} variant="contained" size="small" type="submit" onClick={() => navigate("/integrations/explorePlugins")} > Add New Plugin </Button>
//                             </Box>
//                         </Box>
//                     )}
//                 </Box>
//             </Container>
//         </Box>
//     );
// }
// export default Integrations;
// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Divider,
//     Typography,
//     CircularProgress, // Added for loading state
//     Alert, // Added for error state
//     List, // Added for displaying plugins
//     ListItem, // Added for displaying plugins
//     ListItemText, // Added for displaying plugins
//     ListItemIcon, // Added for displaying plugins
//     Chip, // To show enabled status
// } from "@mui/material";
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // Example icon
// // Assuming you have a function 'queryGraphql' similar to the one in your example
// // import queryGraphql from "../../helpers/ApiInterface";
// import MainDrawer from "../MainDrawer"; // Adjusted path as needed
// import queryGraphql from "../../helpers/ApiInterface";
// // --- Define Interfaces ---
// interface IntegrationDetail {
//     enabled: boolean;
//     repoUrl?: string;
//     updatedAt?: string;
//     datasetId?: string;
//     projectId?: string;
//     serviceAccountName?: string;
//     tableId?: string;
// }

// interface IntegrationData {
//     id: string; // Assuming 'id' is always present at the top level
//     checkov?: IntegrationDetail;
//     costAgent?: IntegrationDetail;
//     kyverno?: IntegrationDetail;
//     prometheus?: IntegrationDetail;
// }

// // --- Helper Function to get enabled plugins ---
// const getInstalledPlugins = (data: IntegrationData | null): { name: string; details: IntegrationDetail }[] => {
//     if (!data) return [];
//     const plugins: { name: string; details: IntegrationDetail }[] = [];
//     if (data.checkov?.enabled) plugins.push({ name: "Checkov", details: data.checkov });
//     if (data.costAgent?.enabled) plugins.push({ name: "Cost Agent", details: data.costAgent });
//     if (data.kyverno?.enabled) plugins.push({ name: "Kyverno", details: data.kyverno });
//     if (data.prometheus?.enabled) plugins.push({ name: "Prometheus", details: data.prometheus });
//     return plugins;
// };

// const Integrations: React.FC = () => {
//     const navigate = useNavigate();
//     const [integrations, setIntegrations] = useState<IntegrationData | null>(null);
//     const [loading, setLoading] = useState<boolean>(true);
//     const [error, setError] = useState<any>(null);

//     // --- Fetch Data ---
//     useEffect(() => {
//         (async () => {
//             try {
//                 setLoading(true);
//                 setError(null);
//                 // Replace with your actual GraphQL call
//                 const data = await queryGraphql(`
//                     query Query {
//                       getIntegrations {
//                         checkov { enabled repoUrl updatedAt }
//                         costAgent { enabled datasetId projectId serviceAccountName tableId updatedAt }
//                         kyverno { enabled repoUrl updatedAt }
//                         prometheus { enabled repoUrl updatedAt }
//                         id
//                       }
//                     }
//                 `);

//                 if (data.getIntegrations) {
//                     setIntegrations(data.getIntegrations);
//                 } else {
//                      console.log("No integration data returned.");
//                      setIntegrations(null);
//                 }
//             } catch (error) {
//                 console.error("Error fetching integrations:", error);
//                 setError(error);
//                 setIntegrations(null);
//             } finally {
//                 setLoading(false);
//             }
//         })();
//     }, []); // Runs once on component mount

//     const installedPlugins = getInstalledPlugins(integrations);
//     const installedCount = installedPlugins.length;

//     return (
//         <Box sx={{ display: "Flex" }}>
//             <MainDrawer index={"integrations"} />
//             <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//                 {/* --- Header --- */}
//                 <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                     <Box display="flex" alignItems="center" mt={1}>
//                         <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px">
//                             Integrations
//                         </Typography>
//                     </Box>
//                     <Divider sx={{ marginY: 1 }} />
//                 </Box>

//                 {/* --- Installed Plugins Header (Always visible) --- */}
//                 <Box m={1} sx={{ pl: "25px", pt: 2 }}>
//                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                         Installed Plugins ({loading ? '...' : installedCount})
//                     </Typography>
//                     <Typography fontSize="14px" fontFamily="Gilroy-Regular">
//                         List of all the installed plugins
//                     </Typography>
//                 </Box>

//                 {/* --- Content Area --- */}
//                 <Box> {/* Removed padding from here to match original structure better */}
//                     {loading && (
//                         <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
//                             <CircularProgress />
//                         </Box>
//                     )}

//                     {error && (
//                          <Box sx={{ p: 3 }}> {/* Keep some padding for error */}
//                              <Alert severity="error">Failed to load integrations. Please try again later.</Alert>
//                          </Box>
//                     )}

//                     {/* --- CONDITION 1: Data exists AND there are installed plugins --- */}
//                     {!loading && !error && installedCount > 0 && (
//                         <Box sx={{p: 3}}> {/* Add padding for the list view */}
//                            <List>
//                               {installedPlugins.map((plugin) => (
//                                   <ListItem key={plugin.name} divider>
//                                        <ListItemIcon>
//                                            <CheckCircleOutlineIcon color="success" />
//                                        </ListItemIcon>
//                                        <ListItemText
//                                            primary={plugin.name}
//                                            secondary={`Enabled${plugin.details.updatedAt ? ' - Last updated: ' + new Date(plugin.details.updatedAt).toLocaleDateString() : ''}`}
//                                        />
//                                        <Chip label="Enabled" color="success" size="small" />
//                                   </ListItem>
//                               ))}
//                            </List>
//                         </Box>
//                     )}

//                      {/* --- CONDITION 2: Data fetched successfully BUT NO installed plugins --- */}
//                      {/* --- This part now exactly matches the structure and style of your original code's empty state --- */}
//                      {!loading && !error && installedCount === 0 && (
//                         <Box sx={{ p: 10 }}> {/* Use p: 10 padding like original */}
//                             <Box
//                                 sx={{
//                                     display: "flex",
//                                     flexDirection: "column",
//                                     alignItems: "center",
//                                     justifyContent: "center",
//                                     minHeight: "40vh",
//                                     textAlign: "center",
//                                 }}
//                             >
//                                <img
//                                    src="/assets/Intergration Page.svg"
//                                    alt="Active Icon" // Reverted alt text
//                                    style={{ width: "400px", marginRight: "8px" }} // Reverted style
//                                />
//                                 <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">No plugins are added yet</Typography>
//                                 <Typography marginTop="0.5vh" marginBottom="8px" fontSize="16px" fontFamily="Gilroy-Regular">Click below button to explore and add new plugins</Typography> {/* Reverted margin */}
//                                 <Button
//                                     sx={{ // Reverted sx styles exactly
//                                         backgroundColor: "black", color: "white",
//                                         fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "0.5vh",
//                                         textTransform: "none", px: 5, py:1, position: 'relative',
//                                         transition: 'background-color 0.3s ease',
//                                         '&:hover': { backgroundColor: "dimgray" },
//                                         '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' },
//                                     }}
//                                     variant="contained" size="small" type="submit" // Reverted type="submit"
//                                     onClick={() => navigate("/integrations/explorePlugins")}
//                                 >
//                                     Add New Plugin
//                                 </Button>
//                             </Box>
//                         </Box>
//                     )}
//                 </Box>
//             </Container>
//         </Box>
//     );
// }
// export default Integrations;
// import React, { useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";

// //import { useAuth } from "../../helpers/AuthProvider";
// import {
// 	Accordion,
// 	AccordionDetails,
// 	AccordionSummary,
// 	Avatar,
// 	Box,
// 	Breadcrumbs,
// 	Button,
// 	Chip,
// 	Container,
// 	Divider,
// 	Grid,
// 	IconButton,
// 	Link,
// 	Paper,
// 	Tooltip,
// 	Typography,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// //import queryGraphql from "../../helpers/ApiInterface";
// import moment from "moment";
// import { ExpandMore } from "@mui/icons-material";
// import { RiRestartLine } from "react-icons/ri";
// import { RiCheckLine } from "react-icons/ri";
// import { RiCloseLine } from "react-icons/ri";
// import { RiHourglass2Line } from "react-icons/ri";
// import DashBoardPage from "../DashBoardPage";
// import MainDrawer from "../MainDrawer";

// const Integrations: React.FC =  () => {
//     const navigate = useNavigate();
//     return (
//         <Box sx={{display:"Flex"}}>
//             <MainDrawer index={"integrations"} />
//             <Container disableGutters maxWidth={false} sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
//             <Box sx={{ p: 2, bgcolor: "#fff" }}>
//                 {/* <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
//                     <Link underline="hover" color="inherit" href="/integrations">
//                         Integrations
//                     </Link>
//                     <Typography color="text.primary">Integrations</Typography>
//                 </Breadcrumbs> */}
//                 <Box display="flex" alignItems="center" mt={1}>
//                     {/* <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/dashboard")}>
//                         <ArrowBackIosNewIcon fontSize="small" />
//                     </IconButton> */}
//                     <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" marginLeft="20px">
//                         Integrations
//                     </Typography>
//                 </Box>
//                 <Divider sx={{ marginY: 1 }} />
//             </Box>
            
//             <Box m={1}>
//             <Box sx={{ display: "flex", alignItems: "left", flexDirection:"column", marginLeft:"25px"}}>
//                            {/* <img
//                                src="/assets/tickcircle.svg"
//                                alt="Active Icon"
//                                style={{ width: "20px", marginRight: "8px" }}
//                            /> */}
//                            <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" >Installed Plugins(0)
//                            </Typography>
//                            <Typography fontSize="14px" fontFamily="Gilroy-Regular" >List of all the installed plugins
//                            </Typography>
//             </Box>
//             </Box>
//             <Box sx={{ p: 10 }}>
//                 <Box
//                     sx={{
//                         display: "flex",
//                         flexDirection: "column",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         minHeight: "40vh",
//                         textAlign: "center",
//                     }}
//                 >   
//                           <img
//                                src="/assets/Intergration Page.svg"
//                                alt="Active Icon"
//                                style={{ width: "400px", marginRight: "8px" }}
//                            />
//                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">No plugins are added yet</Typography>
//                     <Typography marginTop="0.5vh" marginBottom="8px" fontSize="16px" fontFamily="Gilroy-Regular">Click below button to explore and add new plugins</Typography>

//                     <Button
//                                            sx={{
//                                                backgroundColor: "black", color: "white",
//                                                fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "0.5vh",
//                                                textTransform: "none", px: 5, py:1, position: 'relative',
//                                                transition: 'background-color 0.3s ease',
//                                                '&:hover': { backgroundColor: "dimgray" },
//                                                '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)', cursor: 'not-allowed' },
//                                            }}
//                                            variant="contained" size="small" type="submit"
//                                            //disabled={isSubmitting}
//                                            onClick={() => navigate("/integrations/explorePlugins")}
//                                        >
//                                            Add New Plugin
//                                        </Button>
//                 </Box>
//             </Box>
//         </Container>
//         </Box>
//     );
// }
// //     const bucketName = 'prod-faraday-app-state-bucket'; 
// //     const fileName = 'cluster.json'; 
// //     const [checks,setChecks] = useState<any>([]);
// //     const [failedChecks , setFailedChecks] = useState<any>([]);
// //     const [kyverno, setKyverno] = useState<any>([]);
// //     const appendCheck = (newCheck: any) => {
// //         setChecks((prevChecks:any) => [...prevChecks, newCheck]);
// //       };



// //     React.useEffect(()=>{
// // 		const fetchData = async () =>{
// // 			try{
// // 				const data = await queryGraphql(`
// //                     query Query($bucketName: String!, $fileName: String!)
// //                      {
// //                     gcsData(bucketName: $bucketName, fileName: $fileName)
// //                     }`, {bucketName: "prod-faraday-app-state-bucket",fileName: "cluster.json"});
// //                     if(data.gcsData){
                        
// //                         // console.log(typeof(data?.gcsData.results.passed_checks));
// //                         await setChecks(data?.gcsData?.results?.passed_checks);
// //                         // await setChecks((prevChecks:any) => [...prevChecks, data?.gcsData.results.passed_checks]);
// //                         // appendCheck(data?.gcsData.results.passed_checks);
// //                         // console.log("Passed Checks",checks);
// //                         // await checks.map((check:any) => console.log(check?.check_id));
// //                     }
// // 			}
// // 			catch(error){
// // 				console.log(error);
// // 			}
// // 		}
// // 		fetchData();
// // 	},[]);

// //     React.useEffect(()=>{
// // 		const fetchData = async () =>{
// // 			try{
// // 				const data = await queryGraphql(`
// //                     query Query($bucketName: String!, $fileName: String!)
// //                      {
// //                     gcsData(bucketName: $bucketName, fileName: $fileName)
// //                     }`, {bucketName: "prod-faraday-app-state-bucket",fileName: "cluster.json"});
// //                     if(data.gcsData){
                        
// //                         // console.log(typeof(data?.gcsData.results.failed_checks));
// //                         await setFailedChecks(data?.gcsData?.results?.failed_checks);
// //                         // await setChecks((prevChecks:any) => [...prevChecks, data?.gcsData.results.passed_checks]);
                       
// //                     }
// // 			}
// // 			catch(error){
// // 				console.log(error);
// // 			}
// // 		}
// // 		fetchData();
// // 	},[]);

// //     React.useEffect(()=>{
// // 		const fetchData = async () =>{
// // 			try{
// // 				const data = await queryGraphql(`
// //                     query Query($bucketName: String!, $fileName: String!)
// //                      {
// //                     gcsData(bucketName: $bucketName, fileName: $fileName)
// //                     }`, {bucketName: "theta-faraday-fact-bucket",fileName: "kyvernodata.json"});
// //                     if(data.gcsData){
                        
// //                         // console.log(typeof(data?.gcsData.results.failed_checks));
// //                         await setKyverno(data?.gcsData?.items);
// //                         // await setChecks((prevChecks:any) => [...prevChecks, data?.gcsData.results.passed_checks]);
                       
// //                     }
// // 			}
// // 			catch(error){
// // 				console.log(error);
// // 			}
// // 		}
// // 		fetchData();
// // 	},[]);

// //     return(
// //     <Box sx={{display:'flex' , height:'100vh'}}>

// //         <MainDrawer index={"integrations"} />
// //         <Box sx={{width:"100%"}}>
// //         <Box pb={5} pt={2}   >
// //             <Box
// //                 sx={{
// //                     display: "flex",
// //                     justifyContent: "space-between",
// //                     alignItems: "center",
// //                     marginBottom: 0,
// //                     width: "100%",
// //                     // height:"20vh",
                    
// //                     mx: 4,
// //                 }}
// //             >
// //                 <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
// //                     Integrations
// //                 </Typography>
// //             </Box>
// //             <Divider sx={{ marginY: 1, marginX:1 }} /> 
// //         </Box>
        
// //         <Box p={4} pt={2} sx={{display:'flex', flexDirection:'column', bgcolor:"#fafafa", height:'100%',maxHeight:'80vh',overflowY: "auto"}}>

// //             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">Infra Checks </Typography>
// //             <Typography my={2}>Passed Checks</Typography>
// //                 <Grid container
// // 								// alignItems="center"
// // 								justifyContent="flex-start"
// // 								direction="row"
// // 								spacing={3}>
                    
                    
// //                     {checks?.map((check:any, index:string) => ( // Add index for unique key
// //                     <Grid item >
// //                     <Box sx={{ display: 'flex', gap: 3 }}>
// //                 <Card
// //                     key={check.check_id || index} // Use check_id if available, otherwise use index
// //                     elevation={0}
// //                     sx={{
// //                         width: 250,
// //                         borderRadius: "12px",
// //                         minHeight: 250,
// //                         display: "flex",
// //                         bgcolor: "#FFFFFF",
// //                         flexDirection: 'column',
// //                         justifyContent: 'center',
// //                         alignItems: 'center'

// //                     }}
// //                 >
// //                     <CardActions></CardActions>
// //                     <CardContent sx={{ justifyContent: 'center', padding: "1" }}>
// //                         <Box
// //                             display="flex"
// //                             flexDirection="column"
// //                             alignItems="center"
// //                             justifyContent="center">

// //                             <Avatar
// //                                 src="/assets/checkov.jpeg"
// //                                 alt="Kyverno"
// //                                 sx={{ marginBottom: "16px", height: "60px", width: "60px" }}
// //                             />

// //                             {/* <Typography gutterBottom fontSize="22px" fontFamily="Gilroy-Bold">Checkov</Typography> */}
// //                             <Typography>{check?.check_id}</Typography>
// //                             {/* <FormControlLabel
// //                                 control={<Checkbox />}
// //                                 label="Enable Kyverno" 
// //                                 sx={{ marginTop: "16px" }}
// //                             /> */}
                            
// //                             <Typography fontSize="12px" fontFamily="Gilroy-Regular" mt={1}>{check?.check_name}</Typography>
// //                             <Typography mt={1} color="green">{check?.check_result?.result}</Typography>
// //                         </Box>
// //                     </CardContent>
// //                 </Card>
// //                 </Box>
// //                 </Grid>
// //             ))}
                      
                                                                            
                    
// //                 </Grid>

// //                 <Typography my={2}>Failed Checks</Typography>
// //                 <Grid container
// // 								// alignItems="center"
// // 								justifyContent="flex-start"
// // 								direction="row"
// // 								spacing={3}>
                    
                   
// //                     {failedChecks?.map((check:any, index:string) => ( // Add index for unique key
// //                     <Grid item >
// //                     <Box sx={{ display: 'flex', gap: 3  }}>
// //                 <Card
// //                     key={check.check_id || index} // Use check_id if available, otherwise use index
// //                     elevation={0}
// //                     sx={{
// //                         width: 250,
// //                         borderRadius: "12px",
// //                         minHeight: 250,
// //                         display: "flex",
// //                         bgcolor: "#FFFFFF",
// //                         flexDirection: 'column',
// //                         justifyContent: 'center',
// //                         alignItems: 'center'

// //                     }}
// //                 >
// //                     <CardActions></CardActions>
// //                     <CardContent sx={{ justifyContent: 'center', padding: "1" }}>
// //                         <Box
// //                             display="flex"
// //                             flexDirection="column"
// //                             alignItems="center"
// //                             justifyContent="center"
// //                             >

// //                             <Avatar
// //                                 src="/assets/checkov.jpeg"
// //                                 alt="Kyverno"
// //                                 sx={{ marginBottom: "16px", height: "60px", width: "60px" }}
// //                             />

// //                             {/* <Typography gutterBottom fontSize="22px" fontFamily="Gilroy-Bold">Checkov</Typography> */}
// //                             <Typography  mt={1}>{check?.check_id}</Typography>
// //                             {/* <FormControlLabel
// //                                 control={<Checkbox />}
// //                                 label="Enable Kyverno" 
// //                                 sx={{ marginTop: "16px" }}
// //                             /> */}
// //                             <Typography fontSize="12px" fontFamily="Gilroy-Regular" mt={1}>{check?.check_name}</Typography>
// //                             <Typography mt={1} color="red">{check?.check_result?.result}</Typography>
// //                         </Box>
// //                     </CardContent>
// //                 </Card>
// //                 </Box>
// //                 </Grid>
// //             ))}
                      
                                                                            
                    
// //                 </Grid>

// //                 <Typography mt={2} fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">Kyverno Policies </Typography>
// //                 {/* <Typography>Policies</Typography> */}
                


// //                 <Grid container
// // 								// alignItems="center"
// // 								justifyContent="flex-start"
// // 								direction="row"
// // 								spacing={3}>
                              

// //                                             {kyverno?.map((policy: any, index: string) => (
// //                         <Grid item key={policy?.metadata?.uid || index}>
// //                             <Box sx={{ display: 'flex', gap: 3 }}>
// //                                 <Card
// //                                     elevation={0}
// //                                     sx={{
// //                                         width: 250,
// //                                         borderRadius: "12px",
// //                                         minHeight: 250,
// //                                         display: "flex",
// //                                         bgcolor: "#FFFFFF",
// //                                         flexDirection: 'column',
// //                                         justifyContent: 'center',
// //                                         alignItems: 'center'

// //                                     }}
// //                                 >
// //                                     <CardActions></CardActions>
// //                                     <CardContent sx={{ justifyContent: 'center', padding: "1" }}>
// //                                         <Box
// //                                             display="flex"
// //                                             flexDirection="column"
// //                                             alignItems="center"
// //                                             justifyContent="center">
                                        
// //                                             <Avatar
// //                                                 src="/assets/kyverno.png"
// //                                                 alt="Kyverno"
// //                                                 sx={{ marginBottom: "16px", height: "60px", width: "60px" }}
// //                                             />

// // <Typography mt={1}>{policy?.spec?.validationFailureAction}</Typography>

// // <Typography fontSize="12px" fontFamily="Gilroy-Regular" mt={1}>{policy?.metadata?.name}</Typography>
// //                                             {policy?.spec?.rules?.length > 0 && policy?.spec?.rules[0]?.match?.any?.length > 0 ? (
// //                                                 <Typography mt={1} color="green">
// //                                                     {policy?.spec?.rules[0]?.match?.any?.map((item: any) => item.resources?.kinds?.join(', ')).join(', ')}
// //                                                 </Typography>
// //                                             ) : (
// //                                                 <Typography mt={1} color="red">
// //                                                     Kinds not found
// //                                                 </Typography>
// //                                             )}
// //                                         </Box>
// //                                     </CardContent>
// //                                 </Card>
// //                             </Box>
// //                         </Grid>
// //                     ))}                                                    
                    
// //                 </Grid>
                


// //         </Box>
// //         </Box>
       
// //     </Box>
// //     )
// // return(<Box sx={{ display: "flex"}}>
// //     <MainDrawer index={"integrations"} />

// //     <Box sx={{display:"flex" , alignItems:"center", justifyContent:'center' ,flexDirection: 'row', height:"100vh", width:"100vw"}}>
// //  <Typography fontFamily="Gilroy-Bold" fontSize="36px"  textAlign="center"> Coming Soon!!</Typography>
// //     </Box>
// //     </Box>)



// export default Integrations;
